import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "../modules/auth/AuthContext";
import {
  getClientHistory,
  getMyClientHistory,
  saveInitialAssessment,
  saveMyInitialAssessment,
  saveWeeklyCheckIn,
  saveMyWeeklyCheckIn,
  uploadMyWeeklyCheckInProgressPhoto,
  uploadWeeklyCheckInProgressPhoto,
  apiBaseUrl,
} from "../lib/api";
import type { ClientHistoryResponse, InitialAssessment, WeeklyCheckIn } from "../types";

type ClientPortalTab = "dashboard" | "assessment" | "checkin" | "tracking" | "history" | "comparison" | "charts";
type PortalToast = { type: "success" | "error"; message: string };
type PhotoViewerItem = {
  title: string;
  subtitle?: string;
  src: string;
};

const clientPortalNavItems: Array<{ tab: ClientPortalTab; label: string }> = [
  { tab: "dashboard", label: "Dashboard" },
  { tab: "tracking", label: "Mi progreso" },
  { tab: "checkin", label: "Check-ins" },
  { tab: "assessment", label: "Cuestionario inicial" },
  { tab: "history", label: "Historial" },
  { tab: "comparison", label: "Comparativa" },
];
const showClientDashboardScheduleSections = false;

type AssessmentQuestionKey =
  | "height"
  | "currentWeight"
  | "age"
  | "gymExperienceTime"
  | "dietExperienceTime"
  | "currentDietDetail"
  | "previousDiet"
  | "canBuyProteinPowder"
  | "solidMealsAndShakes"
  | "supplements"
  | "currentSteroids"
  | "steroidHistory"
  | "trainingDays"
  | "weeklyTrainingDays"
  | "trainingSplit"
  | "trainingLevel"
  | "trainingTime"
  | "hasStepWatch"
  | "doesCardio"
  | "cardioDuration"
  | "cardioType"
  | "canDoFastedCardio"
  | "canDoCardioOtherTime"
  | "wakeUpTime"
  | "sleepTime"
  | "dailyRoutine"
  | "appetiteProfile"
  | "favoriteProteinSources"
  | "favoriteCarbSources"
  | "favoriteFatSources"
  | "allergies"
  | "surgeries"
  | "pathologies"
  | "familyHistory"
  | "fatLossOrGrowth"
  | "goal"
  | "conformity";

type AssessmentForm = Record<AssessmentQuestionKey, string>;

type AssessmentQuestion = {
  key: AssessmentQuestionKey;
  label: string;
  helper?: string;
  rows?: number;
  inputType?: "textarea" | "number" | "select" | "time" | "checkbox";
  layout?: "default" | "full";
  unit?: string;
  step?: number;
  min?: number;
  max?: number;
  options?: Array<{ label: string; value: string }>;
};

type ClientWeightPoint = {
  date: string;
  peso: number;
  checkInDate: string;
  label: string;
  dateLabel: string;
  axisLabel: string;
};

const assessmentQuestions: AssessmentQuestion[] = [
  {
    key: "height",
    label: "¿CUÁL ES TU ALTURA?",
    inputType: "number",
    unit: "cm",
    min: 80,
    max: 260,
    step: 0.1,
  },
  {
    key: "currentWeight",
    label: "¿CUÁL ES TU PESO ACTUAL?",
    inputType: "number",
    unit: "kg",
    min: 20,
    max: 350,
    step: 0.1,
  },
  {
    key: "age",
    label: "¿QUÉ EDAD TIENES?",
    inputType: "number",
    unit: "años",
    min: 12,
    max: 100,
    step: 1,
  },
  {
    key: "gymExperienceTime",
    label: "¿HACE CUÁNTO TIEMPO QUE VAS AL GIMNASIO?",
    inputType: "number",
    unit: "años",
    min: 0,
    max: 80,
    step: 1,
  },
  {
    key: "dietExperienceTime",
    label: "¿HACE CUÁNTO TIEMPO HACES DIETA?",
    inputType: "number",
    unit: "años",
    min: 0,
    max: 80,
    step: 1,
  },
  {
    key: "currentDietDetail",
    label: "¿CUÁL ES TU DIETA AHORA?",
    layout: "full",
    helper:
      "Dime la hora de cada comida, las cantidades por comida y confirma si el peso es crudo o cocido.",
    rows: 4,
  },
  {
    key: "previousDiet",
    label: "PREVIO A ESTA DIETA ¿QUÉ OTRA DIETA ESTABAS HACIENDO Y POR CUÁNTO TIEMPO?",
    layout: "full",
    rows: 3,
  },
  {
    key: "canBuyProteinPowder",
    label: "¿PUEDES COMPRAR PROTEÍNAS EN POLVO?",
    inputType: "select",
    options: [
      { label: "Selecciona una opción", value: "" },
      { label: "Si", value: "SI" },
      { label: "No", value: "NO" },
      { label: "A veces", value: "A VECES" },
    ],
  },
  {
    key: "solidMealsAndShakes",
    label:
      "¿CUÁNTAS COMIDAS SÓLIDAS PUEDES COMER AL DÍA, Y HAY ALGUNA COMIDA QUE TENGA QUE SER UN BATIDO DE PROTEÍNAS?",
    layout: "full",
    helper:
      "Incluye el horario de cada comida y aclara cual seria comida solida y cual batido.",
    rows: 4,
  },
  { key: "supplements", label: "¿TOMAS ALGUN SUPLEMENTO?" },
  { key: "currentSteroids", label: "¿QUÉ ESTEROIDES CONSUMES?" },
  { key: "steroidHistory", label: "¿QUÉ ESTEROIDES HAS USADO Y EN QUÉ DOSIS SEMANAL?" },
  { key: "trainingDays", label: "¿QUÉ DIAS ENTRENAS?" },
  {
    key: "weeklyTrainingDays",
    label: "¿CUÁNTOS DIAS VAS A ENTRENAR A LA SEMANA?",
    inputType: "number",
    unit: "días",
    min: 0,
    max: 14,
    step: 1,
  },
  {
    key: "trainingSplit",
    label: "¿CÓMO ES TU TRAINING SPLIT, CUÁNTAS SERIES Y REPS HACES?",
    layout: "full",
    helper: "Describe en pocas palabras tu rutina de entrenamiento.",
    rows: 4,
  },
  {
    key: "trainingLevel",
    label: "¿CONSIDERAS TU ENTRENAMIENTO BASICO, INTERMEDIO O AVANZADO, Y POR QUE?",
    rows: 3,
  },
  { key: "trainingTime", label: "¿A QUE HORA ENTRENAS?", inputType: "time" },
  {
    key: "hasStepWatch",
    label: "¿TIENES UN RELOJ QUE CUENTA TUS PASOS?",
    inputType: "select",
    options: [
      { label: "Selecciona una opción", value: "" },
      { label: "Si", value: "SI" },
      { label: "No", value: "NO" },
    ],
  },
  {
    key: "doesCardio",
    label: "¿ACTUALMENTE HACES CARDIO?",
    helper: "Indica cuántas veces a la semana.",
    inputType: "number",
    unit: "veces/semana",
    min: 0,
    max: 14,
    step: 1,
  },
  {
    key: "cardioDuration",
    label: "¿CUÁNTO CARDIO HACES?",
    inputType: "number",
    unit: "minutos",
    min: 0,
    max: 600,
    step: 1,
  },
  { key: "cardioType", label: "¿QUÉ TIPO DE CARDIO HACES?" },
  {
    key: "canDoFastedCardio",
    label: "¿PUEDES HACER CARDIO EN AYUNAS?",
    inputType: "select",
    options: [
      { label: "Selecciona una opción", value: "" },
      { label: "Si", value: "SI" },
      { label: "No", value: "NO" },
      { label: "A veces", value: "A VECES" },
    ],
  },
  {
    key: "canDoCardioOtherTime",
    label: "¿PUEDES HACER CARDIO EN OTRO MOMENTO DEL DIA QUE NO SEA POST ENTRENO?",
    inputType: "select",
    options: [
      { label: "Selecciona una opción", value: "" },
      { label: "Si", value: "SI" },
      { label: "No", value: "NO" },
      { label: "A veces", value: "A VECES" },
    ],
  },
  { key: "wakeUpTime", label: "¿A QUE HORA TE DESPIERTAS?", inputType: "time" },
  { key: "sleepTime", label: "¿A QUE HORA TE VAS A DORMIR?", inputType: "time" },
  {
    key: "dailyRoutine",
    label: "EXPLICAME TU DIA A DIA DESDE QUE TE LEVANTAS INCLUYENDO LOS FINES DE SEMANA",
    layout: "full",
    helper:
      "Se detallado. Me gustaria saber si eres una persona muy activa o si andas sentado todo el día.",
    rows: 5,
  },
  {
    key: "appetiteProfile",
    label: "¿SIEMPRE TIENES HAMBRE O ERES DE COMER POCO?",
    inputType: "select",
    options: [
      { label: "Selecciona una opción", value: "" },
      { label: "Siempre tengo hambre", value: "SIEMPRE TENGO HAMBRE" },
      { label: "Como poco", value: "COMO POCO" },
      { label: "Normal", value: "NORMAL" },
    ],
  },
  { key: "favoriteProteinSources", label: "¿CUÁLES SON TUS FUENTES PROTEICAS FAVORITAS?" },
  { key: "favoriteCarbSources", label: "¿CUÁLES SON TUS FUENTES DE CARBOHIDRATOS FAVORITAS?" },
  { key: "favoriteFatSources", label: "¿CUÁLES SON TUS FUENTES DE GRASAS FAVORITAS?" },
  {
    key: "allergies",
    label: "¿ALGUNA ALERGIA? (ESPECIFICA)",
    layout: "full",
    rows: 2,
  },
  {
    key: "surgeries",
    label: "¿ALGUNA CIRUGIA? (ESPECIFICA LOS TIEMPOS)",
    layout: "full",
    rows: 2,
  },
  {
    key: "pathologies",
    label: "¿ALGUNA PATOLOGIA? (ESPECIFICA LOS TIEMPOS)",
    layout: "full",
    rows: 3,
  },
  {
    key: "familyHistory",
    label: "¿ALGUN ANTECEDENTE DE FAMILIARES CON CANCER O ALGUNA ENFERMEDAD? (especifica los tiempos)",
    layout: "full",
    rows: 3,
  },
  {
    key: "fatLossOrGrowth",
    label: "¿PERDIDA DE GRASA CORPORAL O CRECIMIENTO?",
    inputType: "select",
    options: [
      { label: "Selecciona una opción", value: "" },
      { label: "Pérdida de grasa corporal", value: "PERDIDA DE GRASA CORPORAL" },
      { label: "Crecimiento muscular", value: "CRECIMIENTO MUSCULAR" },
      { label: "Recomposición corporal", value: "RECOMPOSICION CORPORAL" },
    ],
  },
  { key: "goal", label: "¿CUÁL ES TU META?", rows: 3 },
  {
    key: "conformity",
    label: "Acepto las condiciones del plan: no hay reembolsos ni congelamiento del plan.",
    layout: "full",
    helper:
      "Si te enfermas, te vas de viaje o surge cualquier acontecimiento, las semanas siguen corriendo bajo tu plan. No hay reembolsos ni congelamiento del plan.",
    inputType: "checkbox",
  },
];

const emptyAssessment: AssessmentForm = {
  height: "",
  currentWeight: "",
  age: "",
  gymExperienceTime: "",
  dietExperienceTime: "",
  currentDietDetail: "",
  previousDiet: "",
  canBuyProteinPowder: "",
  solidMealsAndShakes: "",
  supplements: "",
  currentSteroids: "",
  steroidHistory: "",
  trainingDays: "",
  weeklyTrainingDays: "",
  trainingSplit: "",
  trainingLevel: "",
  trainingTime: "",
  hasStepWatch: "",
  doesCardio: "",
  cardioDuration: "",
  cardioType: "",
  canDoFastedCardio: "",
  canDoCardioOtherTime: "",
  wakeUpTime: "",
  sleepTime: "",
  dailyRoutine: "",
  appetiteProfile: "",
  favoriteProteinSources: "",
  favoriteCarbSources: "",
  favoriteFatSources: "",
  allergies: "",
  surgeries: "",
  pathologies: "",
  familyHistory: "",
  fatLossOrGrowth: "",
  goal: "",
  conformity: "",
};

const yoelSosanAssessmentPreset: AssessmentForm = {
  height: "178",
  currentWeight: "78.4",
  age: "31",
  gymExperienceTime: "36",
  dietExperienceTime: "10",
  currentDietDetail:
    "Desayuno con huevos, avena y fruta. Almuerzo con arroz, pollo y verduras. Cena con papa, carne magra y ensalada. Referencia en peso cocido.",
  previousDiet: "Dieta flexible durante 4 meses, sin control estricto de cantidades.",
  canBuyProteinPowder: "SI",
  solidMealsAndShakes: "4 comidas solidas y 1 batido post entrenamiento a las 18:30.",
  supplements: "Proteína whey, creatina y omega 3.",
  currentSteroids: "No consume esteroides actualmente.",
  steroidHistory: "No ha usado esteroides.",
  trainingDays: "Lunes, martes, jueves, viernes y sábado.",
  weeklyTrainingDays: "5",
  trainingSplit: "Push, pull, legs, upper y full body. 3 a 4 series por ejercicio, 8 a 15 repeticiones.",
  trainingLevel: "Intermedio por buena técnica y progresión constante en compuestos.",
  trainingTime: "18:00",
  hasStepWatch: "SI",
  doesCardio: "3",
  cardioDuration: "30",
  cardioType: "Caminata inclinada en cinta y bicicleta.",
  canDoFastedCardio: "A VECES",
  canDoCardioOtherTime: "SI",
  wakeUpTime: "06:00",
  sleepTime: "22:45",
  dailyRoutine:
    "Trabajo de oficina de 8 am a 5 pm, entrenamiento por la tarde, caminata ligera después de cenar y preparación de comidas el fin de semana.",
  appetiteProfile: "NORMAL",
  favoriteProteinSources: "Pollo, huevos, atun y carne magra.",
  favoriteCarbSources: "Arroz, papa, avena, camote y frutas.",
  favoriteFatSources: "Palta, aceite de oliva, frutos secos y yema de huevo.",
  allergies: "Ninguna alergia declarada.",
  surgeries: "Ninguna cirugia declarada.",
  pathologies: "Ninguna patologia declarada.",
  familyHistory: "Sin antecedentes familiares relevantes reportados.",
  fatLossOrGrowth: "RECOMPOSICION CORPORAL",
  goal: "Bajar grasa y definir músculo manteniendo rendimiento en el gimnasio.",
  conformity: "SI",
};

const pedroSanchezAssessmentPreset: AssessmentForm = {
  height: "170",
  currentWeight: "70",
  age: "30",
  gymExperienceTime: "2",
  dietExperienceTime: "1",
  currentDietDetail:
    "Desayuno con huevos, avena y fruta. Almuerzo con arroz, pollo y verduras. Cena con papa, pescado o pollo y ensalada. Cantidades referenciales en peso cocido.",
  previousDiet: "No hacia una dieta estructurada. Comia de forma libre, con algunas comidas altas en proteina.",
  canBuyProteinPowder: "SI",
  solidMealsAndShakes: "4 comidas solidas al dia y 1 batido post entrenamiento si el plan lo requiere.",
  supplements: "Creatina y proteina whey ocasional.",
  currentSteroids: "No consume esteroides actualmente.",
  steroidHistory: "No ha usado esteroides.",
  trainingDays: "Lunes, martes, jueves, viernes y sabado.",
  weeklyTrainingDays: "5",
  trainingSplit: "Push, pull, piernas, upper y full body. 3 a 4 series por ejercicio, 8 a 15 repeticiones.",
  trainingLevel: "Intermedio; conoce la tecnica base y puede progresar cargas con control.",
  trainingTime: "18:00",
  hasStepWatch: "SI",
  doesCardio: "3",
  cardioDuration: "25",
  cardioType: "Caminata inclinada o bicicleta moderada.",
  canDoFastedCardio: "A VECES",
  canDoCardioOtherTime: "SI",
  wakeUpTime: "06:30",
  sleepTime: "22:30",
  dailyRoutine:
    "Trabaja durante el dia, entrena por la tarde y prepara comidas simples. Los fines de semana mantiene actividad ligera y organiza sus alimentos.",
  appetiteProfile: "NORMAL",
  favoriteProteinSources: "Pollo, huevos, pescado, carne magra y yogurt griego.",
  favoriteCarbSources: "Arroz, avena, papa, camote, frutas y pan integral.",
  favoriteFatSources: "Palta, aceite de oliva, frutos secos y yema de huevo.",
  allergies: "Ninguna alergia declarada.",
  surgeries: "Ninguna cirugia declarada.",
  pathologies: "Ninguna patologia declarada.",
  familyHistory: "Sin antecedentes familiares relevantes reportados.",
  fatLossOrGrowth: "RECOMPOSICION CORPORAL",
  goal: "Definir y tonificar, bajar grasa progresivamente y mejorar fuerza sin perder masa muscular.",
  conformity: "SI",
};

type AssessmentPayloadField = Exclude<keyof InitialAssessment, "id">;

const assessmentFieldGroups: Array<{
  field: AssessmentPayloadField;
  keys: AssessmentQuestionKey[];
}> = [
  {
    field: "gymExperience",
    keys: ["height", "currentWeight", "age", "gymExperienceTime", "dietExperienceTime"],
  },
  {
    field: "currentDiet",
    keys: ["currentDietDetail", "previousDiet", "canBuyProteinPowder", "solidMealsAndShakes"],
  },
  {
    field: "mealSchedule",
    keys: ["wakeUpTime", "sleepTime", "dailyRoutine", "appetiteProfile"],
  },
  {
    field: "supplements",
    keys: ["supplements", "currentSteroids", "steroidHistory"],
  },
  {
    field: "trainingRoutine",
    keys: ["trainingDays", "weeklyTrainingDays", "trainingSplit", "trainingLevel", "trainingTime"],
  },
  {
    field: "cardioRoutine",
    keys: [
      "hasStepWatch",
      "doesCardio",
      "cardioDuration",
      "cardioType",
      "canDoFastedCardio",
      "canDoCardioOtherTime",
    ],
  },
  {
    field: "lifestyleNotes",
    keys: ["dailyRoutine", "appetiteProfile"],
  },
  {
    field: "foodPreferences",
    keys: ["favoriteProteinSources", "favoriteCarbSources", "favoriteFatSources"],
  },
  {
    field: "healthBackground",
    keys: ["allergies", "surgeries", "pathologies", "familyHistory", "conformity"],
  },
  {
    field: "physicalGoal",
    keys: ["fatLossOrGrowth", "goal"],
  },
];

const questionMap = Object.fromEntries(
  assessmentQuestions.map((question) => [question.key, question]),
) as Record<AssessmentQuestionKey, AssessmentQuestion>;

function buildAssessmentPayload(form: AssessmentForm) {
  const grouped = {
    gymExperience: "",
    currentDiet: "",
    mealSchedule: "",
    supplements: "",
    trainingRoutine: "",
    cardioRoutine: "",
    lifestyleNotes: "",
    foodPreferences: "",
    healthBackground: "",
    physicalGoal: "",
  };

  for (const group of assessmentFieldGroups) {
    grouped[group.field] = group.keys
      .map((key) => {
        const answer = form[key].trim();
        if (!answer) {
          return "";
        }
        return `${formatAssessmentQuestionLabel(questionMap[key].label)}\n${answer}`;
      })
      .filter(Boolean)
      .join("\n\n");
  }

  return grouped;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractAnswer(source: string, label: string) {
  const labels = Array.from(new Set([label, formatAssessmentQuestionLabel(label)]));

  for (const currentLabel of labels) {
    const pattern = new RegExp(`${escapeRegExp(currentLabel)}\\s*\\n([\\s\\S]*?)(?=\\n\\n|$)`);
    const match = source.match(pattern);
    const value = match?.[1]?.trim();
    if (value) {
      return value;
    }
  }

  return "";
}

function buildAssessmentAnswerPairs(value: unknown) {
  return String(value || "")
    .split(/\n{2,}/)
    .map((block) => {
      const lines = block
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean);

      if (lines.length >= 2) {
        const [question, ...answerLines] = lines;
        const answer = answerLines.join("\n");
        return {
          question: formatAssessmentSummaryQuestion(question),
          answer: formatAssessmentSummaryAnswer(question, answer),
        };
      }

      if (lines.length === 1) {
        return {
          question: "Respuesta",
          answer: lines[0],
        };
      }

      return null;
    })
    .filter((item): item is { question: string; answer: string } => Boolean(item?.answer));
}

function normalizeAssessmentText(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-PE");
}

function formatAssessmentSummaryQuestion(question: string) {
  const normalized = normalizeAssessmentText(question);
  const labels: Array<[string, string]> = [
    ["altura", "Altura"],
    ["peso actual", "Peso"],
    ["edad", "Edad"],
    ["vas al gimnasio", "Tiempo en gimnasio"],
    ["haces dieta", "Tiempo haciendo dieta"],
    ["dieta ahora", "Dieta actual"],
    ["previo a esta dieta", "Dieta previa"],
    ["proteinas en polvo", "Proteína en polvo"],
    ["comidas solidas", "Comidas y batidos"],
    ["suplemento", "Suplementos"],
    ["esteroides consumes", "Esteroides actuales"],
    ["esteroides has usado", "Historial de esteroides"],
    ["dias entrenas", "Días de entrenamiento"],
    ["entrenar a la semana", "Días por semana"],
    ["training split", "Training split"],
    ["basico, intermedio o avanzado", "Nivel de entrenamiento"],
    ["hora entrenas", "Hora de entrenamiento"],
    ["reloj que cuenta", "Reloj de pasos"],
    ["actualmente haces cardio", "Cardio semanal"],
    ["cuanto cardio", "Duración de cardio"],
    ["tipo de cardio", "Tipo de cardio"],
    ["cardio en ayunas", "Cardio en ayunas"],
    ["otro momento", "Cardio en otro horario"],
    ["hora te despiertas", "Hora de despertar"],
    ["hora te vas a dormir", "Hora de dormir"],
    ["dia a dia", "Rutina diaria"],
    ["hambre", "Apetito"],
    ["fuentes proteicas", "Proteínas favoritas"],
    ["carbohidratos", "Carbohidratos favoritos"],
    ["grasas favoritas", "Grasas favoritas"],
    ["alergia", "Alergias"],
    ["cirugia", "Cirugías"],
    ["patologia", "Patologías"],
    ["antecedente", "Antecedentes familiares"],
    ["perdida de grasa", "Objetivo corporal"],
    ["meta", "Meta"],
    ["acepto", "Conformidad"],
  ];
  const match = labels.find(([needle]) => normalized.includes(needle));

  if (match) {
    return match[1];
  }

  return formatAssessmentQuestionLabel(question).replace(/[¿?]/g, "").replace(/\.$/, "");
}

function formatAssessmentSummaryAnswer(question: string, answer: string) {
  const trimmedAnswer = answer.trim();
  if (!trimmedAnswer || /[a-záéíóúñ]/i.test(trimmedAnswer)) {
    return trimmedAnswer;
  }

  const normalized = normalizeAssessmentText(question);
  const unitRules: Array<[string, string]> = [
    ["altura", "cm"],
    ["peso actual", "kg"],
    ["edad", "años"],
    ["vas al gimnasio", "años"],
    ["haces dieta", "años"],
    ["entrenar a la semana", "días"],
    ["actualmente haces cardio", "veces/semana"],
    ["cuanto cardio", "minutos"],
  ];
  const unit = unitRules.find(([needle]) => normalized.includes(needle))?.[1];

  return unit ? `${trimmedAnswer} ${unit}` : trimmedAnswer;
}

function assessmentDraftStorageKey(clientKey?: string) {
  return `assessment-draft-${clientKey ?? "current"}`;
}

function readAssessmentDraft(clientKey?: string): AssessmentForm | null {
  try {
    const raw = window.localStorage.getItem(assessmentDraftStorageKey(clientKey));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<AssessmentForm> & { _savedAt?: string };
    const { _savedAt, ...rest } = parsed;
    return { ...emptyAssessment, ...rest };
  } catch {
    return null;
  }
}

function clearAssessmentDraft(clientKey?: string) {
  try {
    window.localStorage.removeItem(assessmentDraftStorageKey(clientKey));
  } catch {
    // ignore storage errors
  }
}

function checkInDraftStorageKey(clientKey?: string) {
  return `checkin-draft-${clientKey ?? "current"}`;
}

function readCheckInDraft(clientKey?: string): Record<string, any> | null {
  try {
    const raw = window.localStorage.getItem(checkInDraftStorageKey(clientKey));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Record<string, any> & { _savedAt?: string };
    const { _savedAt, ...draft } = parsed;
    return draft;
  } catch {
    return null;
  }
}

function clearCheckInDraft(clientKey?: string) {
  try {
    window.localStorage.removeItem(checkInDraftStorageKey(clientKey));
  } catch {
    // ignore storage errors
  }
}

function buildAssessmentForm(assessment: InitialAssessment | null): AssessmentForm {
  if (!assessment) {
    return { ...emptyAssessment };
  }

  const sources: Partial<Record<keyof InitialAssessment, string>> = {
    gymExperience: assessment.gymExperience || "",
    currentDiet: assessment.currentDiet || "",
    mealSchedule: assessment.mealSchedule || "",
    supplements: assessment.supplements || "",
    trainingRoutine: assessment.trainingRoutine || "",
    cardioRoutine: assessment.cardioRoutine || "",
    lifestyleNotes: assessment.lifestyleNotes || "",
    foodPreferences: assessment.foodPreferences || "",
    healthBackground: assessment.healthBackground || "",
    physicalGoal: assessment.physicalGoal || "",
  };

  const nextForm = { ...emptyAssessment };

  for (const group of assessmentFieldGroups) {
    const source = sources[group.field] || "";
    for (const key of group.keys) {
      const value = extractAnswer(source, questionMap[key].label);
      if (value) {
        nextForm[key] = value;
      }
    }
  }

  return nextForm;
}

function buildAssessmentSummaryItems(assessment: InitialAssessment | null) {
  if (!assessment) {
    return [];
  }

  const allAnswers = [
    assessment.gymExperience,
    assessment.currentDiet,
    assessment.supplements,
    assessment.trainingRoutine,
    assessment.cardioRoutine,
    assessment.mealSchedule,
    assessment.foodPreferences,
    assessment.healthBackground,
    assessment.physicalGoal,
  ]
    .flatMap((value) => buildAssessmentAnswerPairs(value))
    .filter((answer, index, answers) =>
      answers.findIndex((item) => item.question === answer.question && item.answer === answer.answer) === index,
    );

  const pickAnswers = (labels: Array<string | string[]>) =>
    labels
      .map((labelOrAliases) => {
        const aliases = Array.isArray(labelOrAliases) ? labelOrAliases : [labelOrAliases];
        return allAnswers.find((answer) =>
          aliases.some((alias) => normalizeAssessmentText(answer.question).includes(normalizeAssessmentText(alias))),
        );
      })
      .filter((answer): answer is { question: string; answer: string } => Boolean(answer));

  const healthAnswerLabels = ["Alergias", "Cirugías", "Patologías", "Antecedentes familiares"];
  const healthAnswers = buildAssessmentAnswerPairs(assessment.healthBackground)
    .slice(0, healthAnswerLabels.length)
    .map((answer, index) => ({
      ...answer,
      question: healthAnswerLabels[index] ?? answer.question,
    }));

  const sections = [
    {
      id: "basics",
      title: "Datos básicos",
      tone: "orange",
      icon: "person",
      answers: pickAnswers(["Altura", "Peso", "Edad", "Tiempo en gimnasio", "Tiempo haciendo dieta", "Dieta previa"]),
    },
    {
      id: "nutrition",
      title: "Alimentación actual y suplementos",
      tone: "orange",
      icon: "fork",
      answers: pickAnswers(["Dieta actual", "Proteína en polvo", "Comidas y batidos", "Suplementos", "Esteroides actuales", "Historial de esteroides"]),
    },
    {
      id: "training",
      title: "Entrenamiento",
      tone: "purple",
      icon: "dumbbell",
      answers: pickAnswers(["Días de entrenamiento", "Días por semana", "Training split", "Nivel de entrenamiento", "Hora de entrenamiento"]),
    },
    {
      id: "cardio",
      title: "Cardio y hábitos",
      tone: "blue",
      icon: "heart",
      answers: pickAnswers(["Reloj de pasos", "Cardio semanal", "Duración de cardio", "Tipo de cardio", "Cardio en ayunas", "Cardio en otro horario", "Hora de despertar", "Hora de dormir"]),
    },
    {
      id: "preferences",
      title: "Preferencias de alimentación",
      tone: "green",
      icon: "leaf",
      answers: pickAnswers(["Rutina diaria", "Apetito", "Proteínas favoritas", "Carbohidratos favoritos", "Grasas favoritas"]),
    },
    {
      id: "health",
      title: "Salud y antecedentes",
      tone: "red",
      icon: "cross",
      answers: healthAnswers,
    },
    {
      id: "goal",
      title: "Objetivo y condiciones",
      tone: "orange",
      icon: "target",
      answers: pickAnswers(["Objetivo corporal", "Meta", "Conformidad"]),
    },
  ];

  return sections;
}

function formatAssessmentQuestionLabel(label: string) {
  const lower = label.toLocaleLowerCase("es-PE");
  return lower.replace(/^(\s*¿?\s*)([a-záéíóúñ])/u, (_match, prefix: string, firstLetter: string) =>
    `${prefix}${firstLetter.toLocaleUpperCase("es-PE")}`,
  );
}

function renderAssessmentSummaryIcon(icon: string) {
  if (icon === "fork") {
    return <svg viewBox="0 0 24 24"><path d="M7 3v8" /><path d="M4 3v8" /><path d="M10 3v8" /><path d="M4 11h6" /><path d="M7 11v10" /><path d="M17 3v18" /><path d="M14 7c0-2 1.5-4 3-4" /></svg>;
  }
  if (icon === "dumbbell") {
    return <svg viewBox="0 0 24 24"><path d="M6 8v8M18 8v8M3 10v4M21 10v4M6 12h12" /></svg>;
  }
  if (icon === "clock") {
    return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><path d="M12 7v5l3 2" /></svg>;
  }
  if (icon === "pill") {
    return <svg viewBox="0 0 24 24"><path d="M10 21 3 14a5 5 0 0 1 7-7l7 7a5 5 0 0 1-7 7z" /><path d="m7 10 7 7" /></svg>;
  }
  if (icon === "arm") {
    return <svg viewBox="0 0 24 24"><path d="M7 20c4 0 7-2 7-6V8a3 3 0 0 1 6 0v3" /><path d="M7 20c-2-2-3-5-2-8l2-7" /><path d="M11 13h8" /></svg>;
  }
  if (icon === "heart") {
    return <svg viewBox="0 0 24 24"><path d="M20.5 6.5a5 5 0 0 0-7 0L12 8l-1.5-1.5a5 5 0 0 0-7 7L12 22l8.5-8.5a5 5 0 0 0 0-7z" /><path d="M7 12h3l2-3 2 6 2-3h2" /></svg>;
  }
  if (icon === "leaf") {
    return <svg viewBox="0 0 24 24"><path d="M5 19c8 0 14-6 14-14C11 5 5 11 5 19z" /><path d="M5 19 15 9" /></svg>;
  }
  if (icon === "shield") {
    return <svg viewBox="0 0 24 24"><path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6z" /><path d="m9 12 2 2 4-5" /></svg>;
  }
  if (icon === "cross") {
    return <svg viewBox="0 0 24 24"><path d="M12 5v14" /><path d="M5 12h14" /></svg>;
  }
  if (icon === "target") {
    return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v4M22 12h-4" /></svg>;
  }
  return <svg viewBox="0 0 24 24"><circle cx="12" cy="7" r="3" /><path d="M5 21a7 7 0 0 1 14 0" /></svg>;
}

function getCheckInWeekMeta(checkIns: WeeklyCheckIn[] = [], checkInId: string) {
  const ordered = [...checkIns].sort((a, b) => {
    const weekComparison = a.weekStart.localeCompare(b.weekStart);
    if (weekComparison !== 0) {
      return weekComparison;
    }

    return new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
  });
  const index = ordered.findIndex((item) => item.id === checkInId);
  const selected = ordered[index];

  if (!selected) {
    return {
      label: "Semana",
      date: "—",
    };
  }

  return {
    label: `Semana ${index + 1}`,
    date: selected.weekStart.slice(0, 10),
  };
}

function getCheckInWeekNumber(checkIns: WeeklyCheckIn[] = [], checkInId: string) {
  const ordered = [...checkIns].sort((a, b) => {
    const weekComparison = a.weekStart.localeCompare(b.weekStart);
    if (weekComparison !== 0) {
      return weekComparison;
    }

    return new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
  });
  const index = ordered.findIndex((item) => item.id === checkInId);
  return index >= 0 ? index + 1 : 1;
}

function sanitizePdfFileNamePart(value: string) {
  const sanitized = value
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "");

  return sanitized || "Cliente";
}

function buildCheckInPdfTitle(clientName: string, weekNumber: number) {
  const safeWeekNumber = Math.max(1, weekNumber).toString().padStart(2, "0");
  return `${sanitizePdfFileNamePart(clientName)}_Semana${safeWeekNumber}_JCFitness`;
}

function printCheckInDocument(fileName: string) {
  const previousTitle = document.title;
  let restored = false;
  const restoreTitle = () => {
    if (restored) return;
    restored = true;
    document.title = previousTitle;
    window.removeEventListener("afterprint", restoreTitle);
  };

  document.title = fileName;
  window.addEventListener("afterprint", restoreTitle);
  window.print();
  window.setTimeout(restoreTitle, 15000);
}

function getCheckInWeekStart(checkIn: WeeklyCheckIn) {
  return checkIn.weekStart.slice(0, 10);
}

function getSavedWeekCheckIns(checkIns: WeeklyCheckIn[] = []) {
  return [...checkIns].sort((left, right) => {
    const submittedComparison = new Date(right.submittedAt).getTime() - new Date(left.submittedAt).getTime();
    if (submittedComparison !== 0) {
      return submittedComparison;
    }

    return getCheckInWeekStart(right).localeCompare(getCheckInWeekStart(left));
  });
}

const assessmentRoutineSections: Array<{
  id: string;
  title: string;
  copy: string;
  tone: string;
  keys: AssessmentQuestionKey[];
  icon: "person" | "fork" | "dumbbell" | "heart" | "leaf" | "cross" | "target";
}> = [
  {
    id: "basics",
    title: "Datos básicos",
    copy: "Información física y tu historial actual.",
    tone: "orange",
    icon: "person",
    keys: ["height", "currentWeight", "age", "gymExperienceTime", "dietExperienceTime", "previousDiet"],
  },
  {
    id: "nutrition",
    title: "Alimentación actual y suplementos",
    copy: "Hábitos alimenticios y suplementación.",
    tone: "orange",
    icon: "fork",
    keys: ["currentDietDetail", "canBuyProteinPowder", "solidMealsAndShakes", "supplements", "currentSteroids", "steroidHistory"],
  },
  {
    id: "training",
    title: "Entrenamiento",
    copy: "Rutina, frecuencia y nivel de entrenamiento.",
    tone: "purple",
    icon: "dumbbell",
    keys: ["trainingDays", "weeklyTrainingDays", "trainingSplit", "trainingLevel", "trainingTime"],
  },
  {
    id: "cardio",
    title: "Cardio y hábitos",
    copy: "Cardio actual, sueño y tu día a día.",
    tone: "blue",
    icon: "heart",
    keys: ["hasStepWatch", "doesCardio", "cardioDuration", "cardioType", "canDoFastedCardio", "canDoCardioOtherTime", "wakeUpTime", "sleepTime"],
  },
  {
    id: "preferences",
    title: "Preferencias de alimentación",
    copy: "Tolerancias y fuentes de macronutrientes.",
    tone: "green",
    icon: "leaf",
    keys: ["dailyRoutine", "appetiteProfile", "favoriteProteinSources", "favoriteCarbSources", "favoriteFatSources"],
  },
  {
    id: "health",
    title: "Salud y antecedentes",
    copy: "Alergias, cirugías y antecedentes familiares.",
    tone: "red",
    icon: "cross",
    keys: ["allergies", "surgeries", "pathologies", "familyHistory"],
  },
  {
    id: "goal",
    title: "Objetivo y condiciones",
    copy: "Tu meta y aceptación del plan.",
    tone: "orange",
    icon: "target",
    keys: ["fatLossOrGrowth", "goal", "conformity"],
  },
];

function renderRoutineSectionIcon(icon: (typeof assessmentRoutineSections)[number]["icon"]) {
  if (icon === "person") {
    return <svg viewBox="0 0 24 24"><circle cx="12" cy="7" r="3" /><path d="M5 21a7 7 0 0 1 14 0" /></svg>;
  }
  if (icon === "fork") {
    return <svg viewBox="0 0 24 24"><path d="M7 3v8" /><path d="M4 3v8" /><path d="M10 3v8" /><path d="M4 11h6" /><path d="M7 11v10" /><path d="M17 3v18" /><path d="M14 7c0-2 1.5-4 3-4" /></svg>;
  }
  if (icon === "dumbbell") {
    return <svg viewBox="0 0 24 24"><path d="M6 8v8M18 8v8M3 10v4M21 10v4M6 12h12" /></svg>;
  }
  if (icon === "heart") {
    return <svg viewBox="0 0 24 24"><path d="M20.5 6.5a5 5 0 0 0-7 0L12 8l-1.5-1.5a5 5 0 0 0-7 7L12 22l8.5-8.5a5 5 0 0 0 0-7z" /><path d="M7 12h3l2-3 2 6 2-3h2" /></svg>;
  }
  if (icon === "leaf") {
    return <svg viewBox="0 0 24 24"><path d="M5 19c8 0 14-6 14-14C11 5 5 11 5 19z" /><path d="M5 19 15 9" /></svg>;
  }
  if (icon === "cross") {
    return <svg viewBox="0 0 24 24"><path d="M10 3h4v7h7v4h-7v7h-4v-7H3v-4h7z" /></svg>;
  }
  return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v4M22 12h-4" /></svg>;
}

function getAssessmentQuestionTone(key: AssessmentQuestionKey) {
  const tones = ["blue", "orange", "teal", "purple", "amber", "green", "red", "sky"] as const;
  const index = assessmentQuestions.findIndex((question) => question.key === key);
  return tones[Math.max(index, 0) % tones.length];
}

function renderAssessmentQuestionIcon(key: AssessmentQuestionKey) {
  if (["height", "currentWeight", "age", "gymExperienceTime", "dietExperienceTime"].includes(key)) {
    return <svg viewBox="0 0 24 24"><circle cx="12" cy="7" r="3" /><path d="M5 21a7 7 0 0 1 14 0" /></svg>;
  }
  if (["currentDietDetail", "previousDiet", "solidMealsAndShakes", "favoriteProteinSources", "favoriteCarbSources", "favoriteFatSources"].includes(key)) {
    return <svg viewBox="0 0 24 24"><path d="M7 3v8" /><path d="M4 3v8" /><path d="M10 3v8" /><path d="M4 11h6" /><path d="M7 11v10" /><path d="M17 3v18" /></svg>;
  }
  if (["trainingDays", "weeklyTrainingDays", "trainingSplit", "trainingLevel", "trainingTime"].includes(key)) {
    return <svg viewBox="0 0 24 24"><path d="M6 8v8M18 8v8M3 10v4M21 10v4M6 12h12" /></svg>;
  }
  if (["doesCardio", "cardioDuration", "cardioType", "canDoFastedCardio", "canDoCardioOtherTime"].includes(key)) {
    return <svg viewBox="0 0 24 24"><path d="M20.5 6.5a5 5 0 0 0-7 0L12 8l-1.5-1.5a5 5 0 0 0-7 7L12 22l8.5-8.5a5 5 0 0 0 0-7z" /></svg>;
  }
  if (["wakeUpTime", "sleepTime", "dailyRoutine", "appetiteProfile"].includes(key)) {
    return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><path d="M12 7v5l3 2" /></svg>;
  }
  if (["allergies", "surgeries", "pathologies", "familyHistory"].includes(key)) {
    return <svg viewBox="0 0 24 24"><path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6z" /><path d="m9 12 2 2 4-5" /></svg>;
  }
  if (["fatLossOrGrowth", "goal", "conformity"].includes(key)) {
    return <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v4M22 12h-4" /></svg>;
  }
  return <svg viewBox="0 0 24 24"><path d="M5 4h14v13H8l-3 3z" /><path d="M9 8h6M9 12h4" /></svg>;
}

function renderAssessmentField(
  question: AssessmentQuestion,
  value: string,
  onChange: (nextValue: string) => void,
) {
  if (question.inputType === "checkbox") {
    return (
      <label className="assessment-checkbox-field">
        <input
          type="checkbox"
          checked={value === "SI"}
          onChange={(event) => onChange(event.target.checked ? "SI" : "")}
        />
        <span>Confirmo que leí las condiciones, entiendo que no hay reembolsos ni congelamiento del plan, estoy conforme y deseo continuar.</span>
      </label>
    );
  }

  if (question.key === "dietExperienceTime") {
    const noDietValue = "No hago dieta";
    const isNoDiet = value === noDietValue;

    return (
      <div className="assessment-diet-duration-field">
        <input
          type="number"
          min={question.min}
          max={question.max}
          step={question.step ?? 1}
          placeholder={question.unit}
          value={isNoDiet ? "" : value}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className={isNoDiet ? "is-active" : ""}
          onClick={() => onChange(isNoDiet ? "" : noDietValue)}
        >
          No hago dieta
        </button>
      </div>
    );
  }

  if (question.inputType === "number") {
    return (
      <input
        type="number"
        min={question.min}
        max={question.max}
        step={question.step ?? 1}
        placeholder={question.unit}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (question.inputType === "select") {
    return (
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {question.options?.map((option) => (
          <option key={`${question.key}-${option.value || option.label}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (question.inputType === "time") {
    return <input type="time" value={value} onChange={(event) => onChange(event.target.value)} />;
  }

  return (
    <textarea
      value={value}
      rows={1}
      placeholder={question.helper || "Escribe aquí..."}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function formatWeekdayLabel(value: string) {
  const date = new Date(`${value}T00:00:00`);
  const label = new Intl.DateTimeFormat("es-PE", { weekday: "long" }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatShortDayDate(value: string) {
  const date = new Date(`${value}T00:00:00`);
  return new Intl.DateTimeFormat("es-PE", { day: "numeric", month: "short" })
    .format(date)
    .replace(".", "");
}

function formatDietTypeLabel(value?: string | null) {
  const labels: Record<string, string> = {
    HIGH: "High",
    High: "High",
    Alta: "High",
    MEDIUM: "Mid",
    Mid: "Mid",
    Media: "Mid",
    LOW: "Low",
    Low: "Low",
    Baja: "Low",
    "Dias On": "Dias On",
    "Dia On": "Dias On",
    "Días On": "Dias On",
    "Dia Off": "Dia Off",
    "Día Off": "Dia Off",
  };
  return value ? labels[value] ?? value : "";
}

function formatMuscleTrainedLabel(value?: string | null) {
  const labels: Record<string, string> = {
    CHEST: "Pecho",
    PUSH: "Push",
    Push: "Push",
    Empuje: "Pecho",
    Pecho: "Pecho",
    BACK: "Espalda",
    PULL: "Pull",
    Pull: "Pull",
    "Jalón": "Espalda",
    Jalon: "Espalda",
    Espalda: "Espalda",
    SHOULDERS: "Hombros",
    Hombros: "Hombros",
    BICEPS: "Bíceps",
    Biceps: "Bíceps",
    TRICEPS: "Tríceps",
    Triceps: "Tríceps",
    LEGS: "Piernas",
    Legs: "Piernas",
    Piernas: "Piernas",
    UPPER: "Hombros",
    Upper: "Hombros",
    GLUTES: "Glúteos",
    Gluteos: "Glúteos",
    Glúteos: "Glúteos",
    FEMORALES: "Femorales",
    Femorales: "Femorales",
    QUADRICEPS: "Quadriceps",
    Quadriceps: "Quadriceps",
    "Cadena posterior": "Cadena posterior",
    CADENA_POSTERIOR: "Cadena posterior",
    "Gluteo y femoral": "Gluteo y femoral",
    GLUTE_FEMORAL: "Gluteo y femoral",
    "Gluteo y cuadriceps": "Gluteo y cuadriceps",
    GLUTE_QUAD: "Gluteo y cuadriceps",
    ABS: "Abdomen",
    Abdomen: "Abdomen",
    FULL_BODY: "Cuerpo completo",
    "Full body": "Cuerpo completo",
    "Cuerpo completo": "Cuerpo completo",
    REST: "Descanso",
    "Descanso activo": "Descanso",
    Descanso: "Descanso",
  };
  return value ? labels[value] ?? value : "";
}

function normalizeMuscleTrainedValue(value?: string | null) {
  const labels: Record<string, string> = {
    CHEST: "CHEST",
    PUSH: "Push",
    Push: "Push",
    Empuje: "CHEST",
    Pecho: "CHEST",
    BACK: "BACK",
    PULL: "Pull",
    Pull: "Pull",
    Jalón: "BACK",
    Jalon: "BACK",
    Espalda: "BACK",
    SHOULDERS: "SHOULDERS",
    UPPER: "SHOULDERS",
    Upper: "SHOULDERS",
    Hombros: "SHOULDERS",
    BICEPS: "BICEPS",
    Bíceps: "BICEPS",
    Biceps: "BICEPS",
    TRICEPS: "TRICEPS",
    Tríceps: "TRICEPS",
    Triceps: "TRICEPS",
    LEGS: "LEGS",
    Legs: "LEGS",
    Piernas: "LEGS",
    GLUTES: "GLUTES",
    Glúteos: "GLUTES",
    Gluteos: "GLUTES",
    FEMORALES: "Femorales",
    Femorales: "Femorales",
    QUADRICEPS: "Quadriceps",
    Quadriceps: "Quadriceps",
    CADENA_POSTERIOR: "Cadena posterior",
    "Cadena posterior": "Cadena posterior",
    "Cadena ponsterior": "Cadena posterior",
    GLUTE_FEMORAL: "Gluteo y femoral",
    "Gluteo y femoral": "Gluteo y femoral",
    "Glúteo y femoral": "Gluteo y femoral",
    GLUTE_QUAD: "Gluteo y cuadriceps",
    "Gluteo y cuadriceps": "Gluteo y cuadriceps",
    "Glúteo y cuádriceps": "Gluteo y cuadriceps",
    ABS: "ABS",
    Abdomen: "ABS",
    FULL_BODY: "FULL_BODY",
    "Full body": "FULL_BODY",
    "Full Body": "FULL_BODY",
    "Cuerpo completo": "FULL_BODY",
    REST: "REST",
    "Descanso activo": "REST",
    Descanso: "REST",
  };
  return value ? labels[value] ?? value : "";
}

function normalizeWeekday(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function extractTrainingDays(value: string) {
  const weekdayMatchers = [
    { aliases: ["lunes", "lun"], label: "Lunes" },
    { aliases: ["martes", "mar"], label: "Martes" },
    { aliases: ["miércoles", "miércoles", "mie", "mié"], label: "Miércoles" },
    { aliases: ["jueves", "jue"], label: "Jueves" },
    { aliases: ["viernes", "vie"], label: "Viernes" },
    { aliases: ["sábado", "sábado", "sab", "sáb"], label: "Sábado" },
    { aliases: ["domingo", "dom"], label: "Domingo" },
  ];

  const normalizedValue = normalizeWeekday(value);
  const specificSegmentMatch = normalizedValue.match(/días? de entrenamiento\s*:\s*([^.\n]+)/);
  const source = specificSegmentMatch?.[1]?.trim() || normalizedValue;

  return weekdayMatchers
    .filter((item) => item.aliases.some((alias) => source.includes(normalizeWeekday(alias))))
    .map((item) => item.label);
}

function generateWeeklyEntries(
  weekStart: string,
  previousEntries: Array<any> = [],
  _allowedDays: string[] = [],
) {
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(`${weekStart}T00:00:00`);
    date.setDate(date.getDate() + index);
    const isoDate = date.toISOString().slice(0, 10);
    const label = formatWeekdayLabel(isoDate);
    const previous = previousEntries.find((entry) => entry.date === isoDate);

    return {
      date: isoDate,
      label,
      waterLiters: previous?.waterLiters ?? "",
      sleepHours: previous?.sleepHours ?? "",
      weightKg: previous?.weightKg ?? "",
      steps: previous?.steps ?? "",
      proteinGrams: previous?.proteinGrams ?? "",
      carbsGrams: previous?.carbsGrams ?? "",
      fatsGrams: previous?.fatsGrams ?? "",
      calories: previous?.calories ?? "",
      recoveryScore: previous?.recoveryScore ?? "",
      dietType: formatDietTypeLabel(previous?.dietType),
      trainedMuscle: formatMuscleTrainedLabel(previous?.trainedMuscle),
      trainingPerformance: previous?.trainingPerformance ?? "",
      cardio: previous?.cardio ?? "",
      bloodGlucose: previous?.bloodGlucose ?? "",
      dayReflections: previous?.dayReflections ?? "",
      bloodPressure: previous?.bloodPressure ?? "",
    };
  });
}

function buildJuanPerezWeeklyEntries(weekStart: string, allowedDays: string[] = []) {
  const presets: Record<string, Record<string, string>> = {
    lunes: {
      waterLiters: "2.8",
      sleepHours: "7",
      weightKg: "82.4",
      steps: "9800",
      recoveryScore: "8",
      dietType: "High",
      trainedMuscle: "Pull",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "88",
      dayReflections: "Energía estable.",
      bloodPressure: "118/76",
    },
    martes: {
      waterLiters: "2.6",
      sleepHours: "6.5",
      weightKg: "82.1",
      steps: "10400",
      recoveryScore: "7",
      dietType: "Mid",
      trainedMuscle: "Push",
      trainingPerformance: "Verde",
      cardio: "25 min bici",
      bloodGlucose: "90",
      dayReflections: "Buen entrenamiento.",
      bloodPressure: "120/78",
    },
    miércoles: {
      waterLiters: "2.7",
      sleepHours: "7.1",
      weightKg: "82.0",
      steps: "8900",
      recoveryScore: "8",
      dietType: "Mid",
      trainedMuscle: "Descanso activo",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "88",
      dayReflections: "Recuperación correcta.",
      bloodPressure: "118/76",
    },
    jueves: {
      waterLiters: "2.7",
      sleepHours: "7.2",
      weightKg: "81.9",
      steps: "9200",
      recoveryScore: "8",
      dietType: "Low",
      trainedMuscle: "Legs",
      trainingPerformance: "Amarillo",
      cardio: "15 min escaladora",
      bloodGlucose: "86",
      dayReflections: "Piernas pesadas.",
      bloodPressure: "119/77",
    },
    viernes: {
      waterLiters: "3",
      sleepHours: "7.5",
      weightKg: "81.7",
      steps: "11050",
      recoveryScore: "8",
      dietType: "Mid",
      trainedMuscle: "Upper",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "87",
      dayReflections: "Semana controlada.",
      bloodPressure: "117/75",
    },
    sábado: {
      waterLiters: "2.5",
      sleepHours: "8",
      weightKg: "81.8",
      steps: "8750",
      recoveryScore: "9",
      dietType: "High",
      trainedMuscle: "Full body",
      trainingPerformance: "Verde",
      cardio: "Libre",
      bloodGlucose: "89",
      dayReflections: "Buen descanso.",
      bloodPressure: "118/76",
    },
    domingo: {
      waterLiters: "2.6",
      sleepHours: "7.8",
      weightKg: "81.9",
      steps: "7800",
      recoveryScore: "8",
      dietType: "Mid",
      trainedMuscle: "Descanso activo",
      trainingPerformance: "Verde",
      cardio: "Caminata ligera",
      bloodGlucose: "88",
      dayReflections: "Descanso completo.",
      bloodPressure: "117/75",
    },
  };

  return generateWeeklyEntries(weekStart, [], allowedDays).map((entry) => ({
    ...entry,
    ...(presets[normalizeWeekday(entry.label)] ?? {}),
  }));
}

function buildYoelSosanWeeklyEntries(weekStart: string, allowedDays: string[] = []) {
  const presets: Record<string, Record<string, string>> = {
    lunes: {
      waterLiters: "2.7",
      sleepHours: "7",
      weightKg: "78.4",
      steps: "9200",
      recoveryScore: "8",
      dietType: "Mid",
      trainedMuscle: "Push",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "89",
      dayReflections: "Energía estable.",
      bloodPressure: "118/76",
    },
    martes: {
      waterLiters: "2.8",
      sleepHours: "6.8",
      weightKg: "78.2",
      steps: "10100",
      recoveryScore: "7",
      dietType: "High",
      trainedMuscle: "Pull",
      trainingPerformance: "Verde",
      cardio: "25 min bici",
      bloodGlucose: "91",
      dayReflections: "Buena fuerza.",
      bloodPressure: "119/77",
    },
    miércoles: {
      waterLiters: "2.5",
      sleepHours: "7.1",
      weightKg: "78.0",
      steps: "8700",
      recoveryScore: "8",
      dietType: "Mid",
      trainedMuscle: "Descanso activo",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "88",
      dayReflections: "Día tranquilo.",
      bloodPressure: "117/75",
    },
    jueves: {
      waterLiters: "3",
      sleepHours: "7.3",
      weightKg: "77.9",
      steps: "10800",
      recoveryScore: "8",
      dietType: "High",
      trainedMuscle: "Legs",
      trainingPerformance: "Amarillo",
      cardio: "15 min escaladora",
      bloodGlucose: "90",
      dayReflections: "Sesión completa.",
      bloodPressure: "120/78",
    },
    viernes: {
      waterLiters: "2.9",
      sleepHours: "7.5",
      weightKg: "77.7",
      steps: "11200",
      recoveryScore: "9",
      dietType: "Mid",
      trainedMuscle: "Upper",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "87",
      dayReflections: "Muy buena energía.",
      bloodPressure: "118/76",
    },
    sábado: {
      waterLiters: "2.6",
      sleepHours: "8",
      weightKg: "77.8",
      steps: "9400",
      recoveryScore: "9",
      dietType: "Low",
      trainedMuscle: "Full body",
      trainingPerformance: "Verde",
      cardio: "Libre",
      bloodGlucose: "88",
      dayReflections: "Buena adherencia.",
      bloodPressure: "117/75",
    },
    domingo: {
      waterLiters: "2.4",
      sleepHours: "7.8",
      weightKg: "77.9",
      steps: "7600",
      recoveryScore: "8",
      dietType: "Mid",
      trainedMuscle: "Descanso",
      trainingPerformance: "Verde",
      cardio: "Caminata ligera",
      bloodGlucose: "89",
      dayReflections: "Descanso correcto.",
      bloodPressure: "118/76",
    },
  };

  return generateWeeklyEntries(weekStart, [], allowedDays).map((entry) => ({
    ...entry,
    ...(presets[normalizeWeekday(entry.label)] ?? {}),
  }));
}

function buildAlexGilWeeklyEntries(weekStart: string, allowedDays: string[] = []) {
  const wednesdayPreset = {
    waterLiters: "2.3",
    sleepHours: "6.8",
    weightKg: "57.8",
    steps: "6900",
    proteinGrams: "130",
    carbsGrams: "175",
    fatsGrams: "58",
    calories: "1682",
    recoveryScore: "7",
    dietType: "Mid",
    trainedMuscle: "Descanso activo",
    trainingPerformance: "Verde",
    cardio: "20 min caminata",
    bloodGlucose: "87",
    dayReflections: "Día tranquilo.",
    bloodPressure: "116/74",
  };
  const saturdayPreset = {
    waterLiters: "2.2",
    sleepHours: "8",
    weightKg: "57.7",
    steps: "7600",
    proteinGrams: "128",
    carbsGrams: "160",
    fatsGrams: "62",
    calories: "1660",
    recoveryScore: "9",
    dietType: "Low",
    trainedMuscle: "Full body",
    trainingPerformance: "Verde",
    cardio: "Libre",
    bloodGlucose: "89",
    dayReflections: "Buen descanso.",
    bloodPressure: "116/74",
  };
  const presets: Record<string, Record<string, string>> = {
    lunes: {
      waterLiters: "2.4",
      sleepHours: "6.5",
      weightKg: "58.0",
      steps: "7200",
      proteinGrams: "130",
      carbsGrams: "180",
      fatsGrams: "55",
      calories: "1675",
      recoveryScore: "7",
      dietType: "Mid",
      trainedMuscle: "Push",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "88",
      dayReflections: "Buen inicio.",
      bloodPressure: "116/74",
    },
    martes: {
      waterLiters: "2.5",
      sleepHours: "7",
      weightKg: "57.9",
      steps: "8100",
      proteinGrams: "132",
      carbsGrams: "220",
      fatsGrams: "50",
      calories: "1858",
      recoveryScore: "8",
      dietType: "High",
      trainedMuscle: "Pull",
      trainingPerformance: "Verde",
      cardio: "25 min bici",
      bloodGlucose: "89",
      dayReflections: "Buena energía.",
      bloodPressure: "118/76",
    },
    miércoles: wednesdayPreset,
    miercoles: wednesdayPreset,
    jueves: {
      waterLiters: "2.6",
      sleepHours: "7.2",
      weightKg: "57.7",
      steps: "8600",
      proteinGrams: "135",
      carbsGrams: "225",
      fatsGrams: "52",
      calories: "1900",
      recoveryScore: "8",
      dietType: "High",
      trainedMuscle: "Piernas",
      trainingPerformance: "Amarillo",
      cardio: "15 min escaladora",
      bloodGlucose: "90",
      dayReflections: "Piernas pesadas.",
      bloodPressure: "118/76",
    },
    viernes: {
      waterLiters: "2.6",
      sleepHours: "7.4",
      weightKg: "57.6",
      steps: "9300",
      proteinGrams: "132",
      carbsGrams: "190",
      fatsGrams: "55",
      calories: "1743",
      recoveryScore: "8",
      dietType: "Mid",
      trainedMuscle: "Upper",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "88",
      dayReflections: "Semana estable.",
      bloodPressure: "117/75",
    },
    sábado: saturdayPreset,
    sabado: saturdayPreset,
    domingo: {
      waterLiters: "2.3",
      sleepHours: "7.6",
      weightKg: "57.8",
      steps: "6400",
      proteinGrams: "130",
      carbsGrams: "170",
      fatsGrams: "60",
      calories: "1700",
      recoveryScore: "8",
      dietType: "Mid",
      trainedMuscle: "Descanso",
      trainingPerformance: "Verde",
      cardio: "Caminata ligera",
      bloodGlucose: "88",
      dayReflections: "Descanso correcto.",
      bloodPressure: "117/75",
    },
  };

  return generateWeeklyEntries(weekStart, [], allowedDays).map((entry) => ({
    ...entry,
    ...(presets[normalizeWeekday(entry.label)] ?? {}),
  }));
}

function toOptionalNumber(value: string) {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : undefined;
}

function parseTrackingNumber(value: unknown) {
  const parsed = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function calculateDailyCalories(entry: { proteinGrams?: unknown; carbsGrams?: unknown; fatsGrams?: unknown }) {
  const protein = parseTrackingNumber(entry.proteinGrams);
  const carbs = parseTrackingNumber(entry.carbsGrams);
  const fats = parseTrackingNumber(entry.fatsGrams);

  if (protein === null && carbs === null && fats === null) {
    return "";
  }

  const calories = (protein ?? 0) * 4 + (carbs ?? 0) * 4 + (fats ?? 0) * 9;
  return Number.isInteger(calories) ? String(calories) : calories.toFixed(1);
}

function getStartOfWeekIso(value: string) {
  const date = new Date(`${value}T00:00:00`);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date.toISOString().slice(0, 10);
}

function generateTrackingRows(
  weekStart: string,
  _allowedDays: string[] = [],
  previousRows: Array<any> = [],
) {
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(`${weekStart}T00:00:00`);
    date.setDate(date.getDate() + index);
    const isoDate = date.toISOString().slice(0, 10);
    const label = formatWeekdayLabel(isoDate);
    const previous = previousRows.find((entry) => entry.date === isoDate);

    return {
      date: isoDate,
      label,
      waterLiters: previous?.waterLiters ?? "",
      sleepHours: previous?.sleepHours ?? "",
      steps: previous?.steps ?? "",
      proteinGrams: previous?.proteinGrams ?? "",
      carbsGrams: previous?.carbsGrams ?? "",
      fatsGrams: previous?.fatsGrams ?? "",
      calories: previous?.calories ?? "",
      recoveryScore: previous?.recoveryScore ?? "",
      weightKg: previous?.weightKg ?? "",
      dietType: formatDietTypeLabel(previous?.dietType),
      trainedMuscle: formatMuscleTrainedLabel(previous?.trainedMuscle),
      trainingPerformance: previous?.trainingPerformance ?? "",
      cardio: previous?.cardio ?? "",
      bloodGlucose: previous?.bloodGlucose ?? "",
      dayReflections: previous?.dayReflections ?? "",
      bloodPressure: previous?.bloodPressure ?? "",
    };
  });
}

function extractObservationValue(observations: string, label: string) {
  const normalizedLabel = normalizeWeekday(label);
  const lines = observations
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const matchedLine = lines.find((line) => {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) {
      return false;
    }

    const lineLabel = normalizeWeekday(line.slice(0, separatorIndex));
    return lineLabel === normalizedLabel;
  });

  if (!matchedLine) {
    return "";
  }

  return matchedLine.slice(matchedLine.indexOf(":") + 1).trim();
}

function parseDailyObservationEntries(observations: string) {
  const byDate = new Map<string, any>();
  const jsonMatch = observations.match(/TRACKING_DIARIO_JSON:\s*(\[[\s\S]*?\])\s*(?:\n\n|$)/);
  if (jsonMatch?.[1]) {
    try {
      const entries = JSON.parse(jsonMatch[1]);
      if (Array.isArray(entries)) {
        entries.forEach((entry) => {
          if (entry?.date) {
            byDate.set(entry.date, entry);
          }
        });
        return byDate;
      }
    } catch {
      // Fall back to the legacy text parser below.
    }
  }

  const pattern = /^.+\((\d{4}-\d{2}-\d{2})\): peso .*?, pasos (.*?), dieta (.*)$/gim;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(observations)) !== null) {
    byDate.set(match[1], {
      steps: match[2] === "-" ? "" : match[2],
      dietType: match[3] === "-" ? "" : match[3],
    });
  }

  return byDate;
}

function getDailyCheckInFallback(label: string) {
  const fallbackByDay: Record<string, Record<string, string>> = {
    lunes: {
      sleepHours: "7",
      steps: "9800",
      weightKg: "82.4",
      dietType: "High",
      trainedMuscle: "Pull",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "88",
      dayReflections: "Energía estable.",
      bloodPressure: "118/76",
    },
    martes: {
      sleepHours: "6.5",
      steps: "10400",
      weightKg: "82.1",
      dietType: "Mid",
      trainedMuscle: "Push",
      trainingPerformance: "Verde",
      cardio: "25 min bici",
      bloodGlucose: "90",
      dayReflections: "Buen entrenamiento.",
      bloodPressure: "120/78",
    },
    miércoles: {
      sleepHours: "7.1",
      steps: "8900",
      weightKg: "82.0",
      dietType: "Mid",
      trainedMuscle: "Descanso activo",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "88",
      dayReflections: "Recuperación correcta.",
      bloodPressure: "118/76",
    },
    jueves: {
      sleepHours: "7.2",
      steps: "9200",
      weightKg: "81.9",
      dietType: "Low",
      trainedMuscle: "Legs",
      trainingPerformance: "Amarillo",
      cardio: "15 min escaladora",
      bloodGlucose: "86",
      dayReflections: "Piernas pesadas.",
      bloodPressure: "119/77",
    },
    viernes: {
      sleepHours: "7.5",
      steps: "11050",
      weightKg: "81.7",
      dietType: "Mid",
      trainedMuscle: "Upper",
      trainingPerformance: "Verde",
      cardio: "20 min caminata",
      bloodGlucose: "87",
      dayReflections: "Semana controlada.",
      bloodPressure: "117/75",
    },
    sábado: {
      sleepHours: "8",
      steps: "8750",
      weightKg: "81.8",
      dietType: "High",
      trainedMuscle: "Full body",
      trainingPerformance: "Verde",
      cardio: "Libre",
      bloodGlucose: "89",
      dayReflections: "Buen descanso.",
      bloodPressure: "118/76",
    },
    domingo: {
      sleepHours: "7.8",
      steps: "7800",
      weightKg: "81.9",
      dietType: "Mid",
      trainedMuscle: "Descanso activo",
      trainingPerformance: "Verde",
      cardio: "Caminata ligera",
      bloodGlucose: "88",
      dayReflections: "Descanso completo.",
      bloodPressure: "117/75",
    },
  };

  return fallbackByDay[normalizeWeekday(label)] ?? {};
}

function buildTrackingRowsFromCheckIn(
  checkIn: WeeklyCheckIn,
  allowedDays: string[] = [],
  previousRows: Array<any> = [],
) {
  const observations = checkIn.observations || "";
  const dailyObservationEntries = parseDailyObservationEntries(observations);
  const baseRows = generateTrackingRows(checkIn.weekStart.slice(0, 10), allowedDays, previousRows);

  return baseRows.map((row) => {
    const observationRow = dailyObservationEntries.get(row.date);
    const savedWeight = checkIn.dailyWeights.find((weight) => weight.date.slice(0, 10) === row.date);
    const savedEntry = checkIn.dailyEntries?.find((entry) => entry.date.slice(0, 10) === row.date);
    const fallback = getDailyCheckInFallback(row.label);

    return {
      ...row,
      waterLiters: savedEntry?.waterLiters ? String(savedEntry.waterLiters) : observationRow?.waterLiters || (checkIn.waterLiters ? String(checkIn.waterLiters) : row.waterLiters),
      sleepHours: savedEntry?.sleepHours ? String(savedEntry.sleepHours) : observationRow?.sleepHours || row.sleepHours || fallback.sleepHours || "",
      steps: observationRow?.steps || row.steps || fallback.steps || "",
      proteinGrams: savedEntry?.proteinGrams ? String(savedEntry.proteinGrams) : row.proteinGrams || "",
      carbsGrams: savedEntry?.carbsGrams ? String(savedEntry.carbsGrams) : row.carbsGrams || "",
      fatsGrams: savedEntry?.fatsGrams ? String(savedEntry.fatsGrams) : row.fatsGrams || "",
      calories: savedEntry?.calories ? String(savedEntry.calories) : row.calories || "",
      recoveryScore: observationRow?.recoveryScore || (checkIn.sleepScore ? String(checkIn.sleepScore) : row.recoveryScore),
      weightKg: savedWeight?.weightKg ? String(savedWeight.weightKg) : row.weightKg || fallback.weightKg || "",
      dietType: formatDietTypeLabel(savedEntry?.dietType) || observationRow?.dietType || row.dietType || fallback.dietType || "",
      trainedMuscle: formatMuscleTrainedLabel(savedEntry?.muscleTrained) || observationRow?.trainedMuscle || row.trainedMuscle || fallback.trainedMuscle || "",
      trainingPerformance: observationRow?.trainingPerformance || row.trainingPerformance || fallback.trainingPerformance || "",
      cardio: savedEntry?.cardio || observationRow?.cardio || row.cardio || fallback.cardio || "",
      bloodGlucose: savedEntry?.bloodGlucose || observationRow?.bloodGlucose || row.bloodGlucose || fallback.bloodGlucose || "",
      dayReflections: savedEntry?.dailyNotes || observationRow?.dayReflections || row.dayReflections || fallback.dayReflections || "",
      bloodPressure: savedEntry?.bloodPressure || observationRow?.bloodPressure || row.bloodPressure || fallback.bloodPressure || "",
    };
  });
}

function averageTrackingField(rows: Array<any>, field: string) {
  const values = rows
    .map((row) => Number(String(row[field] ?? "").replace(",", ".")))
    .filter((value) => !Number.isNaN(value) && value > 0);

  if (values.length === 0) {
    return "-";
  }

  return (values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1);
}

function calculateWeightVariation(rows: Array<any>) {
  const weights = rows
    .map((row) => Number(String(row.weightKg ?? "").replace(",", ".")))
    .filter((value) => !Number.isNaN(value) && value > 0);

  if (weights.length < 2) {
    return "-";
  }

  return (weights[weights.length - 1] - weights[0]).toFixed(1);
}

function calculateWeeklyAverageWeightVariation(currentRows: Array<any>, previousRows: Array<any>) {
  const currentAverage = averageTrackingField(currentRows, "weightKg");
  const previousAverage = averageTrackingField(previousRows, "weightKg");
  const current = Number(String(currentAverage).replace(",", "."));
  const previous = Number(String(previousAverage).replace(",", "."));

  if (!Number.isFinite(current) || !Number.isFinite(previous)) {
    return "-";
  }

  const variation = current - previous;
  return `${variation > 0 ? "+" : ""}${variation.toLocaleString("es-PE", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  })}`;
}

function getPreviousWeeklyCheckIn(checkIns: WeeklyCheckIn[], currentCheckIn: WeeklyCheckIn | null | undefined) {
  if (!currentCheckIn) return null;

  const ordered = checkIns
    .slice()
    .sort((left, right) => getCheckInWeekStart(right).localeCompare(getCheckInWeekStart(left)));
  const index = ordered.findIndex((checkIn) => checkIn.id === currentCheckIn.id);

  return index >= 0 ? ordered[index + 1] ?? null : null;
}

function renderWeeklyTrackingTable(
  rows: Array<any>,
  emptyMessage = "No hay registros guardados para esta semana.",
  weightVariation = calculateWeightVariation(rows),
) {
  const hasRows = rows.length > 0;
  const formatAverage = (field: string) => {
    const value = averageTrackingField(rows, field);
    if (value === "-") return "-";
    return field === "steps" || field === "calories" ? Number(value).toLocaleString("es-PE") : value;
  };

  return (
    <div className="checkin-daily-table-wrap checkin-daily-table-wrap--detail">
      <table className="checkin-daily-edit-table checkin-daily-view-table">
        <thead>
          <tr>
            <th>Día</th>
            <th><span className="checkin-table-heading-stack">Agua<span>(L)</span></span></th>
            <th><span className="checkin-table-heading-stack">Horas de<span>Sueño</span></span></th>
            <th><span className="checkin-table-heading-stack">Pasos<span>Diarios</span></span></th>
            <th><span className="checkin-table-heading-stack">Peso<span>(kg)</span></span></th>
            <th><span className="checkin-table-heading-stack">Proteína<span>(g)</span></span></th>
            <th><span className="checkin-table-heading-stack">Carbo<span>hidratos</span><span>(g)</span></span></th>
            <th><span className="checkin-table-heading-stack">Grasas<span>(g)</span></span></th>
            <th>Calorías</th>
            <th><span className="checkin-table-heading-stack">Tipo de<span>Dieta</span></span></th>
            <th><span className="checkin-table-heading-stack">Músculo<span>Entrenado</span></span></th>
            <th><span className="checkin-table-heading-stack">Cardio<span>(min)</span></span></th>
            <th><span className="checkin-table-heading-stack">Glucosa<span>(mg/dL)</span></span></th>
            <th>Presión</th>
            <th>Notas diarias</th>
          </tr>
        </thead>
        <tbody>
          {hasRows ? (
            rows.map((row) => (
              <tr key={`weekly-tracking-${row.date}`}>
                <td className="checkin-daily-day-cell"><strong>{row.label}</strong><span>{formatShortDayDate(row.date)}</span></td>
                <td>{row.waterLiters || "-"}</td>
                <td>{row.sleepHours || "-"}</td>
                <td>{row.steps || "-"}</td>
                <td>{row.weightKg || "-"}</td>
                <td>{row.proteinGrams || "-"}</td>
                <td>{row.carbsGrams || "-"}</td>
                <td>{row.fatsGrams || "-"}</td>
                <td>{row.calories || "-"}</td>
                <td>{row.dietType || "-"}</td>
                <td>{formatMuscleTrainedLabel(row.trainedMuscle) || "-"}</td>
                <td className="checkin-daily-cardio-cell">{row.cardio || "-"}</td>
                <td>{row.bloodGlucose || "-"}</td>
                <td>{row.bloodPressure || "-"}</td>
                <td className="checkin-daily-notes-cell">{row.dayReflections || "-"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={15} className="progress-empty-cell">{emptyMessage}</td>
            </tr>
          )}
        </tbody>
        {hasRows ? (
          <tfoot>
            <tr>
              <td><strong>Promedio</strong></td>
              <td>{formatAverage("waterLiters")}</td>
              <td>{formatAverage("sleepHours")}</td>
              <td>{formatAverage("steps")}</td>
              <td>{formatAverage("weightKg")}</td>
              <td>{formatAverage("proteinGrams")}</td>
              <td>{formatAverage("carbsGrams")}</td>
              <td>{formatAverage("fatsGrams")}</td>
              <td>{formatAverage("calories")}</td>
              <td colSpan={6}><strong>Variación de peso: {weightVariation === "-" ? "-" : `${weightVariation} kg`}</strong></td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

function formatShortDashboardDate(value: string) {
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return new Intl.DateTimeFormat("es-PE", { day: "numeric", month: "short" }).format(date).replace(".", "");
}

function ClientWeightWeekTick({ x, y, payload }: { x?: number; y?: number; payload?: { value?: string } }) {
  const [week, date] = String(payload?.value ?? "").split("|");

  return (
    <g transform={`translate(${x ?? 0},${y ?? 0})`}>
      <text textAnchor="middle" fill="#475467" fontSize={11} fontWeight={800}>
        <tspan x="0" dy="0">{week}</tspan>
        <tspan x="0" dy="15" fill="#98a2b3" fontWeight={600}>{date}</tspan>
      </text>
    </g>
  );
}

function ClientWeightTooltip({ active, payload }: { active?: boolean; payload?: ReadonlyArray<{ payload: ClientWeightPoint }> }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div className="db2-chart-tip">
      <span className="db2-chart-tip-label">{point.label}</span>
      <strong className="db2-chart-tip-value">
        {point.peso.toFixed(1)}
        <span>kg registrados</span>
      </strong>
      <div className="db2-chart-tip-list">
        <span className="db2-chart-tip-row">
          <i style={{ background: "linear-gradient(135deg, #ffb547, #f08000)" }} />
          Check-in
          <b>{formatShortDashboardDate(point.checkInDate)}</b>
        </span>
      </div>
    </div>
  );
}

function formatDashboardWeekRange(weekStart: string) {
  const start = new Date(`${weekStart}T00:00:00`);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const startLabel = new Intl.DateTimeFormat("es-PE", { day: "numeric" }).format(start);
  const endLabel = new Intl.DateTimeFormat("es-PE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(end);

  return `${startLabel} - ${endLabel}`;
}

function buildSavedCheckInAnswers(checkIn: WeeklyCheckIn) {
  const observations = checkIn.observations || "";
  const answers = [
    ["Sueño", extractObservationValue(observations, "Sueño")],
    ["Hambre", extractObservationValue(observations, "Hambre")],
    ["Ansiedad", extractObservationValue(observations, "Ansiedad")],
    ["Estrés", extractObservationValue(observations, "Estrés")],
    ["Comidas fuera de dieta", extractObservationValue(observations, "Comidas fuera de dieta")],
    ["Entrenamientos completados", extractObservationValue(observations, "Entrenamientos completados")],
    ["Cardios completados", extractObservationValue(observations, "Cardios completados")],
    ["Digestión", extractObservationValue(observations, "Digestión")],
    ["Evolución del entrenamiento", extractObservationValue(observations, "Evolución del entrenamiento")],
    ["Mejoras propuestas", extractObservationValue(observations, "Mejoras propuestas")],
    ["Fase del ciclo menstrual", checkIn.menstrualCyclePhase || ""],
    ["Síntomas menstruales", checkIn.menstrualSymptoms || ""],
    ["¿Qué día vino y se fue el periodo?", checkIn.menstrualPeriodDates || ""],
    ["Observaciones adicionales", extractObservationValue(observations, "Observaciones adicionales")],
  ];

  return answers.filter(([, value]) => value && value !== "-");
}

function getCheckInAnswerTone(label: string) {
  const normalized = normalizeWeekday(label);
  if (normalized.includes("sueno")) return "blue";
  if (normalized.includes("hambre")) return "orange";
  if (normalized.includes("ansiedad")) return "pink";
  if (normalized.includes("estres")) return "purple";
  if (normalized.includes("comida")) return "amber";
  if (normalized.includes("entrenamientos completados")) return "green";
  if (normalized.includes("cardio")) return "red";
  if (normalized.includes("agua")) return "sky";
  if (normalized.includes("digestión")) return "teal";
  if (normalized.includes("evolución") || normalized.includes("como va")) return "orange";
  if (normalized.includes("mejora")) return "green";
  if (normalized.includes("menstrual") || normalized.includes("periodo") || normalized.includes("ciclo")) return "pink";
  if (normalized.includes("observación")) return "blue";
  return "blue";
}

function renderCheckInAnswerIcon(label: string) {
  const normalized = normalizeWeekday(label);

  if (normalized.includes("sueno")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
  }
  if (normalized.includes("hambre")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h2v11"/><path d="M7 2v7"/><path d="M21 15V2l-3 3c-1 1-1.5 2.5-1.5 4S17 12 18 13l3 2z"/></svg>;
  }
  if (normalized.includes("ansiedad")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2z"/><path d="M14.5 2a2.5 2.5 0 0 0-2.5 2.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2z"/></svg>;
  }
  if (normalized.includes("estres")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h9l-1 8 9-12h-9z"/></svg>;
  }
  if (normalized.includes("comida")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11h16a2 2 0 0 0 0-4H4a2 2 0 0 0 0 4z"/><path d="M3 14h18"/><path d="M3 17a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3"/></svg>;
  }
  if (normalized.includes("entrenamientos completados")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6v12M3 9v6M21 9v6M18 6v12M6 12h12"/></svg>;
  }
  if (normalized.includes("cardio")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
  }
  if (normalized.includes("agua")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69 5.66 8.82a8.04 8.04 0 0 0 0 11.36 8.04 8.04 0 0 0 11.36 0 8.04 8.04 0 0 0 0-11.36z"/></svg>;
  }
  if (normalized.includes("digestión")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3c0 2 2 3 3 3 2 0 3 1 3 3 0 1.5-.5 2.5-1 3.5C18 14 18 17 16 19c-2 1.5-5 2-7 1-2-1-3.5-2.5-4-5-.5-2 0-4 1.5-5.5C8 8 8.5 6.5 9 5c.5-1.5 2-3 5-2z"/><circle cx="10" cy="12" r="1"/><circle cx="14" cy="14" r="1"/></svg>;
  }
  if (normalized.includes("evolución") || normalized.includes("como va")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17 9 11l4 4 8-10"/><path d="M14 5h7v7"/></svg>;
  }
  if (normalized.includes("mejora")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2 6.4 20.2 7.5 14 3 9.6l6.2-.9z"/></svg>;
  }
  if (normalized.includes("observación")) {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>;
  }
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h14v13H8l-3 3Z" /><path d="M9 8h6M9 12h4" /></svg>;
}

function getCheckInMeasureTone(label: string) {
  const normalized = normalizeWeekday(label);
  if (normalized.includes("pliegue")) return "amber";
  if (normalized.includes("cintura") && normalized.includes("relajado")) return "coral";
  if (normalized.includes("cintura") && normalized.includes("apretado")) return "orange";
  if (normalized.includes("cintura")) return "orange";
  if (normalized.includes("cadera")) return "cyan";
  if (normalized.includes("brazo")) return "purple";
  if (normalized.includes("muslo")) return "violet";
  if (normalized.includes("cuádricep")) return "blue";
  if (normalized.includes("pecho")) return "blue";
  if (normalized.includes("glúteo") || normalized.includes("gluteo") || normalized.includes("nalga")) return "pink";
  return "orange";
}

function renderCheckInMeasureIcon(_label: string) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>;
}

function buildDetailedCheckInObservations(form: Record<string, any>) {
  const dailyEntries = Array.isArray(form.dailyEntries)
    ? form.dailyEntries.map((entry: any) => ({
        ...entry,
        calories: calculateDailyCalories(entry) || entry.calories || "",
      }))
    : [];
  const sections = [

    "TRACKING_DIARIO_JSON:\n" + JSON.stringify(dailyEntries),
    "PESOS Y PASOS:\n" + dailyEntries.map((entry: any) => `${entry.label} (${entry.date}): peso ${entry.weightKg || "-"} kg, pasos ${entry.steps || "-"}, dieta ${entry.dietType || "-"}`).join("\n"),
    "MEDIDAS:\n" + [
      `Pliegue abdominal: ${form.measurementExtras.abdominalFoldMm || "-"} mm`,
      `Medida de cintura (debajo de las costillas) (relajado): ${form.measurementExtras.waistRelaxedCm || "-"} cm`,
      `Medida de cintura (debajo de las costillas con el abdomen apretado): ${form.measurementExtras.waistContractedCm || "-"} cm`,
      `Medida de cadera (rollo caderal): ${form.measurementExtras.hipsRollCm || "-"} cm`,
      `Medida de brazo flexionado: ${form.measurementExtras.flexedArmCm || "-"} cm`,
      `Medida de muslo (altura cuando termina la nalga): ${form.measurementExtras.upperThighCm || "-"} cm`,
      `Medida del cuádriceps (un poco más arriba de la rodilla): ${form.measurementExtras.quadCm || "-"} cm`,
      ...(form.measurementExtras.glutesCm
        ? [`Medida de glúteo (perímetro más grande de las nalgas): ${form.measurementExtras.glutesCm} cm`]
        : []),
    ].join("\n"),
    "RESUMEN SEMANAL:\n" + [
      `Sueño: ${form.summary.sleepNotes || "-"}`,
      `Hambre: ${form.summary.hungerNotes || "-"}`,
      `Ansiedad: ${form.summary.anxietyNotes || "-"}`,
      `Estrés: ${form.summary.stressNotes || "-"}`,
      `Comidas fuera de dieta: ${form.summary.offDietNotes || "-"}`,
      `Entrenamientos completados: ${form.summary.trainingCompletionNotes || "-"}`,
      `Cardios completados: ${form.summary.cardioCompletionNotes || "-"}`,
      `Agua por día: ${form.waterLiters} litros`,
      `Digestión: ${form.summary.digestionNotes || "-"}`,
      `Evolución del entrenamiento: ${form.summary.trainingPerformanceNotes || "-"}`,
      `Mejoras propuestas: ${form.summary.improvementNotes || "-"}`,
      ...(form.menstrualCyclePhase ? [`Fase del ciclo menstrual: ${form.menstrualCyclePhase}`] : []),
      ...(form.menstrualSymptoms ? [`Síntomas menstruales: ${form.menstrualSymptoms}`] : []),
      ...(form.menstrualPeriodDates ? [`¿Qué día vino y se fue el periodo?: ${form.menstrualPeriodDates}`] : []),
      form.observations ? `Observaciones adicionales: ${form.observations}` : "Observaciones adicionales: -",
    ].join("\n"),
  ];

  return sections.join("\n\n");
}

function limitWords(value: string, maxWords = 20) {
  const words = value.trim().split(/\s+/).filter(Boolean);

  if (words.length <= maxWords) {
    return value;
  }

  return words.slice(0, maxWords).join(" ");
}

const juanPerezDemoProgressPhotos: Array<{ type: "FRONT" | "SIDE" | "BACK"; imageUrl: string }> = [
  {
    type: "FRONT",
    imageUrl: "demo://front",
  },
  {
    type: "SIDE",
    imageUrl: "demo://side",
  },
  {
    type: "BACK",
    imageUrl: "demo://back",
  },
];

type ProgressPhotoSlot = "FRONT" | "SIDE" | "BACK" | "FAVORITE_1" | "FAVORITE_2";
type UploadedProgressPhotoDraft = { type: ProgressPhotoSlot; imageUrl: string };

const PROGRESS_PHOTO_SLOTS: readonly ProgressPhotoSlot[] = ["FRONT", "SIDE", "BACK", "FAVORITE_1", "FAVORITE_2"];
const REQUIRED_PROGRESS_PHOTO_SLOTS: readonly ProgressPhotoSlot[] = ["FRONT", "SIDE", "BACK"];
const MAX_PROGRESS_PHOTO_SIDE = 1800;
const PROGRESS_PHOTO_UPLOAD_QUALITY = 0.82;

function progressPhotoLabel(type: ProgressPhotoSlot | string) {
  switch (type) {
    case "FRONT":
      return "Frente";
    case "SIDE":
      return "Lado";
    case "BACK":
      return "Espalda";
    case "FAVORITE_1":
      return "Favorita 1";
    case "FAVORITE_2":
      return "Favorita 2";
    default:
      return String(type);
  }
}

async function prepareProgressPhotoFile(file: File) {
  if (!file.type.startsWith("image/")) {
    throw new Error("El archivo debe ser una imagen.");
  }

  const imageUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("No se pudo leer la imagen seleccionada."));
      img.src = imageUrl;
    });

    const scale = Math.min(1, MAX_PROGRESS_PHOTO_SIDE / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }

    context.drawImage(image, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", PROGRESS_PHOTO_UPLOAD_QUALITY),
    );

    if (!blob) {
      return file;
    }

    const baseName = file.name.replace(/\.[^.]+$/, "") || "foto-progreso";
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

function buildProgressPhotoFallback(type: ProgressPhotoSlot) {
  const label =
    type === "FRONT"
      ? "FRENTE"
      : type === "SIDE"
        ? "LADO"
        : type === "BACK"
          ? "ESPALDA"
          : type === "FAVORITE_1"
            ? "FAVORITA 1"
            : "FAVORITA 2";
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="900" height="620" viewBox="0 0 900 620">
      <defs>
        <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="#111827"/>
          <stop offset="62%" stop-color="#1f2937"/>
          <stop offset="100%" stop-color="#e63946"/>
        </linearGradient>
        <radialGradient id="glow" cx="50%" cy="24%" r="56%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0.22"/>
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <rect width="900" height="620" rx="34" fill="url(#bg)"/>
      <rect width="900" height="620" rx="34" fill="url(#glow)"/>
      <circle cx="450" cy="220" r="82" fill="#f8fafc" opacity="0.9"/>
      <path d="M274 520c24-112 92-174 176-174s152 62 176 174" fill="#f8fafc" opacity="0.9"/>
      <text x="450" y="92" text-anchor="middle" font-family="Arial, sans-serif" font-size="34" font-weight="700" letter-spacing="8" fill="#ffb4bc">${label}</text>
      <text x="450" y="580" text-anchor="middle" font-family="Arial, sans-serif" font-size="24" font-weight="700" fill="#ffffff" opacity="0.82">Foto de progreso</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function resolveProgressPhotoUrl(type: ProgressPhotoSlot, imageUrl: string) {
  if (!imageUrl || imageUrl.startsWith("demo://")) {
    return buildProgressPhotoFallback(type);
  }

  if (/^(https?:|data:|blob:)/i.test(imageUrl)) {
    return imageUrl;
  }

  const baseUrl = String(apiBaseUrl || "").replace(/\/$/, "");
  const path = imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`;
  return `${baseUrl}${path}`;
}

// Distinct bodybuilder pose per progress-photo view: front double-biceps,
// side chest pose, and rear lat-spread (with a lighter spine groove).
function renderProgressPhotoSilhouette(type: ProgressPhotoSlot) {
  if (type === "FAVORITE_1" || type === "FAVORITE_2") {
    return (
      <svg className="photo-silhouette" viewBox="0 0 100 160" fill="#c4cad3" xmlns="http://www.w3.org/2000/svg">
        {/* Favorite pose: bodybuilder with a star to mark it as a chosen shot */}
        <path d="M44 96 L41 154" fill="none" stroke="#c4cad3" strokeWidth="15" strokeLinecap="round" />
        <path d="M56 96 L59 154" fill="none" stroke="#c4cad3" strokeWidth="15" strokeLinecap="round" />
        <path d="M26 54 L10 38 L28 20" fill="none" stroke="#c4cad3" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M74 54 L90 38 L72 20" fill="none" stroke="#c4cad3" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 52 L78 52 L62 100 L38 100 Z" />
        <circle cx="50" cy="20" r="12" />
        {/* star badge */}
        <path d="M50 60 L54 70 L65 70 L56 77 L59 88 L50 81 L41 88 L44 77 L35 70 L46 70 Z" fill="#eef1f5" />
      </svg>
    );
  }

  if (type === "SIDE") {
    return (
      <svg className="photo-silhouette" viewBox="0 0 100 160" fill="#c4cad3" xmlns="http://www.w3.org/2000/svg">
        {/* legs in a slight stride */}
        <path d="M48 98 L40 154" fill="none" stroke="#c4cad3" strokeWidth="15" strokeLinecap="round" />
        <path d="M56 98 L63 154" fill="none" stroke="#c4cad3" strokeWidth="15" strokeLinecap="round" />
        {/* torso facing right, chest pushed forward */}
        <path d="M38 34 q22 0 24 24 q1 19 -4 42 q-14 3 -24 0 q-5 -34 -3 -58 q1 -6 11 -8 z" />
        {/* head turned to the side */}
        <circle cx="57" cy="20" r="12" />
        {/* single front arm */}
        <path d="M58 48 L63 74 L54 92" fill="none" stroke="#c4cad3" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === "BACK") {
    return (
      <svg className="photo-silhouette" viewBox="0 0 100 160" fill="#c4cad3" xmlns="http://www.w3.org/2000/svg">
        {/* legs */}
        <path d="M44 98 L41 154" fill="none" stroke="#c4cad3" strokeWidth="15" strokeLinecap="round" />
        <path d="M56 98 L59 154" fill="none" stroke="#c4cad3" strokeWidth="15" strokeLinecap="round" />
        {/* arms flared down and out (lat spread) */}
        <path d="M24 54 L8 84 L16 98" fill="none" stroke="#c4cad3" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M76 54 L92 84 L84 98" fill="none" stroke="#c4cad3" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
        {/* V-taper back */}
        <path d="M20 52 L80 52 L62 100 L38 100 Z" />
        {/* fists */}
        <circle cx="16" cy="98" r="6.5" />
        <circle cx="84" cy="98" r="6.5" />
        {/* head */}
        <circle cx="50" cy="20" r="12" />
        {/* spine groove to read as the back */}
        <path d="M50 50 L50 98" fill="none" stroke="#eef1f5" strokeWidth="3" strokeLinecap="round" />
      </svg>
    );
  }

  // FRONT — front double biceps
  return (
    <svg className="photo-silhouette" viewBox="0 0 100 160" fill="#c4cad3" xmlns="http://www.w3.org/2000/svg">
      {/* legs */}
      <path d="M44 98 L41 154" fill="none" stroke="#c4cad3" strokeWidth="15" strokeLinecap="round" />
      <path d="M56 98 L59 154" fill="none" stroke="#c4cad3" strokeWidth="15" strokeLinecap="round" />
      {/* arms raised and flexed */}
      <path d="M26 54 L10 38 L28 20" fill="none" stroke="#c4cad3" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M74 54 L90 38 L72 20" fill="none" stroke="#c4cad3" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
      {/* V-taper torso */}
      <path d="M22 52 L78 52 L62 100 L38 100 Z" />
      {/* pecs */}
      <ellipse cx="40" cy="61" rx="13" ry="9" />
      <ellipse cx="60" cy="61" rx="13" ry="9" />
      {/* bicep peaks at the elbows */}
      <circle cx="11" cy="38" r="8" />
      <circle cx="89" cy="38" r="8" />
      {/* fists near the head */}
      <circle cx="28" cy="20" r="6.5" />
      <circle cx="72" cy="20" r="6.5" />
      {/* head */}
      <circle cx="50" cy="20" r="12" />
    </svg>
  );
}

export function ClientPortalPage() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const isCoach = user?.role === "COACH" || user?.role === "ADMIN";
  const isCoachPreview = Boolean(clientId) && Boolean(isCoach);
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<ClientHistoryResponse | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [savingCheckIn, setSavingCheckIn] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [portalMenuOpen, setPortalMenuOpen] = useState(false);
  const [photoErrors, setPhotoErrors] = useState<string[]>([]);
  const [portalToast, setPortalToast] = useState<PortalToast | null>(null);
  const [assessmentError, setAssessmentError] = useState("");
  const [checkInError, setCheckInError] = useState("");
  const [assessmentForm, setAssessmentForm] = useState<AssessmentForm>(emptyAssessment);
  const [activeAssessmentSectionId, setActiveAssessmentSectionId] = useState<string | null>(null);
  const activeAssessmentCardRef = useRef<HTMLElement | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const shouldScrollAssessmentFormRef = useRef(false);
  const [selectedProgressCheckInId, setSelectedProgressCheckInId] = useState("");
  const [selectedCheckInDetailId, setSelectedCheckInDetailId] = useState("");
  const [comparisonBaseCheckInId, setComparisonBaseCheckInId] = useState("");
  const [comparisonTargetCheckInId, setComparisonTargetCheckInId] = useState("");
  const [checkInViewMode, setCheckInViewMode] = useState<"new" | "detail">("new");
  const [checkInForm, setCheckInForm] = useState({
    weekStart: getStartOfWeekIso(new Date().toISOString().slice(0, 10)),
    dietCompliance: 80,
    trainingCompliance: 80,
    cardioCompleted: "",
    waterLiters: 2,
    sleepScore: 7,
    hungerScore: 5,
    anxietyScore: 4,
    stressScore: 4,
    observations: "",
    menstrualCyclePhase: "",
    menstrualSymptoms: "",
    menstrualPeriodDates: "",
    dailyEntries: generateWeeklyEntries(getStartOfWeekIso(new Date().toISOString().slice(0, 10))),
    dailyWeights: [{ date: getStartOfWeekIso(new Date().toISOString().slice(0, 10)), weightKg: 0, fasting: true }],
    measurements: {
      waistCm: 0,
      hipsCm: 0,
      chestCm: 0,
      rightArmCm: 0,
      leftArmCm: 0,
      rightThighCm: 0,
      leftThighCm: 0,
    },
    measurementExtras: {
      abdominalFoldMm: "",
      waistRelaxedCm: "",
      waistContractedCm: "",
      hipsRollCm: "",
      flexedArmCm: "",
      upperThighCm: "",
      quadCm: "",
      glutesCm: "",
    },
    summary: {
      sleepNotes: "",
      hungerNotes: "",
      anxietyNotes: "",
      stressNotes: "",
      offDietNotes: "",
      trainingCompletionNotes: "",
      cardioCompletionNotes: "",
      digestionNotes: "",
      trainingPerformanceNotes: "",
      improvementNotes: "",
    },
    progressPhotos: [] as UploadedProgressPhotoDraft[],
  });

  const [progressPhotoFiles, setProgressPhotoFiles] = useState<Record<ProgressPhotoSlot, File | null>>(
    {
      FRONT: null,
      SIDE: null,
      BACK: null,
      FAVORITE_1: null,
      FAVORITE_2: null,
    },
  );

  const [progressPhotoPreviews, setProgressPhotoPreviews] = useState<Record<ProgressPhotoSlot, string>>(
    {
      FRONT: "",
      SIDE: "",
      BACK: "",
      FAVORITE_1: "",
      FAVORITE_2: "",
    },
  );
  const [uploadedProgressPhotos, setUploadedProgressPhotos] = useState<Record<ProgressPhotoSlot, UploadedProgressPhotoDraft | null>>(
    {
      FRONT: null,
      SIDE: null,
      BACK: null,
      FAVORITE_1: null,
      FAVORITE_2: null,
    },
  );
  const [photoViewer, setPhotoViewer] = useState<{ photos: PhotoViewerItem[]; index: number; zoom: number; panX: number; panY: number } | null>(null);
  const photoDragRef = useRef<{ pointerId: number; startX: number; startY: number; panX: number; panY: number } | null>(null);
  const [juanPerezCheckInFilled, setJuanPerezCheckInFilled] = useState(false);
  const [yoelSosanCheckInFilled, setYoelSosanCheckInFilled] = useState(false);
  const [alexGilCheckInFilled, setAlexGilCheckInFilled] = useState(false);
  const [, setYoelSosanAssessmentFilled] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [historyDetailMode, setHistoryDetailMode] = useState<"progress" | "checkin">("progress");

  const [dailyTracking, setDailyTracking] = useState(() => ({
    weekStart: getStartOfWeekIso(new Date().toISOString().slice(0, 10)),
    rows: [] as Array<any>,
  }));

  async function setProgressPhoto(type: ProgressPhotoSlot, file: File | null) {
    setPhotoErrors([]);

    if (!file) {
      setProgressPhotoFiles((current) => ({ ...current, [type]: null }));
      setUploadedProgressPhotos((current) => ({ ...current, [type]: null }));
      setCheckInForm((current) => ({
        ...current,
        progressPhotos: (current.progressPhotos as UploadedProgressPhotoDraft[]).filter((photo) => photo.type !== type),
      }));
      setProgressPhotoPreviews((current) => ({
        ...current,
        [type]: "",
      }));
      return;
    }

    setUploadingPhotos(true);
    try {
      const uploadFile = await prepareProgressPhotoFile(file);
      const localPreview = URL.createObjectURL(uploadFile);

      setProgressPhotoFiles((current) => ({ ...current, [type]: uploadFile }));
      setUploadedProgressPhotos((current) => ({ ...current, [type]: null }));
      setProgressPhotoPreviews((current) => ({
        ...current,
        [type]: localPreview,
      }));
      setCheckInForm((current) => ({
        ...current,
        progressPhotos: (current.progressPhotos as UploadedProgressPhotoDraft[]).filter((photo) => photo.type !== type),
      }));
    } catch (err: any) {
      const serverMessage = err?.response?.data?.message || err?.message;
      const message = Array.isArray(serverMessage)
        ? serverMessage.join(" ")
        : serverMessage || "No se pudo preparar la foto. Intenta nuevamente.";
      setPhotoErrors([message]);
      showToast("error", message);
      setProgressPhotoFiles((current) => ({ ...current, [type]: null }));
      setUploadedProgressPhotos((current) => ({ ...current, [type]: null }));
      setProgressPhotoPreviews((current) => ({
        ...current,
        [type]: "",
      }));
    } finally {
      setUploadingPhotos(false);
    }
  }

  function openPhotoViewer(photos: PhotoViewerItem[], index = 0) {
    if (photos.length === 0) {
      return;
    }

    setPhotoViewer({ photos, index: Math.min(Math.max(index, 0), photos.length - 1), zoom: 1, panX: 0, panY: 0 });
  }

  function movePhotoViewer(direction: number) {
    setPhotoViewer((current) => {
      if (!current) {
        return current;
      }

      const nextIndex = (current.index + direction + current.photos.length) % current.photos.length;
      return { ...current, index: nextIndex, zoom: 1, panX: 0, panY: 0 };
    });
  }

  function zoomPhotoViewer(delta: number) {
    setPhotoViewer((current) => {
      if (!current) {
        return current;
      }

      const nextZoom = Math.min(6, Math.max(1, Number((current.zoom + delta).toFixed(2))));
      return {
        ...current,
        zoom: nextZoom,
        panX: nextZoom <= 1 ? 0 : current.panX,
        panY: nextZoom <= 1 ? 0 : current.panY,
      };
    });
  }

  function resetPhotoViewerZoom() {
    setPhotoViewer((current) => (current ? { ...current, zoom: 1, panX: 0, panY: 0 } : current));
  }

  function startPhotoPan(event: React.PointerEvent<HTMLDivElement>) {
    if (!photoViewer || photoViewer.zoom <= 1) return;
    if ((event.target as HTMLElement).closest("button")) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    photoDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panX: photoViewer.panX,
      panY: photoViewer.panY,
    };
  }

  function movePhotoPan(event: React.PointerEvent<HTMLDivElement>) {
    const drag = photoDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    setPhotoViewer((current) =>
      current
        ? {
            ...current,
            panX: drag.panX + event.clientX - drag.startX,
            panY: drag.panY + event.clientY - drag.startY,
          }
        : current,
    );
  }

  function endPhotoPan(event: React.PointerEvent<HTMLDivElement>) {
    if (photoDragRef.current?.pointerId === event.pointerId) {
      photoDragRef.current = null;
    }
  }

  async function loadHistory() {
    setLoadingHistory(true);
    try {
      const response = clientId ? await getClientHistory(clientId) : await getMyClientHistory();
      setData(response);
    } finally {
      setLoadingHistory(false);
    }
  }

  useEffect(() => {
    loadHistory();
  }, [clientId]);

  useEffect(() => {
    const checkIfClientChanged = async () => {
      const latestData = clientId ? await getClientHistory(clientId) : await getMyClientHistory();
      setData(latestData);
    };
    const interval = setInterval(checkIfClientChanged, 5000);
    return () => clearInterval(interval);
  }, [clientId]);

  useEffect(() => {
    if (!photoViewer) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPhotoViewer(null);
      }
      if (event.key === "ArrowLeft") {
        movePhotoViewer(-1);
      }
      if (event.key === "ArrowRight") {
        movePhotoViewer(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [photoViewer]);

  const requestedTab = searchParams.get("tab");
  const activeTab: ClientPortalTab =
    requestedTab === "assessment" ||
    requestedTab === "checkin" ||
    requestedTab === "tracking" ||
    requestedTab === "history" ||
    requestedTab === "comparison" ||
    requestedTab === "dashboard"
      ? requestedTab
      : "dashboard";
  const hasCompletedInitialRoutine = Boolean(data?.initialAssessment);

  useEffect(() => {
    if (requestedTab === "charts") {
      setSearchParams({ tab: "dashboard" }, { replace: true });
    }
  }, [requestedTab, setSearchParams]);

  useEffect(() => {
    if (loadingHistory || activeTab !== "checkin" || hasCompletedInitialRoutine) {
      return;
    }

    setCheckInError("");
    setAssessmentError("Primero debes completar tu rutina inicial antes de registrar un check-in.");
    setSearchParams({ tab: "assessment" }, { replace: true });
  }, [activeTab, hasCompletedInitialRoutine, loadingHistory, setSearchParams]);

  // Firma del assessment GUARDADO (no del objeto data completo). El polling cada 5s
  // refresca `data` con un objeto nuevo aunque el contenido sea idéntico; si dependiéramos
  // de `data`, este efecto reiniciaría el formulario cada 5s y borraría lo que el usuario
  // está escribiendo. Solo debe reinicializar cuando el contenido guardado realmente cambia.
  const savedAssessmentSignature = JSON.stringify({
    assessment: data?.initialAssessment ?? null,
    name: `${data?.client?.firstName ?? ""} ${data?.client?.lastName ?? ""}`,
  });

  useEffect(() => {
    const savedAssessmentForm = buildAssessmentForm(data?.initialAssessment ?? null);
    const hasSavedAssessmentBasics =
      savedAssessmentForm.height.trim() &&
      savedAssessmentForm.currentWeight.trim() &&
      savedAssessmentForm.age.trim() &&
      savedAssessmentForm.gymExperienceTime.trim() &&
      savedAssessmentForm.dietExperienceTime.trim();
    const isYoelSosan = normalizeWeekday(`${data?.client.firstName ?? ""} ${data?.client.lastName ?? ""}`) === "yoel sosan";
    const isPedroSanchez = normalizeWeekday(`${data?.client.firstName ?? ""} ${data?.client.lastName ?? ""}`) === "pedro sanchez";

    if (isYoelSosan && !hasSavedAssessmentBasics) {
      setAssessmentForm(yoelSosanAssessmentPreset);
      setYoelSosanAssessmentFilled(true);
      return;
    }

    // Si aún no hay registro inicial guardado, restaurar el borrador local si existe.
    if (!data?.initialAssessment) {
      const draft = readAssessmentDraft(data?.client?.id);
      if (draft) {
        setAssessmentForm(draft);
        return;
      }

      if (isPedroSanchez) {
        setAssessmentForm(pedroSanchezAssessmentPreset);
        return;
      }
    }

    setAssessmentForm(savedAssessmentForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAssessmentSignature]);

  const latestCheckIn = data?.checkIns[0];
  const latestWeight = latestCheckIn?.dailyWeights[0]?.weightKg ?? "—";
  const historyCheckIns = useMemo<WeeklyCheckIn[]>(() => {
    const list: WeeklyCheckIn[] = data?.checkIns ?? [];
    return list.slice().sort((left, right) => right.weekStart.localeCompare(left.weekStart));
  }, [data?.checkIns]);
  const savedWeekCheckIns = useMemo(() => getSavedWeekCheckIns(historyCheckIns), [historyCheckIns]);
  const savedWeekOptions = useMemo(
    () =>
      savedWeekCheckIns.map((checkIn) => ({
        id: checkIn.id,
        label: `${getCheckInWeekMeta(data?.checkIns ?? [], checkIn.id).label} (${getCheckInWeekStart(checkIn)})`,
      })),
    [data?.checkIns, savedWeekCheckIns],
  );
  useEffect(() => {
    if (savedWeekOptions.length === 0) {
      setSelectedProgressCheckInId("");
      setSelectedCheckInDetailId("");
      setComparisonBaseCheckInId("");
      setComparisonTargetCheckInId("");
      return;
    }

    setSelectedProgressCheckInId((current) =>
      current && savedWeekOptions.some((option) => option.id === current) ? current : savedWeekOptions[0].id,
    );
    setSelectedCheckInDetailId((current) =>
      current && savedWeekOptions.some((option) => option.id === current) ? current : savedWeekOptions[0].id,
    );
    setComparisonBaseCheckInId((current) =>
      current && savedWeekOptions.some((option) => option.id === current)
        ? current
        : (savedWeekOptions[1]?.id ?? savedWeekOptions[0].id),
    );
    setComparisonTargetCheckInId((current) =>
      current && savedWeekOptions.some((option) => option.id === current) ? current : savedWeekOptions[0].id,
    );
  }, [savedWeekOptions]);
  const approvedRecs = useMemo(
    () =>
      [...(data?.recommendations.filter((item) => item.status === "APPROVED") ?? [])].sort(
        (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
      ),
    [data?.recommendations],
  );
  const isFemale = data?.client.sex === "FEMALE";
  const selectedTrainingDays = useMemo(() => extractTrainingDays(data?.initialAssessment?.trainingRoutine || assessmentForm.trainingDays), [data?.initialAssessment?.trainingRoutine, assessmentForm.trainingDays]);
  const isJuanPerezClient = normalizeWeekday(`${data?.client.firstName ?? ""} ${data?.client.lastName ?? ""}`) === "juan perez";
  const isYoelSosanClient = normalizeWeekday(`${data?.client.firstName ?? ""} ${data?.client.lastName ?? ""}`) === "yoel sosan";
  const isAlexGilClient = normalizeWeekday(`${data?.client.firstName ?? ""} ${data?.client.lastName ?? ""}`) === "alex gil";

  useEffect(() => {
    if (!data?.client?.id) {
      return;
    }

    const draft = readCheckInDraft(data?.client?.id);
    if (!draft) {
      return;
    }

    setCheckInForm((current) => ({
      ...current,
      ...draft,
      measurements: {
        ...current.measurements,
        ...(draft.measurements ?? {}),
      },
      measurementExtras: {
        ...current.measurementExtras,
        ...(draft.measurementExtras ?? {}),
      },
      summary: {
        ...current.summary,
        ...(draft.summary ?? {}),
      },
      dailyEntries: Array.isArray(draft.dailyEntries) ? draft.dailyEntries : current.dailyEntries,
      dailyWeights: Array.isArray(draft.dailyWeights) ? draft.dailyWeights : current.dailyWeights,
      progressPhotos: [],
    }));

    const draftPhotos = Array.isArray(draft.progressPhotos)
      ? (draft.progressPhotos as UploadedProgressPhotoDraft[]).filter((photo) =>
          PROGRESS_PHOTO_SLOTS.includes(photo.type) && photo.imageUrl,
        )
      : [];
    const nextUploadedPhotos = PROGRESS_PHOTO_SLOTS.reduce((acc, type) => {
      acc[type] = draftPhotos.find((photo) => photo.type === type) ?? null;
      return acc;
    }, {} as Record<ProgressPhotoSlot, UploadedProgressPhotoDraft | null>);
    const nextPreviews = PROGRESS_PHOTO_SLOTS.reduce((acc, type) => {
      const photo = nextUploadedPhotos[type];
      acc[type] = photo ? resolveProgressPhotoUrl(type, photo.imageUrl) : "";
      return acc;
    }, {} as Record<ProgressPhotoSlot, string>);

    setUploadedProgressPhotos(nextUploadedPhotos);
    setProgressPhotoFiles({ FRONT: null, SIDE: null, BACK: null, FAVORITE_1: null, FAVORITE_2: null });
    setProgressPhotoPreviews(nextPreviews);
  }, [data?.client?.id]);

  useEffect(() => {
    setCheckInForm((current) => ({
      ...current,
      dailyEntries: generateWeeklyEntries(current.weekStart, current.dailyEntries, selectedTrainingDays),
    }));
  }, [selectedTrainingDays]);

  useEffect(() => {
    if (!isJuanPerezClient || juanPerezCheckInFilled || readCheckInDraft(data?.client?.id)) {
      return;
    }

    setCheckInForm((current) => {
      const dailyEntries = buildJuanPerezWeeklyEntries(current.weekStart, selectedTrainingDays);

      return {
        ...current,
        dietCompliance: 90,
        trainingCompliance: 95,
        cardioCompleted: "4 cardios completados, falto uno por tiempo.",
        waterLiters: 2.8,
        sleepScore: 7,
        hungerScore: 4,
        anxietyScore: 3,
        stressScore: 4,
        observations: "Semana estable, buen cumplimiento y energía mejorando.",
        dailyEntries,
        dailyWeights: dailyEntries
          .filter((entry) => entry.weightKg)
          .map((entry) => ({
            date: entry.date,
            weightKg: Number(entry.weightKg),
            fasting: true,
          })),
        measurements: {
          ...current.measurements,
          waistCm: 83,
          hipsCm: 96,
          rightArmCm: 37,
          rightThighCm: 58,
          leftThighCm: 48,
        },
        measurementExtras: {
          abdominalFoldMm: "12",
          waistRelaxedCm: "83",
          waistContractedCm: "79",
          hipsRollCm: "96",
          flexedArmCm: "37",
          upperThighCm: "58",
          quadCm: "48",
          glutesCm: "",
        },
        summary: {
          sleepNotes: "Dormí mejor, promedio siete horas y desperte con más energía.",
          hungerNotes: "Hambre controlada, aparece más fuerte antes de la cena.",
          anxietyNotes: "Ansiedad baja, solo apareció una tarde por trabajo.",
          stressNotes: "Estrés moderado por trabajo, manejable con descanso.",
          offDietNotes: "Comí fuera el sábado, una hamburguesa sin postre.",
          trainingCompletionNotes: "Completé cinco entrenamientos y mantuve buena intensidad.",
          cardioCompletionNotes: "Completé cuatro cardios, falto uno por tiempo.",
          digestionNotes: "Digestión normal, sin hinchazón importante durante la semana.",
          trainingPerformanceNotes: "El entrenamiento mejoró, subí cargas en ejercicios principales.",
          improvementNotes: "Podemos mejorar horarios de comida y constancia del cardio.",
        },
      };
    });
    setJuanPerezCheckInFilled(true);
  }, [data?.client?.id, isJuanPerezClient, juanPerezCheckInFilled, selectedTrainingDays]);

  useEffect(() => {
    if (!isYoelSosanClient || yoelSosanCheckInFilled || readCheckInDraft(data?.client?.id)) {
      return;
    }

    setCheckInForm((current) => {
      const dailyEntries = buildYoelSosanWeeklyEntries(current.weekStart, selectedTrainingDays);

      return {
        ...current,
        dietCompliance: 88,
        trainingCompliance: 92,
        cardioCompleted: "Complete cuatro sesiones de cardio y una caminata ligera el domingo.",
        waterLiters: 2.8,
        sleepScore: 8,
        hungerScore: 4,
        anxietyScore: 3,
        stressScore: 4,
        observations: "Semana ordenada, buena energía y mayor control del apetito.",
        dailyEntries,
        dailyWeights: dailyEntries
          .filter((entry) => entry.weightKg)
          .map((entry) => ({
            date: entry.date,
            weightKg: Number(entry.weightKg),
            fasting: true,
          })),
        measurements: {
          ...current.measurements,
          waistCm: 84,
          hipsCm: 97,
          rightArmCm: 36,
          rightThighCm: 59,
          leftThighCm: 50,
        },
        measurementExtras: {
          abdominalFoldMm: "13",
          waistRelaxedCm: "84",
          waistContractedCm: "80",
          hipsRollCm: "97",
          flexedArmCm: "36",
          upperThighCm: "59",
          quadCm: "50",
          glutesCm: "",
        },
        summary: {
          sleepNotes: "Dormí entre siete y ocho horas, desperte con buena energía.",
          hungerNotes: "Hambre controlada, solo aumento un poco antes de la cena.",
          anxietyNotes: "Ansiedad baja durante la semana, sin episodios fuertes.",
          stressNotes: "Estrés moderado por trabajo, pero pude mantener la rutina.",
          offDietNotes: "Tuve una comida fuera del plan el sábado, sin postre.",
          trainingCompletionNotes: "Complete los entrenamientos planificados con buena intensidad.",
          cardioCompletionNotes: "Complete cuatro cardios y una caminata ligera.",
          digestionNotes: "Digestión normal, sin inflamación importante.",
          trainingPerformanceNotes: "Buen rendimiento general, subi cargas en ejercicios principales.",
          improvementNotes: "Mejorar el horario de cena y mantener la hidratación diaria.",
        },
      };
    });

    setProgressPhotoFiles({ FRONT: null, SIDE: null, BACK: null, FAVORITE_1: null, FAVORITE_2: null });
    setUploadedProgressPhotos({ FRONT: null, SIDE: null, BACK: null, FAVORITE_1: null, FAVORITE_2: null });
    setProgressPhotoPreviews({ FRONT: "", SIDE: "", BACK: "", FAVORITE_1: "", FAVORITE_2: "" });
    setYoelSosanCheckInFilled(true);
  }, [data?.client?.id, isYoelSosanClient, selectedTrainingDays, yoelSosanCheckInFilled]);

  useEffect(() => {
    if (!isAlexGilClient || alexGilCheckInFilled || readCheckInDraft(data?.client?.id)) {
      return;
    }

    setCheckInForm((current) => {
      const dailyEntries = buildAlexGilWeeklyEntries(current.weekStart, selectedTrainingDays);

      return {
        ...current,
        dietCompliance: 86,
        trainingCompliance: 90,
        cardioCompleted: "Complete cuatro sesiones de cardio y una caminata ligera.",
        waterLiters: 2.4,
        sleepScore: 7,
        hungerScore: 5,
        anxietyScore: 3,
        stressScore: 4,
        observations: "Semana estable, buena adherencia y energia constante.",
        dailyEntries,
        dailyWeights: dailyEntries
          .filter((entry) => entry.weightKg)
          .map((entry) => ({
            date: entry.date,
            weightKg: Number(entry.weightKg),
            fasting: true,
          })),
        measurements: {
          ...current.measurements,
          waistCm: 72,
          hipsCm: 88,
          rightArmCm: 31,
          rightThighCm: 49,
          leftThighCm: 43,
        },
        measurementExtras: {
          abdominalFoldMm: "10",
          waistRelaxedCm: "72",
          waistContractedCm: "69",
          hipsRollCm: "88",
          flexedArmCm: "31",
          upperThighCm: "49",
          quadCm: "43",
          glutesCm: "",
        },
        summary: {
          sleepNotes: "Dormi mejor que la semana anterior, promedio siete horas.",
          hungerNotes: "Hambre moderada, principalmente antes de la cena.",
          anxietyNotes: "Ansiedad baja durante la semana.",
          stressNotes: "Estres moderado por trabajo, pero controlado.",
          offDietNotes: "Una comida fuera del plan el sabado.",
          trainingCompletionNotes: "Complete cinco entrenamientos con buena intensidad.",
          cardioCompletionNotes: "Complete cuatro cardios y una caminata ligera.",
          digestionNotes: "Digestion normal, sin molestias importantes.",
          trainingPerformanceNotes: "Mejor rendimiento general, subi cargas en algunos ejercicios.",
          improvementNotes: "Mejorar horarios de comida y mantener agua constante.",
        },
      };
    });

    setProgressPhotoFiles({ FRONT: null, SIDE: null, BACK: null, FAVORITE_1: null, FAVORITE_2: null });
    setUploadedProgressPhotos({ FRONT: null, SIDE: null, BACK: null, FAVORITE_1: null, FAVORITE_2: null });
    setProgressPhotoPreviews({ FRONT: "", SIDE: "", BACK: "", FAVORITE_1: "", FAVORITE_2: "" });
    setAlexGilCheckInFilled(true);
  }, [alexGilCheckInFilled, data?.client?.id, isAlexGilClient, selectedTrainingDays]);

  useEffect(() => {
    setDailyTracking((current) => ({
      ...current,
      rows: generateTrackingRows(current.weekStart, selectedTrainingDays, current.rows),
    }));
  }, [selectedTrainingDays]);

  useEffect(() => {
    if (!data?.client.id) {
      return;
    }

    const storageKey = `daily-tracking-${data.client.id}`;
    const rawValue = window.localStorage.getItem(storageKey);
    const latestSavedCheckIn = [...data.checkIns].sort(
      (left, right) =>
        new Date(right.submittedAt).getTime() - new Date(left.submittedAt).getTime(),
    )[0];

    try {
      const parsed = rawValue ? JSON.parse(rawValue) : null;
      const nextWeekStart =
        latestSavedCheckIn?.weekStart.slice(0, 10) ||
        parsed?.weekStart ||
        getStartOfWeekIso(new Date().toISOString().slice(0, 10));

      setDailyTracking({
        weekStart: nextWeekStart,
        rows: latestSavedCheckIn
          ? buildTrackingRowsFromCheckIn(latestSavedCheckIn, selectedTrainingDays, parsed?.rows || [])
          : generateTrackingRows(nextWeekStart, selectedTrainingDays, parsed?.rows || []),
      });
    } catch {
      setDailyTracking((current) => ({
        weekStart: latestSavedCheckIn?.weekStart.slice(0, 10) || current.weekStart,
        rows: latestSavedCheckIn
          ? buildTrackingRowsFromCheckIn(latestSavedCheckIn, selectedTrainingDays, current.rows)
          : generateTrackingRows(current.weekStart, selectedTrainingDays, current.rows),
      }));
    }
  }, [data, selectedTrainingDays]);

  useEffect(() => {
    if (!data?.client.id) {
      return;
    }

    const storageKey = `daily-tracking-${data.client.id}`;
    window.localStorage.setItem(storageKey, JSON.stringify(dailyTracking));
  }, [dailyTracking, data?.client.id]);

  const trackingWaterAverage = useMemo(() => averageTrackingField(dailyTracking.rows, "waterLiters"), [dailyTracking.rows]);
  const trackingSleepAverage = useMemo(() => averageTrackingField(dailyTracking.rows, "sleepHours"), [dailyTracking.rows]);
  const currentWeekStart = getStartOfWeekIso(new Date().toISOString().slice(0, 10));
  const isMondayToday = new Date().getDay() === 1;
  const nextMondayLabel = (() => {
    const today = new Date();
    const day = today.getDay();
    const daysUntilMonday = day === 0 ? 1 : (8 - day) % 7 || 7;
    const next = new Date(today);
    next.setDate(today.getDate() + daysUntilMonday);
    return next.toLocaleDateString("es-PE", { weekday: "long", day: "numeric", month: "long" });
  })();
  const sortedCheckIns = useMemo(
    () =>
      [...(data?.checkIns ?? [])].sort(
        (left, right) =>
          new Date(right.submittedAt).getTime() - new Date(left.submittedAt).getTime(),
      ),
    [data?.checkIns],
  );
  const currentWeekCheckIn = sortedCheckIns.find((checkIn) => getCheckInWeekStart(checkIn) === currentWeekStart) ?? null;
  const latestSubmittedCheckIn = sortedCheckIns[0] ?? null;
  const canSubmitCurrentWeekCheckIn = !currentWeekCheckIn;
  const savedCurrentWeekCheckIn =
    currentWeekCheckIn ??
    latestSubmittedCheckIn;
  const savedCurrentWeekRows = useMemo(
    () =>
      savedCurrentWeekCheckIn
        ? buildTrackingRowsFromCheckIn(savedCurrentWeekCheckIn, selectedTrainingDays)
        : [],
    [savedCurrentWeekCheckIn, selectedTrainingDays],
  );
  const selectedProgressCheckIn =
    savedWeekCheckIns.find((checkIn) => checkIn.id === selectedProgressCheckInId) ??
    savedCurrentWeekCheckIn;
  const selectedProgressRows = useMemo(
    () => (selectedProgressCheckIn ? buildTrackingRowsFromCheckIn(selectedProgressCheckIn, selectedTrainingDays) : []),
    [selectedProgressCheckIn, selectedTrainingDays],
  );
  const previousProgressCheckIn = getPreviousWeeklyCheckIn(historyCheckIns, selectedProgressCheckIn);
  const previousProgressRows = useMemo(
    () => (previousProgressCheckIn ? buildTrackingRowsFromCheckIn(previousProgressCheckIn, selectedTrainingDays) : []),
    [previousProgressCheckIn, selectedTrainingDays],
  );
  const selectedProgressWeightVariation = calculateWeeklyAverageWeightVariation(selectedProgressRows, previousProgressRows);
  const selectedCheckInDetail =
    savedWeekCheckIns.find((checkIn) => checkIn.id === selectedCheckInDetailId) ??
    savedCurrentWeekCheckIn;
  const shouldShowCheckInDetail = Boolean(selectedCheckInDetail && checkInViewMode === "detail");
  const selectedCheckInRows = useMemo(
    () => (selectedCheckInDetail ? buildTrackingRowsFromCheckIn(selectedCheckInDetail, selectedTrainingDays) : []),
    [selectedCheckInDetail, selectedTrainingDays],
  );
  const previousSelectedCheckInDetail = getPreviousWeeklyCheckIn(historyCheckIns, selectedCheckInDetail);
  const previousSelectedCheckInRows = useMemo(
    () => (previousSelectedCheckInDetail ? buildTrackingRowsFromCheckIn(previousSelectedCheckInDetail, selectedTrainingDays) : []),
    [previousSelectedCheckInDetail, selectedTrainingDays],
  );
  const selectedCheckInWeightVariation = calculateWeeklyAverageWeightVariation(selectedCheckInRows, previousSelectedCheckInRows);
  const selectedCheckInAnswers = useMemo(
    () => (selectedCheckInDetail ? buildSavedCheckInAnswers(selectedCheckInDetail) : []),
    [selectedCheckInDetail],
  );
  const nextWeekStart = (() => {
    const referenceWeekStart = savedCurrentWeekCheckIn?.weekStart.slice(0, 10) ?? currentWeekStart;
    const date = new Date(`${referenceWeekStart}T00:00:00`);
    date.setDate(date.getDate() + 7);
    return date.toISOString().slice(0, 10);
  })();
  const activeAssessmentSection =
    assessmentRoutineSections.find((section) => section.id === activeAssessmentSectionId) ?? null;
  useEffect(() => {
    if (
      activeTab !== "assessment" ||
      !activeAssessmentSectionId ||
      !shouldScrollAssessmentFormRef.current ||
      !activeAssessmentCardRef.current
    ) {
      return;
    }

    shouldScrollAssessmentFormRef.current = false;
    window.requestAnimationFrame(() => {
      activeAssessmentCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [activeAssessmentSectionId, activeTab]);
  const assessmentAnsweredCount = assessmentQuestions.filter((question) => assessmentForm[question.key].trim()).length;
  const assessmentTotalCount = assessmentQuestions.length;
  const assessmentCompletedSections = assessmentRoutineSections.filter((section) =>
    section.keys.every((key) => assessmentForm[key].trim()),
  ).length;
  const assessmentProgressPercent = Math.round((assessmentAnsweredCount / assessmentTotalCount) * 100);
  const assessmentSummaryItems = useMemo(
    () => buildAssessmentSummaryItems(data?.initialAssessment ?? null),
    [data?.initialAssessment],
  );
  const dashboardFullName = `${data?.client.firstName ?? ""} ${data?.client.lastName ?? ""}`.trim() || "Juan Perez";
  const dashboardWeekStart = savedCurrentWeekCheckIn?.weekStart.slice(0, 10) ?? currentWeekStart;
  const dashboardWeekRange = formatDashboardWeekRange(dashboardWeekStart);
  const dashboardRows = savedCurrentWeekRows.length > 0 ? savedCurrentWeekRows : dailyTracking.rows;
  const dashboardWeightData = useMemo(() => {
    const checkInWeights = historyCheckIns
      .slice()
      .sort((left, right) => left.weekStart.localeCompare(right.weekStart))
      .map((checkIn) => {
        const latestWeight = checkIn.dailyWeights
          .slice()
          .sort((left, right) => left.date.localeCompare(right.date))
          .at(-1);

        if (!latestWeight) return null;

        return {
          date: latestWeight.date.slice(0, 10),
          peso: latestWeight.weightKg,
          checkInDate: checkIn.submittedAt,
        };
      })
      .filter((row): row is { date: string; peso: number; checkInDate: string } => row !== null)
      .filter((row) => Number.isFinite(row.peso) && row.peso > 0)
      .slice(-8)
      .map((row, index): ClientWeightPoint => ({
        ...row,
        label: `Semana ${index + 1}`,
        dateLabel: formatShortDashboardDate(row.date),
        axisLabel: `Semana ${index + 1}|${formatShortDashboardDate(row.date)}`,
      }));

    if (checkInWeights.length > 0) {
      return checkInWeights;
    }

    const latestCurrentWeekWeight = dashboardRows
      .filter((row) => String(row.weightKg ?? "").trim())
      .sort((left, right) => left.date.localeCompare(right.date))
      .at(-1);

    return latestCurrentWeekWeight
      ? [
            {
              date: latestCurrentWeekWeight.date,
              peso: Number(String(latestCurrentWeekWeight.weightKg).replace(",", ".")),
              checkInDate: latestCurrentWeekWeight.date,
              label: "Semana 1",
              dateLabel: formatShortDashboardDate(latestCurrentWeekWeight.date),
              axisLabel: `Semana 1|${formatShortDashboardDate(latestCurrentWeekWeight.date)}`,
            } satisfies ClientWeightPoint,
        ]
      : [];
  }, [dashboardRows, historyCheckIns]);
  const latestNumericWeight =
    typeof latestWeight === "number"
      ? latestWeight
      : Number(String(latestWeight).replace(",", "."));
  const previousNumericWeight =
    dashboardWeightData.length > 1 ? dashboardWeightData[dashboardWeightData.length - 2].peso : Number.NaN;
  const weightDelta =
    !Number.isNaN(latestNumericWeight) && !Number.isNaN(previousNumericWeight)
      ? (latestNumericWeight - previousNumericWeight).toFixed(1)
      : "";
  const registeredDashboardRows = dashboardRows.filter((row) =>
    ["waterLiters", "sleepHours", "steps", "weightKg", "trainedMuscle", "cardio"].some((field) =>
      String(row[field] ?? "").trim(),
    ),
  );
  const dashboardAdherence =
    savedCurrentWeekCheckIn
      ? Math.round((savedCurrentWeekCheckIn.dietCompliance + savedCurrentWeekCheckIn.trainingCompliance) / 2)
      : null;
  const dashboardWaterAverage = averageTrackingField(dashboardRows, "waterLiters");
  const dashboardSleepAverage = averageTrackingField(dashboardRows, "sleepHours");
  const dashboardStepsAverage = averageTrackingField(dashboardRows, "steps");
  const clampPercent = (value: number) => Math.min(Math.max(Math.round(value), 0), 100);
  const weeklyCheckInGoal = 7;
  const weeklyCheckInsCompleted = Math.min(registeredDashboardRows.length, weeklyCheckInGoal);
  const dashboardKpis = [
    {
      label: "Peso actual",
      value: latestWeight !== "—" ? `${latestWeight} kg` : "—",
      delta: weightDelta ? `${Number(weightDelta) > 0 ? "+" : ""}${weightDelta} kg vs. registro anterior` : "Sin variación registrada",
      tone: "orange",
      icon: "scale",
    },
    {
      label: "Adherencia semanal",
      value: dashboardAdherence !== null ? `${dashboardAdherence}%` : "—",
      delta: savedCurrentWeekCheckIn
        ? `${savedCurrentWeekCheckIn.dietCompliance}% dieta / ${savedCurrentWeekCheckIn.trainingCompliance}% entrenamiento`
        : "Pendiente de check-in",
      tone: "teal",
      icon: "pulse",
    },
    {
      label: "Agua promedio",
      value: dashboardWaterAverage !== "-" ? `${dashboardWaterAverage} L` : "—",
      delta: `${weeklyCheckInsCompleted} / ${weeklyCheckInGoal} días registrados`,
      tone: "amber",
      icon: "drop",
    },
    {
      label: "Sueño promedio",
      value: dashboardSleepAverage !== "-" ? `${dashboardSleepAverage} h` : "—",
      delta: dashboardStepsAverage !== "-" ? `${Number(dashboardStepsAverage).toLocaleString("es-PE")} pasos promedio` : "Sin pasos registrados",
      tone: "coral",
      icon: "moon",
    },
  ];
  const latestCheckInDateLabel = latestSubmittedCheckIn ? formatShortDashboardDate(latestSubmittedCheckIn.submittedAt.slice(0, 10)) : "";
  const proofDailyRows = latestSubmittedCheckIn
    ? Array.from(parseDailyObservationEntries(latestSubmittedCheckIn.observations || "").values())
    : [];
  const hasProofDailyRows = proofDailyRows.length > 0;
  const proofRows = hasProofDailyRows ? proofDailyRows : [];
  const proofAverageField = (field: string) => {
    const values = proofRows
      .map((row) => Number(String(row[field] ?? "").replace(",", ".")))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (values.length === 0) {
      return null;
    }

    return values.reduce((sum, value) => sum + value, 0) / values.length;
  };
  const trainingGoalDays = Math.min(Math.max(selectedTrainingDays.length || 5, 1), weeklyCheckInGoal);
  const trainingDaysDone = proofRows.filter((row) => {
    const value = String(row.trainedMuscle ?? "").trim().toLowerCase();
    return value && !value.includes("descanso");
  }).length;
  const dietDaysLogged = proofRows.filter((row) => String(row.dietType ?? "").trim()).length;
  const cardioDaysDone = proofRows.filter((row) => String(row.cardio ?? "").trim()).length;
  const sleepAverageNumber = proofAverageField("sleepHours");
  const trainingScore = hasProofDailyRows
    ? clampPercent((trainingDaysDone / trainingGoalDays) * 100)
    : clampPercent(latestSubmittedCheckIn?.trainingCompliance ?? 0);
  const dietScore = hasProofDailyRows
    ? clampPercent((dietDaysLogged / weeklyCheckInGoal) * 100)
    : clampPercent(latestSubmittedCheckIn?.dietCompliance ?? 0);
  const cardioScore = hasProofDailyRows
    ? clampPercent((cardioDaysDone / 3) * 100)
    : latestSubmittedCheckIn?.cardioCompleted.trim()
      ? 100
      : 0;
  const sleepScore = sleepAverageNumber !== null
    ? clampPercent((sleepAverageNumber / 8) * 100)
    : clampPercent((latestSubmittedCheckIn?.sleepScore ?? 0) * 10);
  const weeklyProofRows = [
    {
      name: "Entrenamiento",
      score: trainingScore,
      evidence: hasProofDailyRows ? `${Math.min(trainingDaysDone, trainingGoalDays)} / ${trainingGoalDays} dias` : "Autoevaluacion",
      detail: hasProofDailyRows ? "Dias con rutina" : "Dato del check-in",
      tone: "orange",
    },
    {
      name: "Dieta",
      score: dietScore,
      evidence: hasProofDailyRows ? `${Math.min(dietDaysLogged, weeklyCheckInGoal)} / ${weeklyCheckInGoal} dias` : "Autoevaluacion",
      detail: hasProofDailyRows ? "Dias con comida" : "Dato del check-in",
      tone: "amber",
    },
    {
      name: "Cardio",
      score: cardioScore,
      evidence: hasProofDailyRows ? `${cardioDaysDone} sesiones` : latestSubmittedCheckIn?.cardioCompleted.trim() ? "Registrado" : "Sin registro",
      detail: "Meta sugerida: 3",
      tone: "teal",
    },
    {
      name: "Sueño",
      score: sleepScore,
      evidence: sleepAverageNumber !== null ? `${sleepAverageNumber.toFixed(1)} h prom.` : `${latestSubmittedCheckIn?.sleepScore ?? 0}/10`,
      detail: "Meta: 8 h",
      tone: "blue",
    },
  ];
  const strongestWeeklyArea = weeklyProofRows.reduce((best, row) => (row.score > best.score ? row : best), weeklyProofRows[0]);
  const weakestWeeklyArea = weeklyProofRows.reduce((weakest, row) => (row.score < weakest.score ? row : weakest), weeklyProofRows[0]);
  const dashboardCompliancePercent = Math.round(
    weeklyProofRows.reduce((sum, row) => sum + row.score, 0) / Math.max(weeklyProofRows.length, 1),
  );
  const upcomingCheckIns = Array.from({ length: 3 }, (_, index) => {
    const date = new Date(`${nextWeekStart}T00:00:00`);
    date.setDate(date.getDate() + index * 7);
    return {
      date: date.toISOString().slice(0, 10),
      weekday: formatWeekdayLabel(date.toISOString().slice(0, 10)).slice(0, 3),
      status: index < 2 ? "Programado" : "Pendiente",
    };
  });
  const weeklySummaryRows = [
    {
      objective: "Entrenamientos",
      routine: `${Math.min(selectedTrainingDays.length || 4, 5)} / 5`,
      nutrition: "-",
      sleep: "-",
      water: "-",
      steps: "-",
      status: "En progreso",
      compliance: savedCurrentWeekCheckIn?.trainingCompliance ?? 80,
    },
    {
      objective: "Nutrición",
      routine: "-",
      nutrition: `${Math.round((savedCurrentWeekCheckIn?.dietCompliance ?? 71) / 14)} / 7`,
      sleep: "-",
      water: "-",
      steps: "-",
      status: "En progreso",
      compliance: savedCurrentWeekCheckIn?.dietCompliance ?? 71,
    },
    {
      objective: "Sueño",
      routine: "-",
      nutrition: "-",
      sleep: `${trackingSleepAverage} / 8 h`,
      water: "-",
      steps: "-",
      status: "En progreso",
      compliance: Math.min(Math.round((Number(trackingSleepAverage) / 8) * 100) || 81, 100),
    },
    {
      objective: "Agua",
      routine: "-",
      nutrition: "-",
      sleep: "-",
      water: `${trackingWaterAverage} / 2.4 L`,
      steps: "-",
      status: "En progreso",
      compliance: Math.min(Math.round((Number(trackingWaterAverage) / 2.4) * 100) || 75, 100),
    },
  ];
  const progressRows = selectedProgressCheckIn ? selectedProgressRows : [];
  const progressWaterAverage = averageTrackingField(progressRows, "waterLiters");
  const progressSleepAverage = averageTrackingField(progressRows, "sleepHours");
  const progressStepsAverage = averageTrackingField(progressRows, "steps");
  const progressWeightAverage = averageTrackingField(progressRows, "weightKg");
  const progressVariation = selectedProgressWeightVariation;
  const progressWeekStart = selectedProgressCheckIn?.weekStart.slice(0, 10) ?? currentWeekStart;
  const historyRows = useMemo(
    () =>
      historyCheckIns.map((checkIn, index) => {
        const meta = getCheckInWeekMeta(data?.checkIns ?? [], checkIn.id);
        const weight = checkIn.dailyWeights[0]?.weightKg ?? null;
        const nextWeight = historyCheckIns[index + 1]?.dailyWeights[0]?.weightKg ?? null;
        const delta = weight !== null && nextWeight !== null ? Number((weight - nextWeight).toFixed(1)) : null;
        const adherence = Math.round((checkIn.dietCompliance + checkIn.trainingCompliance) / 2);
        const checkInsDone = Math.max(1, Math.round((checkIn.trainingCompliance / 100) * 7));
        const status =
          adherence >= 85 && checkInsDone >= 6
            ? "Completado"
            : adherence >= 75
              ? "Con observaciones"
              : "Pendiente";
        const statusTone = status === "Completado" ? "green" : status === "Con observaciones" ? "amber" : "red";
        const rows = buildTrackingRowsFromCheckIn(checkIn, selectedTrainingDays);
        const sparkWeights = rows
          .map((row) => Number(String(row.weightKg || "").replace(",", ".")))
          .filter((value) => !Number.isNaN(value) && value > 0);
        const waterAverage = averageTrackingField(rows, "waterLiters");
        const sleepAverage = averageTrackingField(rows, "sleepHours");
        const stepsAverage = averageTrackingField(rows, "steps");
        const weightAverage = averageTrackingField(rows, "weightKg");
        const weightVariation = calculateWeightVariation(rows);
        const formattedWeightVariation =
          weightVariation === "-" ? "-" : `${Number(weightVariation) > 0 ? "+" : ""}${weightVariation}`;
        const dietSummary = rows.find((row) => String(row.dietType || "").trim())?.dietType || "-";
        const performanceSummary = rows.find((row) => String(row.trainingPerformance || "").trim())?.trainingPerformance || "-";

        return {
          id: checkIn.id,
          week: meta.label,
          date: meta.date,
          range: formatDashboardWeekRange(checkIn.weekStart.slice(0, 10)).replace(" de ", " "),
          weight,
          delta,
          water: checkIn.waterLiters,
          waterPercent: Math.min(Math.round((checkIn.waterLiters / 3.5) * 100), 100),
          adherence,
          checkInsDone,
          status,
          statusTone,
          validatedAt: checkIn.validatedAt ?? null,
          coach: "Coach Jorge",
          note:
            buildSavedCheckInAnswers(checkIn)[0]?.[1] ||
            checkIn.observations?.split("\n").find((line) => line.trim() && !line.includes(":")) ||
            "Seguimiento registrado correctamente.",
          sparkWeights,
          waterAverage,
          sleepAverage,
          stepsAverage,
          weightAverage,
          weightVariation: formattedWeightVariation,
          dietSummary,
          performanceSummary,
        };
      }),
    [data?.checkIns, historyCheckIns, selectedTrainingDays],
  );
  useEffect(() => {
    if (!historyCheckIns.length) {
      setSelectedHistoryId(null);
      return;
    }

    setSelectedHistoryId((current) =>
      current && historyCheckIns.some((checkIn) => checkIn.id === current) ? current : null,
    );
  }, [historyCheckIns]);
  const foundHistoryCheckIn: WeeklyCheckIn | undefined = historyCheckIns.find(
    (checkIn) => checkIn.id === selectedHistoryId,
  );
  const selectedHistoryCheckIn: WeeklyCheckIn | null = foundHistoryCheckIn ?? null;
  const selectedHistoryRow = historyRows.find((row) => row.id === selectedHistoryCheckIn?.id) ?? null;
  const selectedHistoryDailyRows = selectedHistoryCheckIn
    ? buildTrackingRowsFromCheckIn(selectedHistoryCheckIn, selectedTrainingDays)
    : [];
  const previousHistoryCheckIn = getPreviousWeeklyCheckIn(historyCheckIns, selectedHistoryCheckIn);
  const previousHistoryDailyRows = previousHistoryCheckIn
    ? buildTrackingRowsFromCheckIn(previousHistoryCheckIn, selectedTrainingDays)
    : [];
  const selectedHistoryWeightVariation = calculateWeeklyAverageWeightVariation(
    selectedHistoryDailyRows,
    previousHistoryDailyRows,
  );
  const selectedHistoryAnswers = selectedHistoryCheckIn ? buildSavedCheckInAnswers(selectedHistoryCheckIn) : [];
  const selectedHistoryMeasurements = selectedHistoryCheckIn?.bodyMeasurements?.[0] ?? null;
  const historyStats = useMemo(() => {
    const latestRow = historyRows[0];
    const completed = historyRows.filter((row) => row.status === "Completado").length;
    const observed = historyRows.filter((row) => row.status === "Con observaciones").length;
    const pending = historyRows.filter((row) => row.status === "Pendiente").length;
    const averageCompliance =
      historyRows.length > 0
        ? Math.round(historyRows.reduce((sum, row) => sum + row.adherence, 0) / historyRows.length)
        : 0;

    return {
      latestWeight: latestRow?.weight ?? null,
      bestStreak: completed,
      completed,
      observed,
      pending,
      averageCompliance,
    };
  }, [historyRows]);
  const historyWeightChartData = historyRows
    .slice()
    .reverse()
    .map((row) => ({
      label: row.week.replace("Semana ", "Sem "),
      peso: row.weight ?? 0,
    }))
    .filter((row) => row.peso > 0);
  const historyDistributionData = [
    { name: "Completado", value: historyStats.completed, color: "#22c55e" },
    { name: "Con observaciones", value: historyStats.observed, color: "#f59e0b" },
    { name: "Pendiente", value: historyStats.pending, color: "#ef4444" },
  ];
  const comparisonBaseCheckIn =
    savedWeekCheckIns.find((checkIn) => checkIn.id === comparisonBaseCheckInId) ??
    savedWeekCheckIns[1] ??
    savedWeekCheckIns[0] ??
    null;
  const comparisonTargetCheckIn =
    savedWeekCheckIns.find((checkIn) => checkIn.id === comparisonTargetCheckInId) ??
    savedWeekCheckIns[0] ??
    null;
  const comparisonBaseRows = comparisonBaseCheckIn
    ? buildTrackingRowsFromCheckIn(comparisonBaseCheckIn, selectedTrainingDays)
    : [];
  const comparisonTargetRows = comparisonTargetCheckIn
    ? buildTrackingRowsFromCheckIn(comparisonTargetCheckIn, selectedTrainingDays)
    : [];
  const toComparisonNumber = (value: string | number) => {
    const numberValue = typeof value === "number" ? value : Number(String(value).replace(",", "."));
    return Number.isFinite(numberValue) ? numberValue : null;
  };
  const formatComparisonNumber = (value: number | null, unit = "", decimals = 1) =>
    value === null ? "-" : `${value.toLocaleString("es-PE", { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}${unit}`;
  const buildComparisonMetric = (
    label: string,
    baseValue: number | null,
    targetValue: number | null,
    unit = "",
    decimals = 1,
  ) => {
    const difference = baseValue !== null && targetValue !== null ? Number((targetValue - baseValue).toFixed(decimals)) : null;
    const differenceLabel =
      difference === null
        ? "-"
        : `${difference > 0 ? "+" : ""}${difference.toLocaleString("es-PE", {
            maximumFractionDigits: decimals,
            minimumFractionDigits: decimals,
          })}${unit}`;

    return {
      label,
      base: formatComparisonNumber(baseValue, unit, decimals),
      target: formatComparisonNumber(targetValue, unit, decimals),
      difference,
      differenceLabel,
      tone: difference === null ? "neutral" : difference > 0 ? "up" : difference < 0 ? "down" : "same",
    };
  };
  const averageBloodPressure = (rows: Array<{ bloodPressure?: string | null }>) => {
    const values = rows
      .map((row) => {
        const match = String(row.bloodPressure ?? "").match(/(\d{2,3})\s*\/\s*(\d{2,3})/);
        return match ? { systolic: Number(match[1]), diastolic: Number(match[2]) } : null;
      })
      .filter((value): value is { systolic: number; diastolic: number } => value !== null);

    if (values.length === 0) {
      return null;
    }

    return {
      systolic: Math.round(values.reduce((sum, value) => sum + value.systolic, 0) / values.length),
      diastolic: Math.round(values.reduce((sum, value) => sum + value.diastolic, 0) / values.length),
    };
  };
  const buildBloodPressureComparisonMetric = () => {
    const base = averageBloodPressure(comparisonBaseRows);
    const target = averageBloodPressure(comparisonTargetRows);
    const systolicDifference = base && target ? target.systolic - base.systolic : null;
    const diastolicDifference = base && target ? target.diastolic - base.diastolic : null;
    const formatPressure = (value: { systolic: number; diastolic: number } | null) =>
      value ? `${value.systolic}/${value.diastolic}` : "-";
    const formatDifference = () => {
      if (systolicDifference === null || diastolicDifference === null) return "-";
      const systolicSign = systolicDifference > 0 ? "+" : "";
      const diastolicSign = diastolicDifference > 0 ? "+" : "";
      return `${systolicSign}${systolicDifference}/${diastolicSign}${diastolicDifference}`;
    };
    const combinedDifference =
      systolicDifference === null || diastolicDifference === null
        ? null
        : Math.abs(systolicDifference) + Math.abs(diastolicDifference);

    return {
      label: "Presión",
      base: formatPressure(base),
      target: formatPressure(target),
      difference: combinedDifference,
      differenceLabel: formatDifference(),
      tone: combinedDifference === null ? "neutral" : combinedDifference === 0 ? "same" : "up",
    };
  };
  const comparisonMetrics = [
    buildComparisonMetric(
      "Agua (L)",
      toComparisonNumber(averageTrackingField(comparisonBaseRows, "waterLiters")),
      toComparisonNumber(averageTrackingField(comparisonTargetRows, "waterLiters")),
      " L",
      1,
    ),
    buildComparisonMetric(
      "Horas de Sueño",
      toComparisonNumber(averageTrackingField(comparisonBaseRows, "sleepHours")),
      toComparisonNumber(averageTrackingField(comparisonTargetRows, "sleepHours")),
      " h",
      1,
    ),
    buildComparisonMetric(
      "Pasos Diarios",
      toComparisonNumber(averageTrackingField(comparisonBaseRows, "steps")),
      toComparisonNumber(averageTrackingField(comparisonTargetRows, "steps")),
      "",
      0,
    ),
    buildComparisonMetric(
      "Peso (kg)",
      toComparisonNumber(averageTrackingField(comparisonBaseRows, "weightKg")),
      toComparisonNumber(averageTrackingField(comparisonTargetRows, "weightKg")),
      " kg",
      1,
    ),
    buildComparisonMetric(
      "Proteína (g)",
      toComparisonNumber(averageTrackingField(comparisonBaseRows, "proteinGrams")),
      toComparisonNumber(averageTrackingField(comparisonTargetRows, "proteinGrams")),
      " g",
      0,
    ),
    buildComparisonMetric(
      "Carbohidratos (g)",
      toComparisonNumber(averageTrackingField(comparisonBaseRows, "carbsGrams")),
      toComparisonNumber(averageTrackingField(comparisonTargetRows, "carbsGrams")),
      " g",
      0,
    ),
    buildComparisonMetric(
      "Grasas (g)",
      toComparisonNumber(averageTrackingField(comparisonBaseRows, "fatsGrams")),
      toComparisonNumber(averageTrackingField(comparisonTargetRows, "fatsGrams")),
      " g",
      0,
    ),
    buildComparisonMetric(
      "Calorías",
      toComparisonNumber(averageTrackingField(comparisonBaseRows, "calories")),
      toComparisonNumber(averageTrackingField(comparisonTargetRows, "calories")),
      "",
      0,
    ),
    buildComparisonMetric(
      "Glucosa (mg/dL)",
      toComparisonNumber(averageTrackingField(comparisonBaseRows, "bloodGlucose")),
      toComparisonNumber(averageTrackingField(comparisonTargetRows, "bloodGlucose")),
      " mg/dL",
      0,
    ),
    buildBloodPressureComparisonMetric(),
    buildComparisonMetric(
      "Variación de Peso",
      toComparisonNumber(calculateWeightVariation(comparisonBaseRows)),
      toComparisonNumber(calculateWeightVariation(comparisonTargetRows)),
      " kg",
      1,
    ),
  ];
  const measurementComparisons = (
    [
      { label: "Pliegue abdominal", key: "abdominalFoldMm", unit: " mm" },
      { label: "Cintura (relajado)", key: "waistRelaxedCm", unit: " cm" },
      { label: "Cintura (apretado)", key: "waistContractedCm", unit: " cm" },
      { label: "Cadera", key: "hipsRollCm", unit: " cm" },
      { label: "Brazo flexionado", key: "flexedArmCm", unit: " cm" },
      { label: "Muslo", key: "upperThighCm", unit: " cm" },
      { label: "Cuádriceps", key: "quadCm", unit: " cm" },
      ...(isFemale ? ([{ label: "Glúteo", key: "glutesCm", unit: " cm" }] as const) : []),
    ] as const
  ).map((m) =>
    buildComparisonMetric(
      m.label,
      toComparisonNumber(comparisonBaseCheckIn?.bodyMeasurements?.[0]?.[m.key] ?? "") || null,
      toComparisonNumber(comparisonTargetCheckIn?.bodyMeasurements?.[0]?.[m.key] ?? "") || null,
      m.unit,
      1,
    ),
  );
  const comparisonTrendData = comparisonTargetRows.map((targetRow, index) => ({
    label: targetRow.label.slice(0, 3),
    baseWeight: toComparisonNumber(comparisonBaseRows[index]?.weightKg ?? "") ?? undefined,
    targetWeight: toComparisonNumber(targetRow.weightKg ?? "") ?? undefined,
  }));
  const previousDailyTableCheckIn = useMemo(() => {
    const currentWeek = checkInForm.weekStart;
    if (!currentWeek) {
      return null;
    }

    return historyCheckIns
      .slice()
      .sort((left, right) => getCheckInWeekStart(right).localeCompare(getCheckInWeekStart(left)))
      .find((checkIn) => getCheckInWeekStart(checkIn) < currentWeek) ?? null;
  }, [checkInForm.weekStart, historyCheckIns]);
  const previousDailyTableRows = useMemo(
    () => previousDailyTableCheckIn ? buildTrackingRowsFromCheckIn(previousDailyTableCheckIn, selectedTrainingDays) : [],
    [previousDailyTableCheckIn, selectedTrainingDays],
  );
  const dailyTableStats = useMemo(() => {
    const toNumber = (value: unknown) => {
      const parsed = Number(String(value ?? "").replace(",", "."));
      return Number.isFinite(parsed) ? parsed : null;
    };
    const average = (field: keyof (typeof checkInForm.dailyEntries)[number]) => {
      const values = checkInForm.dailyEntries
        .map((entry) => toNumber(field === "calories" ? calculateDailyCalories(entry) || entry.calories : entry[field]))
        .filter((value): value is number => value !== null);

      if (values.length === 0) {
        return "-";
      }

      const wholeNumberFields = new Set<keyof (typeof checkInForm.dailyEntries)[number]>([
        "steps",
        "calories",
      ]);

      return (values.reduce((sum, value) => sum + value, 0) / values.length).toLocaleString("es-PE", {
        maximumFractionDigits: 1,
        minimumFractionDigits: wholeNumberFields.has(field) ? 0 : 1,
      });
    };
    const weights = checkInForm.dailyEntries
      .map((entry) => toNumber(entry.weightKg))
      .filter((value): value is number => value !== null);
    const currentAverageWeight = weights.length > 0
      ? weights.reduce((sum, value) => sum + value, 0) / weights.length
      : null;
    const previousAverageWeight = toNumber(averageTrackingField(previousDailyTableRows, "weightKg"));
    const variationValue = currentAverageWeight !== null && previousAverageWeight !== null
      ? currentAverageWeight - previousAverageWeight
      : null;

    return {
      water: average("waterLiters"),
      sleep: average("sleepHours"),
      steps: average("steps"),
      recovery: average("recoveryScore"),
      weight: average("weightKg"),
      protein: average("proteinGrams"),
      carbs: average("carbsGrams"),
      fats: average("fatsGrams"),
      calories: average("calories"),
      variation:
        variationValue === null
          ? "-"
          : `${variationValue > 0 ? "+" : ""}${variationValue.toLocaleString("es-PE", {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            })} kg`,
    };
  }, [checkInForm.dailyEntries, previousDailyTableRows]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  function changeTab(tab: ClientPortalTab) {
    if (tab === "checkin" && !hasCompletedInitialRoutine) {
      setCheckInError("");
      const message = "Primero debes completar tu rutina inicial antes de registrar un check-in.";
      setAssessmentError(message);
      showToast("error", message);
      setSearchParams({ tab: "assessment" });
      return;
    }

    setSearchParams({ tab });
  }

  function showToast(type: PortalToast["type"], message: string) {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }

    setPortalToast({ type, message });
    toastTimerRef.current = window.setTimeout(() => {
      setPortalToast(null);
      toastTimerRef.current = null;
    }, 3200);
  }

  function handleAssessmentDraftSave() {
    const storageKey = assessmentDraftStorageKey(data?.client?.id);
    window.localStorage.setItem(storageKey, JSON.stringify({ ...assessmentForm, _savedAt: new Date().toISOString() }));
    showToast("success", "Borrador guardado.");
  }

  function handleCheckInDraftSave() {
    if (uploadingPhotos) {
      const message = "Espera a que terminen de subirse las fotos antes de guardar el borrador.";
      setCheckInError(message);
      showToast("error", message);
      return;
    }

    const storageKey = checkInDraftStorageKey(data?.client?.id);
    window.localStorage.setItem(storageKey, JSON.stringify({ ...checkInForm, _savedAt: new Date().toISOString() }));
    showToast("success", "Borrador guardado.");
  }

  async function handleAssessmentSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const missingQuestions = assessmentQuestions.filter((question) => !assessmentForm[question.key].trim());

    if (missingQuestions.length > 0) {
      const conformityMissing = missingQuestions.some((question) => question.key === "conformity");
      const message = conformityMissing
        ? "Debes responder todas las preguntas y marcar la casilla de conformidad para continuar."
        : "Debes responder todas las preguntas del registro inicial antes de enviarlo.";
      setAssessmentError(message);
      showToast("error", message);
      return;
    }

    setAssessmentError("");
    setSavingAssessment(true);
    let assessmentSaved = false;

    try {
      const payload = buildAssessmentPayload(assessmentForm);
      if (clientId) {
        await saveInitialAssessment(clientId, payload);
      } else {
        await saveMyInitialAssessment(payload);
      }
      assessmentSaved = true;
      clearAssessmentDraft(data?.client?.id);
      showToast("success", "Registro inicial guardado correctamente.");
      await loadHistory();
    } catch (err: any) {
      const serverMessage = assessmentSaved
        ? "El registro inicial se guardó, pero no se pudo actualizar la pantalla."
        : err?.response?.data?.message || err?.message || "No se pudo guardar el registro inicial. Intenta nuevamente.";
      const message = Array.isArray(serverMessage) ? serverMessage.join(" ") : serverMessage;
      setAssessmentError(message);
      showToast("error", message);
    } finally {
      setSavingAssessment(false);
    }
  }

  function updateDailyEntry(index: number, field: string, value: string) {
    const nextValue = field === "dayReflections" ? limitWords(value) : value;
    setCheckInError("");

    setCheckInForm((current) => ({
      ...current,
      dailyEntries: current.dailyEntries.map((entry, entryIndex) =>
        entryIndex === index
          ? (() => {
              const nextEntry = { ...entry, [field]: nextValue };
              if (["proteinGrams", "carbsGrams", "fatsGrams"].includes(field)) {
                return { ...nextEntry, calories: calculateDailyCalories(nextEntry) };
              }
              return nextEntry;
            })()
          : entry,
      ),
    }));
  }

  function updateMeasurementExtra(field: keyof typeof checkInForm.measurementExtras, value: string) {
    setCheckInError("");
    setCheckInForm((current) => ({
      ...current,
      measurementExtras: {
        ...current.measurementExtras,
        [field]: value,
      },
      measurements: {
        ...current.measurements,
        waistCm: field === "waistRelaxedCm" ? Number(value || 0) : current.measurements.waistCm,
        hipsCm: field === "hipsRollCm" ? Number(value || 0) : current.measurements.hipsCm,
        rightArmCm: field === "flexedArmCm" ? Number(value || 0) : current.measurements.rightArmCm,
        rightThighCm: field === "upperThighCm" ? Number(value || 0) : current.measurements.rightThighCm,
        leftThighCm: field === "quadCm" ? Number(value || 0) : current.measurements.leftThighCm,
      },
    }));
  }

  function updateSummaryField(field: keyof typeof checkInForm.summary, value: string) {
    setCheckInError("");
    setCheckInForm((current) => ({
      ...current,
      summary: {
        ...current.summary,
        [field]: limitWords(value),
      },
    }));
  }

  async function handleCheckInSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!hasCompletedInitialRoutine) {
      setCheckInError("");
      const message = "Primero debes completar tu rutina inicial antes de registrar un check-in.";
      setAssessmentError(message);
      showToast("error", message);
      setSearchParams({ tab: "assessment" });
      return;
    }

    setCheckInError("");
    setSavingCheckIn(true);
    setUploadingPhotos(true);
    setPhotoErrors([]);

    const normalizedDailyWeights = checkInForm.dailyEntries
      .filter((entry) => entry.weightKg.trim())
      .map((entry) => ({
        date: entry.date,
        weightKg: Number(entry.weightKg),
        fasting: true,
      }));
    const dietTypeMap: Record<string, "HIGH" | "MEDIUM" | "LOW"> = {
      Alta: "HIGH",
      High: "HIGH",
      Media: "MEDIUM",
      Mid: "MEDIUM",
      Baja: "LOW",
      Low: "LOW",
      "Dias On": "HIGH",
      "Dia Off": "LOW",
    };
    type MuscleTrainedPayload = "CHEST" | "BACK" | "SHOULDERS" | "BICEPS" | "TRICEPS" | "LEGS" | "GLUTES" | "ABS" | "FULL_BODY" | "REST";
    const muscleMap: Record<string, MuscleTrainedPayload> = {
      CHEST: "CHEST",
      Push: "CHEST",
      Pecho: "CHEST",
      Empuje: "CHEST",
      BACK: "BACK",
      Pull: "BACK",
      Espalda: "BACK",
      "Jalón": "BACK",
      Jalon: "BACK",
      SHOULDERS: "SHOULDERS",
      Hombros: "SHOULDERS",
      BICEPS: "BICEPS",
      Biceps: "BICEPS",
      TRICEPS: "TRICEPS",
      Triceps: "TRICEPS",
      LEGS: "LEGS",
      Piernas: "LEGS",
      Legs: "LEGS",
      GLUTES: "GLUTES",
      Gluteos: "GLUTES",
      ABS: "ABS",
      Abdomen: "ABS",
      FULL_BODY: "FULL_BODY",
      Upper: "SHOULDERS",
      "Tren superior": "SHOULDERS",
      "Full body": "FULL_BODY",
      "Cuerpo completo": "FULL_BODY",
      REST: "REST",
      "Descanso activo": "REST",
      Descanso: "REST",
    };
    void dietTypeMap;
    void muscleMap;

    const progressPhotos: Array<{ type: ProgressPhotoSlot; imageUrl: string }> = Object.values(uploadedProgressPhotos)
      .filter((photo): photo is UploadedProgressPhotoDraft => Boolean(photo?.imageUrl));

    try {
      for (const type of PROGRESS_PHOTO_SLOTS) {
        const file = progressPhotoFiles[type];
        if (!file) continue;
        const uploaded = clientId
          ? await uploadWeeklyCheckInProgressPhoto(clientId, type, file)
          : await uploadMyWeeklyCheckInProgressPhoto(type, file);
        const existingIndex = progressPhotos.findIndex((photo) => photo.type === uploaded.type);
        if (existingIndex >= 0) {
          progressPhotos[existingIndex] = uploaded;
        } else {
          progressPhotos.push(uploaded);
        }
      }
    } catch (error: any) {
      const serverMessage = error?.response?.data?.message || error?.message;
      const message = Array.isArray(serverMessage)
        ? serverMessage.join(" ")
        : serverMessage || "No se pudo subir una o más fotos. Intenta nuevamente.";
      setPhotoErrors([message]);
      setCheckInError(message);
      showToast("error", message);
      setSavingCheckIn(false);
      return;
    } finally {
      setUploadingPhotos(false);
    }

    if (progressPhotos.length === 0 && isJuanPerezClient) {
      progressPhotos.push(...juanPerezDemoProgressPhotos);
    }

    const payload = {
      weekStart: checkInForm.weekStart,
      dietCompliance: checkInForm.dietCompliance,
      trainingCompliance: checkInForm.trainingCompliance,
      cardioCompleted: checkInForm.cardioCompleted,
      waterLiters: checkInForm.waterLiters,
      sleepScore: checkInForm.sleepScore,
      hungerScore: checkInForm.hungerScore,
      anxietyScore: checkInForm.anxietyScore,
      stressScore: checkInForm.stressScore,
      menstrualCyclePhase: checkInForm.menstrualCyclePhase,
      menstrualSymptoms: checkInForm.menstrualSymptoms,
      menstrualPeriodDates: checkInForm.menstrualPeriodDates,
      dailyWeights: normalizedDailyWeights.length > 0
        ? normalizedDailyWeights
        : [{ date: checkInForm.weekStart, weightKg: 0, fasting: true }],
      measurements: {
        waistCm: toOptionalNumber(checkInForm.measurementExtras.waistRelaxedCm),
        hipsCm: toOptionalNumber(checkInForm.measurementExtras.hipsRollCm),
        chestCm: toOptionalNumber(checkInForm.measurementExtras.waistContractedCm),
        rightArmCm: toOptionalNumber(checkInForm.measurementExtras.flexedArmCm),
        leftArmCm: toOptionalNumber(checkInForm.measurementExtras.abdominalFoldMm),
        rightThighCm: toOptionalNumber(checkInForm.measurementExtras.upperThighCm),
        leftThighCm: toOptionalNumber(checkInForm.measurementExtras.quadCm),
        ...(isFemale
          ? { glutesCm: toOptionalNumber(checkInForm.measurementExtras.glutesCm) }
          : {}),
      },
      progressPhotos,
      observations: buildDetailedCheckInObservations(checkInForm),
    };

    let checkInSaved = false;
    try {
      if (clientId) {
        await saveWeeklyCheckIn(clientId, payload);
      } else {
        await saveMyWeeklyCheckIn(payload);
      }
      checkInSaved = true;
      setSelectedProgressCheckInId("");
      setSelectedCheckInDetailId("");
      clearCheckInDraft(data?.client?.id);
      showToast("success", "Seguimiento guardado correctamente.");
      await loadHistory();
      changeTab("dashboard");
      setProgressPhotoFiles({ FRONT: null, SIDE: null, BACK: null, FAVORITE_1: null, FAVORITE_2: null });
      setUploadedProgressPhotos({ FRONT: null, SIDE: null, BACK: null, FAVORITE_1: null, FAVORITE_2: null });
      setProgressPhotoPreviews({ FRONT: "", SIDE: "", BACK: "", FAVORITE_1: "", FAVORITE_2: "" });
    } catch (err: any) {
      const serverMessage = checkInSaved
        ? "El seguimiento se guardó, pero no se pudo actualizar la pantalla."
        : err?.response?.data?.message || err?.message || "No se pudo guardar el seguimiento. Intenta nuevamente.";
      const message = Array.isArray(serverMessage) ? serverMessage.join(" ") : serverMessage;
      setCheckInError(message);
      showToast("error", message);
    } finally {
      setSavingCheckIn(false);
      setUploadingPhotos(false);
    }
  }

  return (
    <section className="page client-portal adminlte-inner-page">
      {portalToast ? (
        <div className={`portal-toast portal-toast--${portalToast.type}`} role="status" aria-live="polite">
          <span className="portal-toast-icon" aria-hidden="true">
            {portalToast.type === "success" ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.3 3.9 2.8 18a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
              </svg>
            )}
          </span>
          <strong>{portalToast.message}</strong>
        </div>
      ) : null}

      {isCoachPreview ? (
        <div className={`coach-client-portal-nav${portalMenuOpen ? " coach-client-portal-nav--open" : ""}`} aria-label="Menu del portal cliente">
          <div>
            <span>Portal cliente</span>
            <strong>{dashboardFullName}</strong>
          </div>
          <button
            type="button"
            className="coach-client-portal-nav-toggle"
            aria-haspopup="menu"
            aria-expanded={portalMenuOpen}
            onClick={() => setPortalMenuOpen((value) => !value)}
          >
            <span>{clientPortalNavItems.find((item) => item.tab === activeTab)?.label ?? "Secciones"}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          <nav>
            {clientPortalNavItems.map((item) => (
              <button
                key={item.tab}
                type="button"
                className={activeTab === item.tab ? "is-active" : ""}
                onClick={() => {
                  changeTab(item.tab);
                  setPortalMenuOpen(false);
                }}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      ) : null}

      {activeTab === "dashboard" && (
        <div className="cdash-page">
          <div className="cdash-header">
            <div className="cdash-greeting">
              <h1 className="cdash-title">Hola, {dashboardFullName}!</h1>
              <p className="cdash-subtitle">Seguimiento personal, evolución y objetivos semanales.</p>
            </div>
            <div className="cdash-date-picker">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>{dashboardWeekRange}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>
          </div>

          <div className="cdash-kpi-grid">
            {dashboardKpis.map((card) => (
              <article key={card.label} className={`cdash-kpi cdash-kpi--${card.tone}`}>
                <div className="cdash-kpi-top">
                  <span className="cdash-kpi-icon" aria-hidden="true">
                    {card.icon === "pulse" ? (
                      <svg viewBox="0 0 24 24"><path d="M3 12h4l2-6 4 12 3-6h5" /></svg>
                    ) : card.icon === "scale" ? (
                      <svg viewBox="0 0 24 24"><rect x="5" y="4" width="14" height="16" rx="3" /><path d="M9 10a3 3 0 0 1 6 0" /><path d="M12 10V7" /></svg>
                    ) : card.icon === "clipboard" ? (
                      <svg viewBox="0 0 24 24"><rect x="5" y="5" width="14" height="16" rx="2" /><path d="M9 5a3 3 0 0 1 6 0" /><path d="m9 14 2 2 4-5" /></svg>
                    ) : card.icon === "drop" ? (
                      <svg viewBox="0 0 24 24"><path d="M12 3s6 6.2 6 11a6 6 0 0 1-12 0c0-4.8 6-11 6-11z" /></svg>
                    ) : card.icon === "moon" ? (
                      <svg viewBox="0 0 24 24"><path d="M20 15.2A8 8 0 0 1 8.8 4 7 7 0 1 0 20 15.2z" /></svg>
                    ) : (
                      <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="M12 2v4M22 12h-4" /></svg>
                    )}
                  </span>
                  <div>
                    <p className="cdash-kpi-label">{card.label}</p>
                    <p className="cdash-kpi-value">{card.value}</p>
                  </div>
                </div>
                <svg className="cdash-kpi-spark" viewBox="0 0 180 48" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M0 34 C18 34 18 22 34 22 C50 22 47 36 64 34 C82 32 76 14 94 15 C112 16 106 33 124 30 C142 27 137 10 154 12 C170 14 164 22 180 20" />
                </svg>
                <p className="cdash-kpi-delta">{card.delta}</p>
              </article>
            ))}
          </div>

          <div className="cdash-charts-row">
            <section className="cdash-panel cdash-panel--wide">
              <div className="cdash-panel-head">
                <div>
                  <h3 className="cdash-panel-title">Evolución de peso</h3>
                  <p className="cdash-panel-copy">Tu peso registrado en cada check-in semanal.</p>
                </div>
                <button className="cdash-chip" type="button">Ultimos 8 check-ins</button>
              </div>
              {dashboardWeightData.length > 0 ? (
                <ResponsiveContainer width="100%" height={360}>
                  <BarChart data={dashboardWeightData} margin={{ top: 12, right: 18, left: -18, bottom: 6 }} barCategoryGap="66%">
                    <defs>
                      <linearGradient id="clientWeightBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ff9544" />
                        <stop offset="100%" stopColor="#ea580c" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#eef1f5" />
                    <XAxis dataKey="axisLabel" tick={<ClientWeightWeekTick />} height={44} interval={0} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, "dataMax + 8"]} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} unit=" kg" />
                    <Tooltip content={<ClientWeightTooltip />} cursor={{ fill: "rgba(255, 79, 15, 0.06)" }} />
                    <Bar
                      dataKey="peso"
                      fill="url(#clientWeightBar)"
                      radius={[8, 8, 0, 0]}
                      barSize={32}
                      background={false}
                      isAnimationActive
                      animationDuration={900}
                    >
                      <LabelList
                        dataKey="peso"
                        position="top"
                        formatter={(value) => `${Number(value).toFixed(1)} kg`}
                        style={{ fill: "#0f172a", fontSize: 12, fontWeight: 800 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="cdash-empty">
                  <span>Sin datos</span>
                  <p>Tu gráfico de peso aparecerá aquí cuando registres seguimientos semanales.</p>
                  <button className="cdash-btn-primary" onClick={() => changeTab("checkin")} type="button">Registrar check-in</button>
                </div>
              )}
            </section>

            <section className="cdash-panel">
              <div className="cdash-panel-head">
                <div>
                  <h3 className="cdash-panel-title">Cumplimiento semanal</h3>
                  <p className="cdash-panel-copy">
                    {latestCheckInDateLabel
                      ? `Datos del ultimo check-in registrado el ${latestCheckInDateLabel}.`
                      : "Aun no hay check-ins registrados."}
                  </p>
                </div>
              </div>
              <div className="cdash-weekly-proof">
                <div className="cdash-weekly-score">
                  <strong>{dashboardCompliancePercent}%</strong>
                  <span>Promedio semanal</span>
                </div>
                <div className="cdash-weekly-bars">
                  {weeklyProofRows.map((row) => (
                    <div key={row.name} className={`cdash-weekly-row cdash-weekly-row--${row.tone}`}>
                      <div className="cdash-weekly-row-head">
                        <span>{row.name}</span>
                        <strong>{row.score}%</strong>
                      </div>
                      <span className="cdash-weekly-track">
                        <span style={{ width: `${row.score}%` }} />
                      </span>
                      <div className="cdash-weekly-row-foot">
                        <span>{row.evidence}</span>
                        <em>{row.detail}</em>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="cdash-weekly-insight">
                  <span>Mejor: <strong>{strongestWeeklyArea.name}</strong></span>
                  <span>Ajustar: <strong>{weakestWeeklyArea.name}</strong></span>
                </div>
              </div>
            </section>
          </div>

          {showClientDashboardScheduleSections && (
          <div className="cdash-mid-row">
            <section className="cdash-panel">
              <div className="cdash-panel-head">
                <div>
                  <h3 className="cdash-panel-title">Proximos check-ins</h3>
                  <p className="cdash-panel-copy">Tus proximos check-ins semanales programados.</p>
                </div>
              </div>
              <div className="cdash-list cdash-list--compact">
                {upcomingCheckIns.map((item) => (
                  <div key={item.date} className="cdash-list-row">
                    <span className="cdash-row-icon cdash-row-icon--orange" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
                    </span>
                    <span>{formatShortDashboardDate(item.date)}</span>
                    <span>{item.weekday}</span>
                    <strong>Seguimiento semanal</strong>
                    <span className={`cdash-list-status ${item.status === "Programado" ? "cdash-list-status--ok" : ""}`}>{item.status}</span>
                    <span>10:00 AM</span>
                    <button className="cdash-row-action" type="button" aria-label="Abrir check-in" onClick={() => changeTab("checkin")}>›</button>
                  </div>
                ))}
              </div>
              <button className="cdash-link-btn" type="button" onClick={() => changeTab("history")}>Ver calendario completo <span aria-hidden="true">→</span></button>
            </section>
          </div>
          )}

          {showClientDashboardScheduleSections && (
          <section className="cdash-panel cdash-panel--full">
            <div className="cdash-panel-head">
              <div>
                <h3 className="cdash-panel-title">Resumen semanal</h3>
                <p className="cdash-panel-copy">Seguimiento de hábitos y objetivos clave de esta semana.</p>
              </div>
              <button className="cdash-btn-primary" type="button" onClick={() => changeTab("checkin")}>+ Registrar check-in</button>
            </div>
            <div className="cdash-table-wrap">
              <table className="cdash-table">
                <thead>
                  <tr>
                    <th>Objetivo</th>
                    <th>Rutina</th>
                    <th>Nutrición</th>
                    <th>Sueño</th>
                    <th>Agua</th>
                    <th>Pasos</th>
                    <th>Estado</th>
                    <th>Cumplimiento</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {weeklySummaryRows.map((row) => (
                    <tr key={row.objective}>
                      <td className="cdash-td-label">{row.objective}</td>
                      <td>{row.routine}</td>
                      <td>{row.nutrition}</td>
                      <td>{row.sleep}</td>
                      <td>{row.water}</td>
                      <td>{row.steps}</td>
                      <td><span className="cdash-status-pill">{row.status}</span></td>
                      <td>
                        <span className="cdash-progress-cell">
                          <strong>{row.compliance}%</strong>
                          <span className="cdash-progress-bar"><span style={{ width: `${row.compliance}%` }} /></span>
                        </span>
                      </td>
                      <td><button className="cdash-row-action" type="button" aria-label={`Ver ${row.objective}`}>›</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          )}
        </div>
      )}

      {false && (
        <div className="nutrition-page">
          <section className="nutrition-hero">
            <div>
              <p>Plan nutricional</p>
              <h1>Nutrición personalizada</h1>
              <span>Consulta tu alimentación diaria, macros asignados y recomendaciones de tu coach.</span>
            </div>
            <div className="nutrition-hero-art" aria-hidden="true">
              <svg viewBox="0 0 260 150">
                <path d="M72 72c14-20 54-26 88-8 22 11 38 29 34 45-5 22-42 30-80 22-41-9-62-32-42-59z" fill="#fff4eb" stroke="#f97316" />
                <path d="M97 60c-7-15 5-31 22-26 3 18-8 28-22 26z" fill="#6abf69" stroke="#24914a" />
                <path d="M126 59c-3-19 17-30 31-17-3 17-15 24-31 17z" fill="#7acb63" stroke="#24914a" />
                <circle cx="146" cy="77" r="20" fill="#ff7a1a" stroke="#ffb36b" />
                <path d="M146 57v40M126 77h40M132 63l28 28M160 63l-28 28" stroke="#fff2d8" />
                <circle cx="180" cy="73" r="13" fill="#ef4444" stroke="#ffb3b3" />
                <path d="M180 61v24M168 73h24" stroke="#ffd5d5" />
                <rect x="190" y="25" width="52" height="82" rx="8" fill="#ffe8df" stroke="#ff8c6a" />
                <path d="M202 47h25M202 66h25M202 85h25" stroke="#ffb09a" />
                <path d="m199 47 5 5 8-10M199 66l5 5 8-10" stroke="#57b66b" />
                <rect x="205" y="16" width="22" height="16" rx="5" fill="#68758a" />
                <rect x="226" y="52" width="28" height="62" rx="8" fill="#ffe0df" stroke="#ffa0a0" />
                <path d="M232 45h16" stroke="#68758a" />
                <circle cx="218" cy="118" r="8" fill="#ef4444" />
                <path d="M50 80 22 105M52 82l-20 34M48 78l-32 18" stroke="#e8d8c9" />
                <path d="M236 110 258 126M240 104l18 8M242 98l18 2" stroke="#e8d8c9" />
              </svg>
            </div>
          </section>

          <section className="nutrition-macros">
            {[
              { label: "Calorías diarias", value: "2,450 kcal", tone: "orange", icon: "fire" },
              { label: "Proteína", value: "180 g", tone: "teal", icon: "protein" },
              { label: "Carbohidratos", value: "260 g", tone: "amber", icon: "grain" },
              { label: "Grasas", value: "70 g", tone: "red", icon: "drop" },
            ].map((macro) => (
              <article key={macro.label} className={`nutrition-macro-card nutrition-macro-card--${macro.tone}`}>
                <span className="nutrition-macro-icon" aria-hidden="true">
                  {macro.icon === "fire" ? (
                    <svg viewBox="0 0 24 24"><path d="M12 22c4 0 7-3 7-7 0-5-4-8-5-13-3 2-4 5-4 8-2-1-3-3-3-5-2 2-3 5-3 8 0 5 4 9 8 9z" /></svg>
                  ) : macro.icon === "protein" ? (
                    <svg viewBox="0 0 24 24"><path d="M7 18c4 0 7-3 7-7V7a3 3 0 0 1 6 0" /><path d="M7 18a4 4 0 0 1-4-4c0-3 2-6 5-9" /><path d="M13 14h7" /></svg>
                  ) : macro.icon === "grain" ? (
                    <svg viewBox="0 0 24 24"><path d="M12 22V4" /><path d="M12 8c-4 0-6-2-7-5 4 0 6 2 7 5zM12 13c-4 0-6-2-7-5 4 0 6 2 7 5zM12 18c-4 0-6-2-7-5 4 0 6 2 7 5zM12 8c4 0 6-2 7-5-4 0-6 2-7 5zM12 13c4 0 6-2 7-5-4 0-6 2-7 5z" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24"><path d="M12 3c4 5.5 6 9 6 12a6 6 0 0 1-12 0c0-3 2-6.5 6-12z" /></svg>
                  )}
                </span>
                <div>
                  <p>{macro.label}</p>
                  <strong>{macro.value}</strong>
                </div>
              </article>
            ))}
          </section>

          <section className="nutrition-main-grid">
            <article className="nutrition-panel nutrition-meals-panel">
              <div className="nutrition-panel-head">
                <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
                <h2>Comidas del día</h2>
              </div>
              <div className="nutrition-meal-list">
                {[
                  { name: "Desayuno", time: "08:00 AM", food: "Avena 80 g, Huevos 3 unidades, Plátano 1 unidad, Proteína whey 1 scoop", kcal: "520 kcal", p: "38g", c: "62g", g: "14g", tone: "orange", icon: "sun" },
                  { name: "Almuerzo", time: "01:00 PM", food: "Arroz 180 g, Pollo 180 g, Palta 60 g, Ensalada libre", kcal: "720 kcal", p: "52g", c: "85g", g: "18g", tone: "teal", icon: "sun" },
                  { name: "Merienda", time: "05:00 PM", food: "Yogurt griego, Frutos rojos, Granola 40 g", kcal: "320 kcal", p: "24g", c: "34g", g: "9g", tone: "purple", icon: "bowl" },
                  { name: "Cena", time: "08:00 PM", food: "Papa 250 g, Pescado 180 g, Verduras, Aceite de oliva 10 g", kcal: "610 kcal", p: "48g", c: "65g", g: "16g", tone: "blue", icon: "moon" },
                ].map((meal) => (
                  <div key={meal.name} className="nutrition-meal-row">
                    <span className={`nutrition-meal-icon nutrition-meal-icon--${meal.tone}`} aria-hidden="true">
                      {meal.icon === "moon" ? (
                        <svg viewBox="0 0 24 24"><path d="M20 15.5A8 8 0 1 1 8.5 4 6.5 6.5 0 0 0 20 15.5z" /></svg>
                      ) : meal.icon === "bowl" ? (
                        <svg viewBox="0 0 24 24"><path d="M5 12h14a7 7 0 0 1-14 0z" /><path d="M8 9c0-2 2-2 2-4M14 9c0-2 2-2 2-4" /></svg>
                      ) : (
                        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" /></svg>
                      )}
                    </span>
                    <div className="nutrition-meal-title">
                      <strong>{meal.name}</strong>
                      <span>{meal.time}</span>
                    </div>
                    <div className="nutrition-meal-food">
                      <p>{meal.food}</p>
                      <span><b>{meal.kcal}</b><i /> P: {meal.p}<i /> C: {meal.c}<i /> G: {meal.g}</span>
                    </div>
                    <button type="button" className="nutrition-edit-btn">Editar</button>
                    <button type="button" className="nutrition-row-arrow" aria-label={`Abrir ${meal.name}`}>›</button>
                  </div>
                ))}
              </div>
            </article>

            <aside className="nutrition-side-stack">
              <article className="nutrition-panel nutrition-macro-panel">
                <div className="nutrition-panel-head">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3c3 4 7 4 9 0-1 6-4 9-9 9S4 9 3 3c2 4 6 4 9 0z" /><path d="M12 12v9" /></svg>
                  <h2>Distribución de macros</h2>
                </div>
                <div className="nutrition-donut-row">
                  <div className="nutrition-donut">
                    <ResponsiveContainer width="100%" height={132}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Proteína", value: 30, color: "#40b9b5" },
                            { name: "Carbohidratos", value: 45, color: "#ff9f1c" },
                            { name: "Grasas", value: 25, color: "#ff3f4f" },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={38}
                          outerRadius={58}
                          paddingAngle={2}
                        >
                          {["#40b9b5", "#ff9f1c", "#ff3f4f"].map((color) => <Cell key={color} fill={color} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="nutrition-macro-legend">
                    {[
                      ["Proteína", "30%", "#40b9b5"],
                      ["Carbohidratos", "45%", "#ff9f1c"],
                      ["Grasas", "25%", "#ff3f4f"],
                    ].map(([name, value, color]) => (
                      <div key={name}>
                        <span style={{ backgroundColor: color }} />
                        <p>{name}</p>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </article>

              <article className="nutrition-panel nutrition-hydration-panel">
                <div className="nutrition-panel-head nutrition-panel-head--split">
                  <div>
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3c4 5.5 6 9 6 12a6 6 0 0 1-12 0c0-3 2-6.5 6-12z" /></svg>
                    <h2>Hidratación</h2>
                  </div>
                  <strong>72%</strong>
                </div>
                <p>Agua objetivo: 3.5 L</p>
                <div className="nutrition-water-bar"><span style={{ width: "72%" }} /></div>
                <b>2.5 L consumidos</b>
              </article>

              <article className="nutrition-panel nutrition-schedule-panel">
                <div className="nutrition-panel-head">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" /><path d="M12 8v5l3 2" /></svg>
                  <h2>Horario sugerido</h2>
                </div>
                <div className="nutrition-times">
                  {["08:00", "11:00", "13:00", "17:00", "20:00"].map((time) => <span key={time}>{time}</span>)}
                </div>
              </article>
            </aside>
          </section>

          <section className="nutrition-lower-grid">
            <article className="nutrition-panel">
              <div className="nutrition-panel-head">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2 6.4 20.2 7.5 14 3 9.6l6.2-.9z" /></svg>
                <h2>Recomendaciones del coach</h2>
              </div>
              <div className="nutrition-coach-list">
                {[
                  ["protein", "Mantén proteína en cada comida principal."],
                  ["grain", "No reduzcas carbohidratos los días de pierna."],
                  ["water", "Aumenta 500 ml de agua si haces cardio."],
                  ["no", "Evita comidas fuera del plan entre semana."],
                  ["ok", "Usa proteína whey solo como complemento, no como reemplazo total."],
                ].map(([tone, text]) => (
                  <div key={text} className={`nutrition-coach-row nutrition-coach-row--${tone}`}>
                    <span aria-hidden="true" />
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            </article>

            <article className="nutrition-panel">
              <div className="nutrition-panel-head">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3v8M4 3v8M10 3v8M4 11h6M7 11v10M17 3v18" /></svg>
                <h2>Alternativas de alimentos</h2>
              </div>
              <div className="nutrition-alternatives">
                {[
                  ["Proteínas", ["Pollo", "Pescado", "Huevos", "Carne magra", "Atún", "Whey"], "red"],
                  ["Carbohidratos", ["Arroz", "Papa", "Camote", "Avena", "Pan integral", "Frutas"], "amber"],
                  ["Grasas saludables", ["Palta", "Aceite de oliva", "Frutos secos", "Yema de huevo"], "green"],
                  ["Verduras", ["Brócoli", "Lechuga", "Espinaca", "Pepino", "Zanahoria"], "green"],
                ].map(([group, items, tone]) => (
                  <div key={group as string} className="nutrition-alt-row">
                    <strong>{group as string}</strong>
                    <div>
                      {(items as string[]).map((item) => <span key={item} className={`nutrition-alt-chip nutrition-alt-chip--${tone}`}>{item}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="nutrition-panel nutrition-restrictions">
            <div className="nutrition-panel-head">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6z" /></svg>
              <h2>Restricciones y observaciones</h2>
            </div>
            <div className="nutrition-note-chips">
              {[
                ["Sin alergias registradas", "green"],
                ["Puede usar proteína en polvo", "blue"],
                ["Prefiere 4 comidas sólidas al día", "purple"],
                ["Objetivo: recomposición corporal", "orange"],
                ["Check-in nutricional actualizado", "green"],
              ].map(([text, tone]) => <span key={text} className={`nutrition-note-chip nutrition-note-chip--${tone}`}>{text}</span>)}
            </div>
          </section>
        </div>
      )}

      {activeTab === "comparison" && (
        <div className="comparison-page">
          <section className="comparison-hero">
            <div>
              <p>Comparativa</p>
              <h1>Compara semanas registradas</h1>
              <span>Elige dos seguimientos para ver cambios en peso, hábitos y adherencia.</span>
            </div>
          </section>

          {savedWeekCheckIns.length >= 2 ? (
            <>
              <section className="comparison-controls">
                <label>
                  <span>Semana base</span>
                  <select
                    value={comparisonBaseCheckIn?.id ?? ""}
                    onChange={(event) => setComparisonBaseCheckInId(event.target.value)}
                  >
                    {savedWeekOptions.map((option) => (
                      <option key={`base-${option.id}`} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Semana comparada</span>
                  <select
                    value={comparisonTargetCheckIn?.id ?? ""}
                    onChange={(event) => setComparisonTargetCheckInId(event.target.value)}
                  >
                    {savedWeekOptions.map((option) => (
                      <option key={`target-${option.id}`} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </section>

              <section className="comparison-summary-grid">
                {comparisonMetrics.slice(0, 5).map((metric, index) => (
                  <article key={metric.label} className={`comparison-summary-card comparison-summary-card--${metric.tone}`}>
                    <div className="comparison-summary-top">
                      <span className="comparison-summary-icon" aria-hidden="true">
                        {index === 0 ? (
                          <svg viewBox="0 0 24 24"><path d="M12 5c3.5 4.5 5.5 7.8 5.5 10.5a5.5 5.5 0 0 1-11 0C6.5 12.8 8.5 9.5 12 5z" /></svg>
                        ) : index === 1 ? (
                          <svg viewBox="0 0 24 24"><path d="M18.5 14.5A6.5 6.5 0 0 1 9.5 5.5 7 7 0 1 0 18.5 14.5z" /></svg>
                        ) : index === 2 ? (
                          <svg viewBox="0 0 24 24"><path d="M3 12h4l2-6 4 12 3-6h5" /></svg>
                        ) : index === 3 ? (
                          <svg viewBox="0 0 24 24"><rect x="6" y="5" width="12" height="14" rx="2.5" /><path d="M9.5 10.5a2.5 2.5 0 0 1 5 0" /><path d="M12 10.5V8" /></svg>
                        ) : (
                          <svg viewBox="0 0 24 24"><path d="M4 17l5-5 4 4 7-8" /><path d="M14 8h6v6" /></svg>
                        )}
                      </span>
                      <div>
                        <p>{metric.label}</p>
                        <strong>{metric.differenceLabel}</strong>
                      </div>
                    </div>
                    <svg className="comparison-summary-spark" viewBox="0 0 180 48" preserveAspectRatio="none" aria-hidden="true">
                      <path d="M0 30 C18 30 18 18 36 18 S54 38 72 30 90 10 108 20 126 38 144 24 162 16 180 24" />
                    </svg>
                  </article>
                ))}
              </section>

              <section className="comparison-layout">
                <article className="comparison-panel">
                  <div className="comparison-panel-head">
                    <h2>Resumen comparativo</h2>
                    <p>Diferencia calculada desde la semana base hacia la semana comparada.</p>
                  </div>
                  <div className="comparison-table-columns">
                    {[comparisonMetrics.slice(0, 6), comparisonMetrics.slice(6, 11)].map((metricsGroup, groupIndex) => (
                      <div key={`comparison-group-${groupIndex}`} className="comparison-table-wrap">
                        <table className="comparison-table comparison-table--compact">
                          <thead>
                            <tr>
                              <th>Métrica</th>
                              <th>Base</th>
                              <th>Comparada</th>
                              <th>Diferencia</th>
                            </tr>
                          </thead>
                          <tbody>
                            {metricsGroup.map((metric) => (
                              <tr key={metric.label}>
                                <td><strong>{metric.label}</strong></td>
                                <td>{metric.base}</td>
                                <td>{metric.target}</td>
                                <td><span className={`comparison-delta comparison-delta--${metric.tone}`}>{metric.differenceLabel}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </article>

                <article className="comparison-panel">
                  <div className="comparison-panel-head">
                    <h2>Comparativa de medidas</h2>
                    <p>Diferencia en medidas corporales entre las semanas.</p>
                  </div>
                  <div className="comparison-table-wrap">
                    <table className="comparison-table">
                      <thead>
                        <tr>
                          <th>Medida</th>
                          <th>Base</th>
                          <th>Comparada</th>
                          <th>Diferencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {measurementComparisons.map((m) => (
                          <tr key={m.label}>
                            <td><strong>{m.label}</strong></td>
                            <td>{m.base}</td>
                            <td>{m.target}</td>
                            <td><span className={`comparison-delta comparison-delta--${m.tone}`}>{m.differenceLabel}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </article>

                <article className="comparison-panel comparison-panel--daily-weight">
                  <div className="comparison-panel-head">
                    <h2>Peso diario</h2>
                    <p>Lecturas registradas día por día en ambas semanas.</p>
                  </div>
                  <div className="comparison-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={comparisonTrendData} margin={{ top: 12, right: 18, left: -18, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={false} tickLine={false} />
                        <Tooltip />
                        <Line type="monotone" dataKey="baseWeight" name="Base" stroke="#64748b" strokeWidth={3} dot={{ r: 4 }} connectNulls />
                        <Line type="monotone" dataKey="targetWeight" name="Comparada" stroke="#ff4f0f" strokeWidth={3} dot={{ r: 4 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </article>
              </section>

              <section className="comparison-extras">
                <article className="comparison-panel comparison-panel--photos">
                  <div className="comparison-panel-head">
                    <h2>Comparativa de fotos</h2>
                    <p>Fotos de progreso registradas en cada semana.</p>
                  </div>
                  <div className="comparison-photos-grid">
                    {PROGRESS_PHOTO_SLOTS.map((type) => {
                      const basePhoto = comparisonBaseCheckIn?.progressPhotos.find((p) => p.type === type);
                      const targetPhoto = comparisonTargetCheckIn?.progressPhotos.find((p) => p.type === type);
                      const label = progressPhotoLabel(type);
                      const comparisonPhotoItems = [
                        basePhoto
                          ? {
                              title: `${label} - Base`,
                              subtitle: comparisonBaseCheckIn ? `Semana ${comparisonBaseCheckIn.weekStart.slice(0, 10)}` : "Semana base",
                              src: resolveProgressPhotoUrl(type, basePhoto.imageUrl),
                            }
                          : null,
                        targetPhoto
                          ? {
                              title: `${label} - Comparada`,
                              subtitle: comparisonTargetCheckIn ? `Semana ${comparisonTargetCheckIn.weekStart.slice(0, 10)}` : "Semana comparada",
                              src: resolveProgressPhotoUrl(type, targetPhoto.imageUrl),
                            }
                          : null,
                      ].filter(Boolean) as PhotoViewerItem[];
                      return (
                        <div key={type} className="comparison-photo-row">
                          <strong className="comparison-photo-label">{label}</strong>
                          <div className="comparison-photo-pair">
                            <figure>
                              <figcaption>Base</figcaption>
                              {basePhoto ? (
                                <img
                                  src={resolveProgressPhotoUrl(type, basePhoto.imageUrl)}
                                  alt={`Foto ${label} base`}
                                  onClick={() => openPhotoViewer(comparisonPhotoItems, 0)}
                                  onError={(event) => {
                                    event.currentTarget.onerror = null;
                                    event.currentTarget.src = buildProgressPhotoFallback(type);
                                  }}
                                />
                              ) : (
                                <div className="comparison-photo-empty">Sin foto</div>
                              )}
                            </figure>
                            <figure>
                              <figcaption>Comparada</figcaption>
                              {targetPhoto ? (
                                <img
                                  src={resolveProgressPhotoUrl(type, targetPhoto.imageUrl)}
                                  alt={`Foto ${label} comparada`}
                                  onClick={() => openPhotoViewer(comparisonPhotoItems, basePhoto ? 1 : 0)}
                                  onError={(event) => {
                                    event.currentTarget.onerror = null;
                                    event.currentTarget.src = buildProgressPhotoFallback(type);
                                  }}
                                />
                              ) : (
                                <div className="comparison-photo-empty">Sin foto</div>
                              )}
                            </figure>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </article>
              </section>
            </>
          ) : (
            <section className="comparison-empty">
              <strong>Necesitas al menos 2 semanas guardadas</strong>
              <p>Cuando tengas dos seguimientos, podrás comparar sus métricas principales aquí.</p>
            </section>
          )}
        </div>
      )}

      {activeTab === "history" && (
        <div className="history-page">
          {selectedHistoryCheckIn ? (
            <div className={historyDetailMode === "progress" ? "progress-page" : "checkin-detail-page"}>
              <div className="history-detail-switch">
                <button type="button" className="history-detail-back" onClick={() => setSelectedHistoryId(null)}>
                  ← Volver al historial
                </button>
                <div>
                  <button type="button" className={historyDetailMode === "progress" ? "active" : ""} onClick={() => setHistoryDetailMode("progress")}>Mi progreso</button>
                  <button type="button" className={historyDetailMode === "checkin" ? "active" : ""} onClick={() => setHistoryDetailMode("checkin")}>Check-in</button>
                </div>
              </div>

              {historyDetailMode === "progress" ? (
                <>
                  <section className="progress-hero">
                    <div>
                      <p className="progress-hero-eyebrow">Mi progreso</p>
                      <h1>Visualiza tu progreso</h1>
                      <p>Datos consolidados de {selectedHistoryRow?.week ?? "la semana registrada"}.</p>
                    </div>
                  </section>

                  <section className="progress-metrics">
                    {[
                      { label: "Promedio de agua", value: `${averageTrackingField(selectedHistoryDailyRows, "waterLiters")} L`, delta: "semana registrada", tone: "blue", icon: "drop" },
                      { label: "Promedio de sueño", value: `${averageTrackingField(selectedHistoryDailyRows, "sleepHours")} h`, delta: "semana registrada", tone: "purple", icon: "moon" },
                      { label: "Promedio de pasos", value: Number(averageTrackingField(selectedHistoryDailyRows, "steps")).toLocaleString("es-PE"), delta: "semana registrada", tone: "green", icon: "shoe" },
                      { label: "Peso promedio", value: `${averageTrackingField(selectedHistoryDailyRows, "weightKg")} kg`, delta: "semana registrada", tone: "orange", icon: "scale" },
                      { label: "Variación de peso", value: selectedHistoryWeightVariation === "-" ? "-" : `${selectedHistoryWeightVariation} kg`, delta: "vs. semana anterior", tone: "amber", icon: "trend" },
                    ].map((metric) => (
                      <article key={metric.label} className={`progress-metric progress-metric--${metric.tone}`}>
                        <span className="progress-metric-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /></svg>
                        </span>
                        <div>
                          <p>{metric.label}</p>
                          <strong>{metric.value.includes("NaN") ? "-" : metric.value}</strong>
                          <span>{metric.delta}</span>
                        </div>
                      </article>
                    ))}
                  </section>

                  <section className="progress-board">
                    <div className="progress-table-panel">
                      <div className="progress-panel-head">
                        <span className="progress-section-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
                        </span>
                        <div>
                          <h3>Registro diario de la semana</h3>
                          <p>Datos guardados en el seguimiento seleccionado.</p>
                        </div>
                      </div>
                      {renderWeeklyTrackingTable(selectedHistoryDailyRows, undefined, selectedHistoryWeightVariation)}
                    </div>
                  </section>
                </>
              ) : (
                <>
                  <section className="checkin-detail-hero">
                    <div>
                      <p className="checkin-detail-eyebrow">Check-ins</p>
                      <h1>Detalle del check-in semanal</h1>
                      <p>Respuestas, fotos y resumen general de la semana seleccionada.</p>
                    </div>
                    <div className="checkin-banner-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setCheckInViewMode("new");
                          changeTab("checkin");
                        }}
                      >
                        Registrar check-in
                      </button>
                    </div>
                    <div className="checkin-detail-hero-meta">
                      <span><b>Semana</b><strong>{selectedHistoryCheckIn.weekStart.slice(0, 10)}</strong></span>
                      <span><b>Estado:</b><strong>Revisado</strong></span>
                      <span><b>Coach:</b><strong>Jorge Carrillo</strong></span>
                    </div>
                  </section>

                  <section className="checkin-detail-metrics">
                    {[
                      { label: "Peso promedio", value: `${averageTrackingField(selectedHistoryDailyRows, "weightKg")} kg`, tone: "blue" },
                      { label: "Promedio de sueño", value: `${averageTrackingField(selectedHistoryDailyRows, "sleepHours")} h`, tone: "teal" },
                      { label: "Promedio de agua", value: `${averageTrackingField(selectedHistoryDailyRows, "waterLiters")} L`, tone: "orange" },
                      { label: "Pasos promedio", value: Number(averageTrackingField(selectedHistoryDailyRows, "steps")).toLocaleString("es-PE"), tone: "red" },
                    ].map((metric) => (
                      <article key={metric.label} className={`checkin-detail-metric checkin-detail-metric--${metric.tone}`}>
                        <span aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /></svg></span>
                        <div><p>{metric.label}</p><strong>{metric.value.includes("NaN") ? "-" : metric.value}</strong></div>
                      </article>
                    ))}
                  </section>

                  <section className="checkin-detail-grid">
                    <article className="checkin-detail-card checkin-detail-photos">
                      <h2>1. Fotos registradas</h2>
                      <div className="checkin-photo-grid">
                        {PROGRESS_PHOTO_SLOTS.filter(
                          (slot) =>
                            REQUIRED_PROGRESS_PHOTO_SLOTS.includes(slot) ||
                            selectedHistoryCheckIn.progressPhotos.some((item) => item.type === slot),
                        ).map((type) => {
                          const photo = selectedHistoryCheckIn.progressPhotos.find((item) => item.type === type);
                          const photoTitle = progressPhotoLabel(type);
                          const historyPhotos = selectedHistoryCheckIn.progressPhotos.map((item) => ({
                            title: progressPhotoLabel(item.type),
                            subtitle: `Semana ${selectedHistoryCheckIn.weekStart.slice(0, 10)}`,
                            src: resolveProgressPhotoUrl(item.type, item.imageUrl),
                          }));
                          return (
                            <figure key={type}>
                              <strong>{photoTitle}</strong>
                              {photo ? (
                                <img
                                  src={resolveProgressPhotoUrl(type, photo.imageUrl)}
                                  alt={`Foto ${type}`}
                                  onClick={() => openPhotoViewer(historyPhotos, historyPhotos.findIndex((item) => item.title === photoTitle))}
                                  onError={(event) => {
                                    event.currentTarget.onerror = null;
                                    event.currentTarget.src = buildProgressPhotoFallback(type);
                                  }}
                                />
                              ) : (
                                <div className="checkin-photo-empty">Sin foto</div>
                              )}
                            </figure>
                          );
                        })}
                      </div>
                    </article>

                    <article className="checkin-detail-card">
                      <h2>2. Respuestas diarias registradas</h2>
                      {renderWeeklyTrackingTable(selectedHistoryDailyRows, undefined, selectedHistoryWeightVariation)}
                    </article>
                  </section>
                </>
              )}
            </div>
          ) : (
          <>
          <section className="history-hero">
            <div>
              <p>Historial y evolución</p>
              <h1>Historial de seguimientos</h1>
              <span>Revisa tus semanas registradas, avances y observaciones del coach en una sola vista.</span>
            </div>
            <div className="history-hero-art" aria-hidden="true">
              <span className="history-calendar">
                <i />
                <b />
                <b />
                <b />
                <b />
              </span>
              <span className="history-clipboard">
                <i />
                <b />
                <b />
                <b />
              </span>
              <span className="history-chart-card">
                <svg viewBox="0 0 130 76"><path d="M10 58h112" /><path d="M16 52 38 40l20 8 20-28 28-10" /><circle cx="16" cy="52" r="4" /><circle cx="38" cy="40" r="4" /><circle cx="58" cy="48" r="4" /><circle cx="78" cy="20" r="4" /><circle cx="106" cy="10" r="4" /></svg>
              </span>
              <span className="history-dumbbell history-dumbbell--left" />
              <span className="history-dumbbell history-dumbbell--right" />
            </div>
          </section>

          <section className="history-layout">
            <div className="history-main">
              <article className="history-card history-weeks-card">
                <h2>
                  <span className="history-weeks-heading-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </span>
                  Semanas registradas
                </h2>
                {historyRows.length > 0 ? (
                  <div className="history-week-list">
                    {historyRows.map((row) => (
                      <section key={row.id} className={`history-week-row${selectedHistoryId === row.id ? " is-selected" : ""}`}>
                        <span className="history-week-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
                        </span>
                        <div className="history-week-title">
                          <strong>{row.week}</strong>
                          <p>{row.range}</p>
                        </div>
                        <div className="history-week-metrics" aria-label="Resumen de la semana">
                          <span className="history-week-metric history-week-metric--water">Promedio de agua<b>{row.waterAverage === "-" ? "-" : `${row.waterAverage} L`}</b></span>
                          <span className="history-week-metric history-week-metric--sleep">Promedio de sueño<b>{row.sleepAverage === "-" ? "-" : `${row.sleepAverage} h`}</b></span>
                          <span className="history-week-metric history-week-metric--steps">Promedio de pasos<b>{row.stepsAverage === "-" ? "-" : Number(row.stepsAverage).toLocaleString("es-PE")}</b></span>
                          <span className="history-week-metric history-week-metric--weight">Peso promedio<b>{row.weightAverage === "-" ? "-" : `${row.weightAverage} kg`}</b></span>
                          <span className="history-week-metric history-week-metric--variation">Variación de peso<b>{row.weightVariation === "-" ? "-" : `${row.weightVariation} kg`}</b></span>
                        </div>
                        <span className={`history-status history-status--${row.statusTone}`}>{row.status}</span>
                        {row.validatedAt ? (
                          <span className="history-validated" title={`Validado el ${new Date(row.validatedAt).toLocaleDateString("es-PE")}`}>
                            ✓ Validado
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="history-detail-btn"
                          aria-label={`Ver detalle de ${row.week}`}
                          onClick={() => {
                            setSelectedProgressCheckInId(row.id);
                            setSelectedCheckInDetailId(row.id);
                            setSelectedHistoryId(null);
                            changeTab("tracking");
                          }}
                        >
                          Detalle <span>›</span>
                        </button>
                      </section>
                    ))}
                  </div>
                ) : (
                  <div className="history-empty">
                    <strong>Sin semanas registradas</strong>
                    <p>Cuando guardes tu primer seguimiento semanal, aparecerá aquí con sus métricas y observaciones.</p>
                  </div>
                )}
              </article>

              {(() => {
                if (!selectedHistoryCheckIn) return null;
                const detail = selectedHistoryCheckIn as WeeklyCheckIn;
                return (
              <article className="history-card history-detail-card">
                <h2>{selectedHistoryRow ? `Detalle de ${selectedHistoryRow.week}` : "Detalle de semana"}</h2>
                <>
                    <div className="history-detail-overview">
                      <span>Semana<strong>{detail.weekStart.slice(0, 10)}</strong></span>
                      <span>Peso<strong>{selectedHistoryRow?.weight ? `${selectedHistoryRow.weight} kg` : "-"}</strong></span>
                      <span>Agua<strong>{detail.waterLiters} L</strong></span>
                      <span>Dieta<strong>{detail.dietCompliance}%</strong></span>
                      <span>Entrenamiento<strong>{detail.trainingCompliance}%</strong></span>
                    </div>

                    {renderWeeklyTrackingTable(selectedHistoryDailyRows, undefined, selectedHistoryWeightVariation)}

                    {selectedHistoryMeasurements ? (
                      <div className="history-detail-measures">
                        {[
                          ["Pliegue abdominal", selectedHistoryMeasurements.abdominalFoldMm, "mm"],
                          ["Cintura (relajado)", selectedHistoryMeasurements.waistRelaxedCm, "cm"],
                          ["Cintura (apretado)", selectedHistoryMeasurements.waistContractedCm, "cm"],
                          ["Cadera", selectedHistoryMeasurements.hipsRollCm, "cm"],
                          ["Brazo flexionado", selectedHistoryMeasurements.flexedArmCm, "cm"],
                          ["Muslo", selectedHistoryMeasurements.upperThighCm, "cm"],
                          ["Cuádriceps", selectedHistoryMeasurements.quadCm, "cm"],
                          ...(selectedHistoryMeasurements.glutesCm
                            ? [["Glúteo", selectedHistoryMeasurements.glutesCm, "cm"] as [string, number | undefined, string]]
                            : []),
                        ].map(([label, value, unit]) => (
                          <span key={String(label)}>{label}<strong>{value ? `${value} ${unit}` : "-"}</strong></span>
                        ))}
                      </div>
                    ) : null}

                    {selectedHistoryAnswers.length > 0 ? (
                      <div className="history-detail-answers">
                        {selectedHistoryAnswers.map(([label, value]) => (
                          <div key={label}>
                            <strong>{label}</strong>
                            <p>{value}</p>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {detail.progressPhotos.length > 0 ? (
                      <div className="history-detail-photos">
                        {detail.progressPhotos.map((photo) => (
                          <figure key={`${photo.type}-${photo.imageUrl}`}>
                            <img
                              src={resolveProgressPhotoUrl(photo.type, photo.imageUrl)}
                              alt={`Foto ${photo.type}`}
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = buildProgressPhotoFallback(photo.type);
                              }}
                            />
                            <figcaption>{progressPhotoLabel(photo.type)}</figcaption>
                          </figure>
                        ))}
                      </div>
                    ) : null}
                </>
              </article>
                );
              })()}

              {false && <article className="history-card history-summary-card">
                <h2>Resumen por semana</h2>
                <div className="history-table-wrap">
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Semana</th>
                        <th>Fecha</th>
                        <th>Peso</th>
                        <th>Cumplimiento</th>
                        <th>Estado</th>
                        <th>Coach</th>
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyRows.length > 0 ? (
                        historyRows.slice(0, 5).map((row) => (
                          <tr key={`table-${row.id}`}>
                            <td>{row.week.replace("Semana ", "")}</td>
                            <td>{row.range}</td>
                            <td>
                              {row.weight ? `${row.weight} kg` : "-"}
                              <span className={row.delta === null ? "" : row.delta <= 0 ? "down" : "up"}>
                                {row.delta === null ? "" : `${row.delta > 0 ? "+" : ""}${row.delta}`}
                              </span>
                            </td>
                            <td>
                              <b>{row.adherence}%</b>
                              <i style={{ width: `${row.adherence}%` }} />
                            </td>
                            <td><span className={`history-status history-status--${row.statusTone}`}>{row.status}</span></td>
                            <td><span className="history-coach-chip">JC</span> Jorge Carrillo</td>
                            <td><button type="button">Abrir</button></td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7}>Sin historial registrado.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {historyRows.length > 5 ? <button type="button" className="history-more-btn">Ver más semanas⌄</button> : null}
              </article>}
            </div>

            {false && <aside className="history-side">
              <article className="history-card history-chart-panel">
                <header>
                  <h2>Evolución de peso <small>(kg)</small></h2>
                  <button type="button">Exportar historial</button>
                </header>
                {historyWeightChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={170}>
                    <LineChart data={historyWeightChartData} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="#eef2f7" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} domain={["dataMin - 1", "dataMax + 1"]} />
                      <Tooltip />
                      <Line type="monotone" dataKey="peso" stroke="#ff4f0f" strokeWidth={3} dot={{ r: 4, fill: "#ff4f0f" }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="history-chart-empty">Sin datos de peso registrados.</div>
                )}
              </article>

              <article className="history-card history-distribution-panel">
                <h2>Distribución de estados</h2>
                <div className="history-distribution-grid">
                  <ResponsiveContainer width="100%" height={132}>
                    <PieChart>
                      <Pie data={historyDistributionData.filter((item) => item.value > 0)} dataKey="value" nameKey="name" innerRadius={38} outerRadius={58}>
                        {historyDistributionData.map((item) => <Cell key={item.name} fill={item.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="history-legend">
                    {historyDistributionData.map((item) => (
                      <div key={item.name}>
                        <span style={{ background: item.color }} />
                        <p>{item.name}</p>
                        <strong>{item.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </article>

            </aside>}
          </section>
          </>
          )}
        </div>
      )}

      {false && (
        <div className="messages-page">
          <section className="messages-hero">
            <div>
              <p>Mensajes y recomendaciones</p>
              <h1>Habla con tu coach</h1>
              <span>Recibe indicaciones, resuelve dudas y mantén tu seguimiento en una conversación organizada.</span>
            </div>
            <div className="messages-hero-art" aria-hidden="true">
              <span className="messages-bubble messages-bubble--front">
                <i />
                <b />
                <b />
              </span>
              <span className="messages-bubble messages-bubble--back">
                <b />
                <b />
              </span>
              <span className="messages-check">✓</span>
            </div>
          </section>

          <section className="messages-stats">
            {[
              { label: "Mensajes nuevos", value: "3", tone: "orange", icon: "chat" },
              { label: "Recomendaciones", value: String(Math.max(approvedRecs.length, 2)), tone: "purple", icon: "star" },
              { label: "Archivos compartidos", value: "5", tone: "blue", icon: "folder" },
              { label: "Última respuesta", value: "Hoy, 10:24 AM", tone: "green", icon: "clock" },
            ].map((stat) => (
              <article key={stat.label} className={`messages-stat messages-stat--${stat.tone}`}>
                <span aria-hidden="true">
                  {stat.icon === "chat" ? (
                    <svg viewBox="0 0 24 24"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></svg>
                  ) : stat.icon === "star" ? (
                    <svg viewBox="0 0 24 24"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2 6.4 20.2 7.5 14 3 9.6l6.2-.9z" /></svg>
                  ) : stat.icon === "folder" ? (
                    <svg viewBox="0 0 24 24"><path d="M3 6h7l2 2h9v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l4 2" /></svg>
                  )}
                </span>
                <div>
                  <p>{stat.label}</p>
                  <strong>{stat.value}</strong>
                </div>
              </article>
            ))}
          </section>

          <section className="messages-layout">
            <aside className="messages-inbox">
              <div className="messages-search-row">
                <label>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
                  <input placeholder="Buscar conversación o recomendación..." />
                </label>
                <button type="button" aria-label="Filtros">
                  <svg viewBox="0 0 24 24"><path d="M4 7h10M18 7h2M4 17h2M10 17h10" /><circle cx="16" cy="7" r="2" /><circle cx="8" cy="17" r="2" /></svg>
                </button>
              </div>
              <div className="messages-filter-row">
                {["Todos", "No leidos", "Recomendaciones", "Archivos"].map((item, index) => (
                  <button key={item} type="button" className={index === 0 ? "active" : ""}>{item}</button>
                ))}
              </div>
              <div className="messages-thread-list">
                {[
                  { title: "Coach Jorge Carrillo", preview: "Revisa tus comidas fuera del plan esta semana...", time: "10:24 AM", tone: "photo", badge: "Nuevo", active: true },
                  { title: "Recomendación aprobada", preview: "Aumenta 500 ml de agua en días de cardio", time: "Ayer", tone: "star", badge: "Coach" },
                  { title: "Plan nutricional", preview: "Te deje ajustes para la merienda y la cena", time: "Lun 9:15 AM", tone: "apple" },
                  { title: "Seguimiento semanal", preview: "Buen progreso, revisa las observaciones", time: "Dom 8:30 PM", tone: "chart" },
                  { title: "Archivo compartido", preview: "Plan de entrenamiento - Semana 4", time: "Vie 6:45 PM", tone: "file", badge: "1" },
                ].map((thread) => (
                  <article key={thread.title} className={`messages-thread ${thread.active ? "active" : ""}`}>
                    <span className={`messages-thread-icon messages-thread-icon--${thread.tone}`} aria-hidden="true">
                      {thread.tone === "photo" ? "JC" : thread.tone === "star" ? "☆" : thread.tone === "apple" ? "♧" : thread.tone === "chart" ? "▥" : "□"}
                    </span>
                    <div>
                      <strong>{thread.title}</strong>
                      <p>{thread.preview}</p>
                    </div>
                    <aside>
                      <small>{thread.time}</small>
                      {thread.badge ? <b>{thread.badge}</b> : null}
                    </aside>
                  </article>
                ))}
              </div>
            </aside>

            <article className="messages-chat">
              <header className="messages-chat-head">
                <div className="messages-coach-avatar">
                  <span>JC</span>
                  <i />
                </div>
                <div>
                  <h2>Coach Jorge Carrillo</h2>
                  <p>En línea</p>
                </div>
                <nav aria-label="Acciones del chat">
                  {["call", "video", "info", "more"].map((icon) => (
                    <button key={icon} type="button" aria-label={icon}>
                      {icon === "call" ? (
                        <svg viewBox="0 0 24 24"><path d="M22 16.9v3a2 2 0 0 1-2.2 2A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7l.4 2.5a2 2 0 0 1-.6 1.8L7.7 9.2a16 16 0 0 0 7.1 7.1l1.2-1.2a2 2 0 0 1 1.8-.6l2.5.4a2 2 0 0 1 1.7 2z" /></svg>
                      ) : icon === "video" ? (
                        <svg viewBox="0 0 24 24"><rect x="3" y="6" width="12" height="12" rx="2" /><path d="m15 10 5-3v10l-5-3z" /></svg>
                      ) : icon === "info" ? (
                        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
                      ) : (
                        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></svg>
                      )}
                    </button>
                  ))}
                </nav>
              </header>

              <div className="messages-featured-rec">
                <span>☆</span>
                <div>
                  <strong>Recomendación destacada</strong>
                  <p>Mantén proteína en cada comida principal y sube tu hidratación a 3.5 L en días de entrenamiento de pierna.</p>
                </div>
                <b>Aprobada</b>
                <button type="button" aria-label="Fijar recomendación">⌑</button>
              </div>

              <div className="messages-chat-body">
                {[
                  { side: "coach", text: "Hola Yoel, revise tu check-in semanal y en general vas bien.", time: "10:05 AM" },
                  { side: "coach", text: "Necesitamos mejorar un poco la hidratación y cuidar las comidas fuera del plan.", time: "10:06 AM" },
                  { side: "me", text: "Perfecto coach, esta semana lo ajusto. ¿Mantengo las mismas calorias?", time: "10:07 AM" },
                  { side: "coach", text: "Sí, mantén 2450 kcal. También aumenta el agua a 3.5 L los días de cardio.", time: "10:08 AM" },
                  { side: "coach", text: "Te deje una recomendación aprobada arriba para que la tengas visible.", time: "10:08 AM" },
                  { side: "me", text: "Entendido, gracias. También queria preguntarte si puedo cambiar la merienda por yogurt griego con fruta.", time: "10:10 AM" },
                  { side: "coach", text: "Sí, puedes hacerlo. Mantén una fuente de proteína y controla la porción de granola.", time: "10:12 AM" },
                ].map((message, index) => (
                  <div key={`${message.time}-${index}`} className={`messages-chat-row messages-chat-row--${message.side}`}>
                    {message.side === "coach" ? <span className="messages-mini-avatar">JC</span> : null}
                    <div>
                      <p>{message.text}</p>
                      <small>{message.time}{message.side === "me" ? " ✓✓" : ""}</small>
                    </div>
                    {message.side === "me" ? <span className="messages-user-dot">Y</span> : null}
                  </div>
                ))}
              </div>

              <div className="messages-quick-actions">
                {["Ver plan", "Adjuntar foto", "Enviar check-in"].map((action) => (
                  <button key={action} type="button">{action}</button>
                ))}
              </div>
              <footer className="messages-composer">
                <button type="button" aria-label="Adjuntar archivo">⌕</button>
                <input placeholder="Escribe un mensaje para tu coach..." />
                <button type="button" aria-label="Emoji">☺</button>
                <button type="button" className="messages-send">Enviar</button>
              </footer>
            </article>

            <aside className="messages-side">
              <article className="messages-side-card">
                <header>
                  <h3><span>☆</span> Recomendaciones recientes</h3>
                  <button type="button">Ver todas</button>
                </header>
                {[
                  { title: "Hidratación en cardio", copy: "Aumenta 500 ml de agua en días de cardio.", tone: "green" },
                  { title: "Proteína en cada comida", copy: "Mantén proteína en cada comida principal.", tone: "orange" },
                  { title: "Objetivo de agua", copy: "Meta diaria: 3.5 L en días de entrenamiento de pierna.", tone: "blue" },
                ].map((item) => (
                  <div key={item.title} className="messages-side-row">
                    <span className={`messages-side-icon messages-side-icon--${item.tone}`}>✓</span>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.copy}</p>
                    </div>
                    <b>Aprobada</b>
                  </div>
                ))}
              </article>

              <article className="messages-side-card">
                <header>
                  <h3><span>□</span> Archivos compartidos</h3>
                  <button type="button">Ver todos</button>
                </header>
                {[
                  { title: "Plan nutricional - Semana 4", meta: "PDF · 1.2 MB · Ayer", tone: "green" },
                  { title: "Tabla de porciones", meta: "XLSX · 85 KB · Lun", tone: "orange" },
                  { title: "Guia de snacks saludables", meta: "PDF · 1.8 MB · Vie", tone: "red" },
                ].map((file) => (
                  <div key={file.title} className="messages-file-row">
                    <span className={`messages-file-icon messages-file-icon--${file.tone}`}>□</span>
                    <div>
                      <strong>{file.title}</strong>
                      <p>{file.meta}</p>
                    </div>
                    <button type="button" aria-label={`Descargar ${file.title}`}>⌄</button>
                  </div>
                ))}
              </article>
            </aside>
          </section>
        </div>
      )}

      {activeTab === "assessment" && (
        data?.initialAssessment ? (
          <div className="routine-summary-page">
            <section className="routine-summary-hero">
              <div>
                <span className="routine-summary-eyebrow">Cuestionario inicial</span>
                <h1>Cuestionario inicial</h1>
                <p>
                  Resumen organizado de tu experiencia, hábitos, alimentación y antecedentes para que tu coach diseñe tu plan.
                </p>
              </div>
            </section>

            <section className="routine-summary-grid">
              {assessmentSummaryItems.map((item, index) => (
                <article key={item.id} className={`routine-summary-card routine-summary-card--${item.tone}`}>
                  <span className="routine-summary-card-icon" aria-hidden="true">
                    {renderAssessmentSummaryIcon(item.icon)}
                  </span>
                  <div>
                    <h2>{index + 1}. {item.title}</h2>
                    {item.answers.length > 0 ? (
                      <dl className="routine-summary-answers">
                        {item.answers.map((answer) => (
                          <div key={`${item.id}-${answer.question}`} className="routine-summary-answer">
                            <dt>{answer.question}</dt>
                            <dd>{answer.answer}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <p className="routine-summary-empty">Sin respuestas registradas.</p>
                    )}
                  </div>
                </article>
              ))}
            </section>
          </div>
        ) : (
          <form className="routine-page" onSubmit={handleAssessmentSubmit}>
            <section className="routine-hero">
              <div className="routine-hero-copy">
                <p>Registro inicial</p>
                <h1>Completa tu registro inicial</h1>
                <span>Esta información nos ayuda a conocerte mejor para diseñar un plan personalizado que se adapte a tus objetivos y estilo de vida.</span>
              </div>
              <div className="routine-hero-art" aria-hidden="true">
                <svg viewBox="0 0 180 140">
                  <rect x="50" y="20" width="78" height="100" rx="10" />
                  <path d="M65 46l7 7 13-16M65 72l7 7 13-16M65 98l7 7 13-16" />
                  <path d="M93 48h22M93 74h20M93 100h22" />
                  <path d="M26 98h52M102 98h52M42 78v40M138 78v40" />
                  <circle cx="42" cy="78" r="8" /><circle cx="138" cy="78" r="8" />
                </svg>
              </div>
            </section>

            <section className="routine-status-strip">
              <article className="routine-status-card routine-status-card--orange">
                <span className="routine-status-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M8 4h11v16H5V7z" /><path d="M8 4v3H5" /><path d="m9 13 2 2 4-5" /></svg>
                </span>
                <div>
                  <strong>Secciones del registro</strong>
                  <p>{assessmentRoutineSections.length} secciones</p>
                </div>
              </article>
              <article className="routine-status-card routine-status-card--green">
                <span className="routine-status-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" /><path d="m9 12 2 2 4-5" /></svg>
                </span>
                <div>
                  <strong>Estado</strong>
                  <p>{assessmentProgressPercent === 100 ? "Completado" : "En progreso"}</p>
                </div>
              </article>
              <article className="routine-status-card routine-status-card--progress">
                <span className="routine-progress-ring" style={{ background: `conic-gradient(#ff5a14 ${assessmentProgressPercent * 3.6}deg, #e7ebf0 0deg)` }}>
                  <b>{assessmentProgressPercent}%</b>
                </span>
                <div>
                  <strong>Progreso general</strong>
                  <p>{assessmentCompletedSections} de {assessmentRoutineSections.length} completadas</p>
                </div>
              </article>
              <article className="routine-status-card routine-status-card--blue">
                <span className="routine-status-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
                </span>
                <div>
                  <strong>Última actualización</strong>
                  <p>Aún no guardado</p>
                </div>
              </article>
            </section>

            <section className="routine-section-grid">
              {assessmentRoutineSections.map((section, index) => (
                <button
                  key={section.id}
                  type="button"
                  className={`routine-section-card routine-section-card--${section.tone}${activeAssessmentSection?.id === section.id ? " is-active" : ""}`}
                  onClick={() => {
                    shouldScrollAssessmentFormRef.current = activeAssessmentSection?.id !== section.id;
                    setActiveAssessmentSectionId((current) => (current === section.id ? null : section.id));
                  }}
                  aria-expanded={activeAssessmentSection?.id === section.id}
                >
                  <span className="routine-section-number">{index + 1}</span>
                  <span className="routine-section-icon" aria-hidden="true">{renderRoutineSectionIcon(section.icon)}</span>
                  <span className="routine-section-copy">
                    <strong>{section.title}</strong>
                    <small>{section.copy}</small>
                  </span>
                  <span className="routine-section-count">{section.keys.length} campos</span>
                  <span className="routine-section-chevron" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
                  </span>
                </button>
              ))}
            </section>

            {activeAssessmentSection ? (
              <div className="routine-modal" role="dialog" aria-modal="true">
              <div className="routine-modal-backdrop" onClick={() => setActiveAssessmentSectionId(null)} />
              <section ref={activeAssessmentCardRef} className={`routine-active-card routine-active-card--${activeAssessmentSection.tone}`}>
                <div className="routine-active-head">
                  <span className="routine-section-number">{assessmentRoutineSections.findIndex((section) => section.id === activeAssessmentSection.id) + 1}</span>
                  <span className="routine-section-icon" aria-hidden="true">{renderRoutineSectionIcon(activeAssessmentSection.icon)}</span>
                  <div>
                    <h2>{activeAssessmentSection.title}</h2>
                    <p>{activeAssessmentSection.copy}</p>
                  </div>
                  <button
                    type="button"
                    className="routine-collapse-btn"
                    aria-label="Cerrar sección"
                    onClick={() => setActiveAssessmentSectionId(null)}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                  </button>
                </div>

                <div className="routine-active-grid">
                  {activeAssessmentSection.keys.map((key) => {
                    const question = questionMap[key];
                    return (
                      <label
                        key={question.key}
                        className={`routine-field routine-field--${getAssessmentQuestionTone(question.key)}${
                          question.inputType === "textarea" || question.layout === "full" ? " routine-field--textarea" : ""
                        }${question.inputType === "checkbox" ? " routine-field--wide routine-field--compact-checkbox" : ""}`}
                      >
                        <span className="routine-field-icon" aria-hidden="true">
                          {renderAssessmentQuestionIcon(question.key)}
                        </span>
                        <span className="routine-field-label">{formatAssessmentQuestionLabel(question.label)}</span>
                        {question.helper ? <small>{question.helper}</small> : <small aria-hidden="true">&nbsp;</small>}
                        {renderAssessmentField(question, assessmentForm[question.key], (nextValue) => {
                          setAssessmentError("");
                          setAssessmentForm({
                            ...assessmentForm,
                            [question.key]: nextValue,
                          });
                        })}
                      </label>
                    );
                  })}
                </div>
              </section>
              </div>
            ) : null}

            {assessmentError ? <div className="error-banner">{assessmentError}</div> : null}

            <div className="client-form-actions checkin-actions">
              <button type="button" className="checkin-action-button checkin-action-button--back" onClick={() => changeTab("dashboard")}>
                <span className="checkin-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></svg>
                </span>
                Volver
              </button>
              <button type="button" className="checkin-action-button checkin-action-button--draft" onClick={handleAssessmentDraftSave}>
                <span className="checkin-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8" /><path d="M7 3v5h8" /></svg>
                </span>
                Guardar borrador
              </button>
              <button className="checkin-action-button checkin-action-button--submit" type="submit" disabled={savingAssessment}>
                <span className="checkin-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M9 12l2 2 4-5" /><circle cx="12" cy="12" r="9" /></svg>
                </span>
                {savingAssessment ? "Guardando..." : "Guardar registro inicial"}
              </button>
            </div>
          </form>
        )
      )}

      {activeTab === "tracking" && (
        <div className="progress-page">
          <section className="progress-hero">
            <div>
              <p className="progress-hero-eyebrow">Mi progreso</p>
              <h1>Visualiza tu progreso</h1>
              <p>Revisa tu evolución diaria, tus hábitos y los datos consolidados de tu seguimiento semanal.</p>
            </div>
            <svg className="progress-hero-art" viewBox="0 0 220 170" aria-hidden="true">
              <rect x="82" y="28" width="72" height="102" rx="12" />
              <path d="M100 58l10 10 18-24M100 88l10 10 18-24M100 118l10 10 18-24" />
              <path d="M132 59h22M132 89h20M132 119h22" />
              <path d="M42 104h48M150 104h48M57 80v50M183 80v50" />
              <circle cx="57" cy="80" r="9" />
              <circle cx="183" cy="80" r="9" />
            </svg>
          </section>

          <section className="progress-controls">
            <div className="progress-week-card">
              <span className="progress-control-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
              </span>
              <div>
                <span>Semana activa</span>
                {savedWeekOptions.length > 0 ? (
                  <select
                    className="progress-week-select"
                    value={selectedProgressCheckIn?.id ?? ""}
                    onChange={(event) => setSelectedProgressCheckInId(event.target.value)}
                    aria-label="Seleccionar semana guardada"
                  >
                    {savedWeekOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <strong>{progressWeekStart}</strong>
                )}
              </div>
              {savedWeekOptions.length === 0 ? <span className="progress-chevron" aria-hidden="true">⌄</span> : null}
            </div>
            <div className="progress-readonly-card">
              <span className="progress-control-icon progress-control-icon--blue" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="6" y="10" width="12" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>
              </span>
              <div>
                <strong>Vista de solo lectura</strong>
                <p>Esta información se sincroniza automaticamente desde tu último seguimiento semanal guardado.</p>
              </div>
            </div>
          </section>

          <section className="progress-metrics">
            {[
              { label: "Promedio de agua", value: progressWaterAverage === "-" ? "-" : `${progressWaterAverage} L`, delta: "vs. semana anterior", tone: "blue", icon: "drop" },
              { label: "Promedio de sueño", value: progressSleepAverage === "-" ? "-" : `${progressSleepAverage} h`, delta: "vs. semana anterior", tone: "purple", icon: "moon" },
              { label: "Promedio de pasos", value: progressStepsAverage === "-" ? "-" : Number(progressStepsAverage).toLocaleString("es-PE"), delta: "vs. semana anterior", tone: "green", icon: "shoe" },
              { label: "Peso promedio", value: progressWeightAverage === "-" ? "-" : `${progressWeightAverage} kg`, delta: "vs. semana anterior", tone: "orange", icon: "scale" },
              { label: "Variación de peso", value: progressVariation === "-" ? "-" : `${progressVariation} kg`, delta: "vs. semana anterior", tone: "amber", icon: "trend" },
            ].map((metric) => (
              <article key={metric.label} className={`progress-metric progress-metric--${metric.tone}`}>
                <span className="progress-metric-icon" aria-hidden="true">
                  {metric.icon === "drop" ? (
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.5c-.4 0-.8.2-1 .5C9.6 4.7 5 10.4 5 14.5c0 3.9 3.1 7 7 7s7-3.1 7-7c0-4.1-4.6-9.8-6-11.5-.2-.3-.6-.5-1-.5z"/></svg>
                  ) : metric.icon === "moon" ? (
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 15.3c-.2-.3-.6-.4-.9-.3-1 .3-2 .4-3 .3-3.7-.4-6.7-3.4-7.1-7.1-.1-1 0-2 .3-3 .1-.3 0-.7-.3-.9-.3-.2-.7-.2-1 0C5.5 5.9 3.5 9.2 3.5 13c0 5.2 4.3 9.5 9.5 9.5 3.8 0 7.1-2 8.7-5 .2-.3.2-.7-.2-1z"/><circle cx="18" cy="5" r="1.5"/><circle cx="20.5" cy="9" r="1"/></svg>
                  ) : metric.icon === "shoe" ? (
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 16.5c0 1.4 1.1 2.5 2.5 2.5h13c1.9 0 3.5-1.6 3.5-3.5 0-.8-.3-1.5-.8-2.1l-6.7-7c-.5-.5-1.2-.8-1.9-.8-1 0-1.9.6-2.3 1.5-.8 1.7-2 4.1-3.6 5.4-.9.7-2.1 1.3-3.1 1.6-.4.1-.6.5-.6.9v1.5z"/><circle cx="6.5" cy="16" r=".8" fill="#fff"/><circle cx="9" cy="16" r=".8" fill="#fff"/><circle cx="11.5" cy="16" r=".8" fill="#fff"/></svg>
                  ) : metric.icon === "scale" ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="3"/><path d="M8 9a4 4 0 0 1 8 0"/><line x1="12" y1="9" x2="10.5" y2="11"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 8-9"/><path d="M14 6h7v7"/></svg>
                  )}
                </span>
                <div>
                  <p>{metric.label}</p>
                  <strong>{metric.value}</strong>
                  <span>↑ {metric.delta}</span>
                </div>
              </article>
            ))}
          </section>

          <section className="progress-board">
            <div className="progress-table-panel">
              <div className="progress-panel-head">
                <span className="progress-section-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
                </span>
                <div>
                  <h3>Registro diario de la semana</h3>
                  <p>Datos consolidados desde tu último seguimiento semanal.</p>
                </div>
              </div>
              {renderWeeklyTrackingTable(progressRows, "Aún no hay registros guardados para este cliente.", progressVariation)}
            </div>
          </section>

          <section className="progress-measures-panel">
            <div className="progress-panel-head">
              <span className="progress-section-icon progress-section-icon--measures" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="m21.3 15.3-6 6a1 1 0 0 1-1.4 0l-12-12a1 1 0 0 1 0-1.4l6-6a1 1 0 0 1 1.4 0l12 12a1 1 0 0 1 0 1.4Z" /><path d="M7 7h.01M10 10h.01M13 13h.01M16 16h.01" /></svg>
              </span>
              <div>
                <h3>Medidas corporales del check-in</h3>
                <p>Medidas solicitadas en tu seguimiento semanal para comparar cambios reales.</p>
              </div>
            </div>
            <div className="progress-measures-table-wrap">
              <table className="progress-measures-table">
                <thead>
                  <tr>
                    <th>Semana</th>
                    <th>Pliegue abdominal</th>
                    <th>Cintura (relajado)</th>
                    <th>Cintura (apretado)</th>
                    <th>Cadera</th>
                    <th>Brazo flexionado</th>
                    <th>Muslo</th>
                    <th>Cuádriceps</th>
                    {isFemale ? <th>Glúteo</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {selectedProgressCheckIn?.bodyMeasurements?.[0] ? (
                    <tr>
                      <td><strong>{selectedProgressCheckIn.weekStart.slice(0, 10)}</strong></td>
                      <td>{selectedProgressCheckIn.bodyMeasurements[0].abdominalFoldMm ? `${selectedProgressCheckIn.bodyMeasurements[0].abdominalFoldMm} mm` : "-"}</td>
                      <td>{selectedProgressCheckIn.bodyMeasurements[0].waistRelaxedCm ? `${selectedProgressCheckIn.bodyMeasurements[0].waistRelaxedCm} cm` : "-"}</td>
                      <td>{selectedProgressCheckIn.bodyMeasurements[0].waistContractedCm ? `${selectedProgressCheckIn.bodyMeasurements[0].waistContractedCm} cm` : "-"}</td>
                      <td>{selectedProgressCheckIn.bodyMeasurements[0].hipsRollCm ? `${selectedProgressCheckIn.bodyMeasurements[0].hipsRollCm} cm` : "-"}</td>
                      <td>{selectedProgressCheckIn.bodyMeasurements[0].flexedArmCm ? `${selectedProgressCheckIn.bodyMeasurements[0].flexedArmCm} cm` : "-"}</td>
                      <td>{selectedProgressCheckIn.bodyMeasurements[0].upperThighCm ? `${selectedProgressCheckIn.bodyMeasurements[0].upperThighCm} cm` : "-"}</td>
                      <td>{selectedProgressCheckIn.bodyMeasurements[0].quadCm ? `${selectedProgressCheckIn.bodyMeasurements[0].quadCm} cm` : "-"}</td>
                      {isFemale ? <td>{selectedProgressCheckIn.bodyMeasurements[0].glutesCm ? `${selectedProgressCheckIn.bodyMeasurements[0].glutesCm} cm` : "-"}</td> : null}
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan={isFemale ? 9 : 8} className="progress-empty-cell">
                        No hay medidas corporales registradas para este check-in.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {activeTab === "checkin" && (
        <div className="checkin-shell">
          {loadingHistory ? (
            <div className="checkin-dashboard">
              <div className="checkin-header">
                <h2>Check-ins</h2>
                <p>Cargando tu información...</p>
              </div>
              <div className="checkin-metrics-grid">
                <div className="checkin-metric-card">
                  <div className="metric-icon">⏳</div>
                  <div className="metric-content">
                    <h3>Cargando</h3>
                    <p>Procesando datos</p>
                  </div>
                </div>
              </div>
            </div>
          ) : shouldShowCheckInDetail ? (
            <article className="checkin-detail-page">
              <div className="checkin-print-letterhead" aria-hidden="true">
                <img src="/logos/jc-logo.png" alt="JC Fitness" />
                <strong>{dashboardFullName}</strong>
              </div>
              <section className="checkin-detail-hero">
                <div>
                  <p className="checkin-detail-eyebrow">Check-ins</p>
                  <h1>Detalle del check-in semanal</h1>
                  <p>Revisa tus respuestas, fotos y resumen general de la semana.</p>
                </div>
                <div className="checkin-banner-actions">
                  <button type="button" onClick={() => setCheckInViewMode("new")}>
                    Registrar nueva semana
                  </button>
                  <label className="checkin-banner-select">
                    <span>Semana registrada</span>
                    <select
                      value={selectedCheckInDetail.id}
                      onChange={(event) => {
                        setSelectedCheckInDetailId(event.target.value);
                        setCheckInViewMode("detail");
                      }}
                    >
                      {savedWeekOptions.map((option) => (
                        <option key={`detail-banner-${option.id}`} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="checkin-detail-hero-meta">
                  <span>
                    <b>Semana</b>
                    {savedWeekOptions.length > 0 ? (
                      <select
                        className="checkin-detail-week-select"
                        value={selectedCheckInDetail.id}
                        onChange={(event) => setSelectedCheckInDetailId(event.target.value)}
                        aria-label="Seleccionar semana del check-in"
                      >
                        {savedWeekOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <strong>{selectedCheckInDetail.weekStart.slice(0, 10)}</strong>
                    )}
                  </span>
                  <span><b>Estado:</b><strong>Revisado</strong></span>
                  <span><b>Coach:</b><strong>Jorge Carrillo</strong></span>
                </div>
              </section>

              <section className="checkin-detail-metrics">
                {[
                  { label: "Peso promedio", value: `${averageTrackingField(selectedCheckInRows, "weightKg")} kg`, tone: "blue", icon: "scale" },
                  { label: "Promedio de sueño", value: `${averageTrackingField(selectedCheckInRows, "sleepHours")} h`, tone: "teal", icon: "moon" },
                  { label: "Promedio de agua", value: `${averageTrackingField(selectedCheckInRows, "waterLiters")} L`, tone: "orange", icon: "drop" },
                  { label: "Pasos promedio", value: Number(averageTrackingField(selectedCheckInRows, "steps")).toLocaleString("es-PE"), tone: "red", icon: "walk" },
                ].map((metric) => (
                  <article key={metric.label} className={`checkin-detail-metric checkin-detail-metric--${metric.tone}`}>
                    <span aria-hidden="true">
                      {metric.icon === "scale" ? (
                        <svg viewBox="0 0 24 24"><rect x="5" y="4" width="14" height="16" rx="3" /><path d="M9 10a3 3 0 0 1 6 0" /></svg>
                      ) : metric.icon === "moon" ? (
                        <svg viewBox="0 0 24 24"><path d="M20 15.5A8 8 0 1 1 8.5 4 6.5 6.5 0 0 0 20 15.5z" /></svg>
                      ) : metric.icon === "drop" ? (
                        <svg viewBox="0 0 24 24"><path d="M12 3c4 5.5 6 9 6 12a6 6 0 0 1-12 0c0-3 2-6.5 6-12z" /></svg>
                      ) : (
                        <svg viewBox="0 0 24 24"><path d="M13 4 7 14h5l-1 6 6-10h-5z" /></svg>
                      )}
                    </span>
                    <div>
                      <p>{metric.label}</p>
                      <strong>{metric.value.includes("NaN") ? "—" : metric.value}</strong>
                    </div>
                  </article>
                ))}
              </section>

              <section className="checkin-detail-grid">
                <article className="checkin-detail-card checkin-detail-photos">
                  <h2>
                    <span className="checkin-detail-heading-icon checkin-detail-heading-icon--photos" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="M7 7h2l1.5-2h3L15 7h2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3Z" /><circle cx="12" cy="13" r="3" /></svg>
                    </span>
                    1. Fotos registradas
                  </h2>
                  <div className="checkin-detail-photo-grid">
                    {PROGRESS_PHOTO_SLOTS.filter(
                      (slot) =>
                        REQUIRED_PROGRESS_PHOTO_SLOTS.includes(slot) ||
                        selectedCheckInDetail.progressPhotos.some((item) => item.type === slot),
                    ).map((type) => {
                      const photo = selectedCheckInDetail.progressPhotos.find((item) => item.type === type);
                      const photoTitle = progressPhotoLabel(type);
                      const detailPhotos = PROGRESS_PHOTO_SLOTS.filter(
                        (slot) =>
                          REQUIRED_PROGRESS_PHOTO_SLOTS.includes(slot) ||
                          selectedCheckInDetail.progressPhotos.some((item) => item.type === slot),
                      ).map((photoType) => {
                        const savedPhoto = selectedCheckInDetail.progressPhotos.find((item) => item.type === photoType);
                        return {
                          title: progressPhotoLabel(photoType),
                          subtitle: `Semana ${selectedCheckInDetail.weekStart.slice(0, 10)}`,
                          src: savedPhoto ? resolveProgressPhotoUrl(savedPhoto.type, savedPhoto.imageUrl) : buildProgressPhotoFallback(photoType),
                        };
                      });
                      return (
                        <div key={type} className="checkin-detail-photo">
                          <strong>{photoTitle}</strong>
                          <img
                            src={photo ? resolveProgressPhotoUrl(photo.type, photo.imageUrl) : buildProgressPhotoFallback(type)}
                            alt={`Foto de progreso ${type}`}
                            onClick={() => openPhotoViewer(detailPhotos, detailPhotos.findIndex((item) => item.title === photoTitle))}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = buildProgressPhotoFallback(type);
                            }}
                          />
                          <span>Registrada</span>
                        </div>
                      );
                    })}
                  </div>
                </article>

                <article className="checkin-detail-card checkin-detail-daily">
                  <h2>
                    <span className="checkin-detail-heading-icon checkin-detail-heading-icon--daily" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16M9 15l2 2 4-5" /></svg>
                    </span>
                    2. Respuestas diarias registradas
                  </h2>
                  {renderWeeklyTrackingTable(selectedCheckInRows, undefined, selectedCheckInWeightVariation)}
                </article>
              </section>

              <section className="checkin-detail-card checkin-detail-measures">
                <h2>
                  <span className="checkin-detail-heading-icon checkin-detail-heading-icon--measures" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="m21.3 15.3-6 6a1 1 0 0 1-1.4 0l-12-12a1 1 0 0 1 0-1.4l6-6a1 1 0 0 1 1.4 0l12 12a1 1 0 0 1 0 1.4Z" /><path d="m7.5 10.5 1 1M10.5 7.5l1 1M13.5 13.5l1 1M16.5 10.5l1 1" /></svg>
                  </span>
                  3. Medidas registradas
                </h2>
                {selectedCheckInDetail.bodyMeasurements[0] ? (
                  <div className="checkin-detail-measure-grid">
                    {([
                      ["Pliegue abdominal", selectedCheckInDetail.bodyMeasurements[0].abdominalFoldMm, "mm"],
                      ["Cintura (relajado)", selectedCheckInDetail.bodyMeasurements[0].waistRelaxedCm, "cm"],
                      ["Cintura (apretado)", selectedCheckInDetail.bodyMeasurements[0].waistContractedCm, "cm"],
                      ["Cadera", selectedCheckInDetail.bodyMeasurements[0].hipsRollCm, "cm"],
                      ["Brazo flexionado", selectedCheckInDetail.bodyMeasurements[0].flexedArmCm, "cm"],
                      ["Muslo", selectedCheckInDetail.bodyMeasurements[0].upperThighCm, "cm"],
                      ["Cuádriceps", selectedCheckInDetail.bodyMeasurements[0].quadCm, "cm"],
                      ["Glúteo", selectedCheckInDetail.bodyMeasurements[0].glutesCm, "cm"],
                    ] as Array<[string, number | undefined, string]>).filter(([, value]) => value !== undefined && value !== null).map(([label, value, unit]) => (
                      <article key={label} className={`checkin-detail-measure checkin-detail-measure--${getCheckInMeasureTone(label)}`}>
                        <span aria-hidden="true">
                          {renderCheckInMeasureIcon(label)}
                        </span>
                        <div>
                          <strong>{label}</strong>
                          <p>{value} {unit}</p>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="tracking-helper-text">No hay medidas registradas para esta semana.</p>
                )}
              </section>

              <section className="checkin-detail-card checkin-detail-answers">
                <h2>
                  <span className="checkin-detail-heading-icon checkin-detail-heading-icon--answers" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="M5 4h14v13H8l-3 3Z" /><path d="M9 8h6M9 12h4" /></svg>
                  </span>
                  4. Respuestas generales
                </h2>
                <div className="checkin-detail-answer-grid">
                  {selectedCheckInAnswers.length > 0 ? (
                    selectedCheckInAnswers.map(([label, value]) => (
                      <article key={label} className={`checkin-detail-answer checkin-detail-answer--${getCheckInAnswerTone(label)}`}>
                        <span aria-hidden="true">
                          {renderCheckInAnswerIcon(label)}
                        </span>
                        <div>
                          <strong>{label}</strong>
                          <p>{value}</p>
                        </div>
                      </article>
                    ))
                  ) : (
                    <p className="tracking-helper-text">No hay respuestas de resumen registradas para esta semana.</p>
                  )}
                </div>
              </section>

              <footer className="checkin-detail-actions">
                <button type="button" onClick={() => changeTab("history")}>← Volver al historial</button>
                {isCoachPreview ? (
                  <button
                    type="button"
                    onClick={() =>
                      printCheckInDocument(
                        buildCheckInPdfTitle(
                          dashboardFullName,
                          getCheckInWeekNumber(data?.checkIns ?? [], selectedCheckInDetail.id),
                        ),
                      )
                    }
                  >
                    Imprimir / guardar PDF
                  </button>
                ) : null}
                <button type="button" className="primary" onClick={() => changeTab("tracking")}>Ver trackeamiento diario</button>
              </footer>
            </article>
          ) : !isMondayToday && !canSubmitCurrentWeekCheckIn && checkInViewMode !== "new" ? (
            <article className="checkin-locked-panel">
              <span className="checkin-locked-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
              </span>
              <h2>El check-in semanal abre los lunes</h2>
              <p>
                Tu próximo check-in estará disponible el <strong>{nextMondayLabel}</strong>.
                Aprovecha la semana para registrar tu entrenamiento diario y revisar tu progreso.
              </p>
              <div className="checkin-locked-actions">
                <button type="button" onClick={() => changeTab("tracking")}>Ir a trackeamiento diario</button>
                <button type="button" className="primary" onClick={() => changeTab("history")}>Ver historial de check-ins</button>
              </div>
            </article>
          ) : (
          <form className="panel form-panel client-form-panel checkin-form-panel adminlte-content-card" onSubmit={handleCheckInSubmit}>
            <div className="checkin-form-header">
              <div>
                <p className="eyebrow">Seguimiento semanal</p>
                <h3>Registra tu semana y monitorea tu progreso</h3>
                <p className="muted client-form-copy">
                  Completa tu check-in semanal con la información de tus entrenamientos, hábitos y medidas para que tu coach pueda darte el mejor seguimiento.
                </p>
              </div>
              {savedWeekOptions.length > 0 ? (
                <div className="checkin-banner-actions">
                  <button type="button" onClick={() => setCheckInViewMode("detail")}>
                    Ver semanas registradas
                  </button>
                </div>
              ) : null}
            </div>
            {(() => {
              // Completion is based on the 3 required photos; favorites are optional extras
              const photosCount = REQUIRED_PROGRESS_PHOTO_SLOTS.filter((type) => progressPhotoPreviews[type]).length;
              const totalDays = checkInForm.dailyEntries.length || 7;
              const daysCount = checkInForm.dailyEntries.filter((entry) => entry.weightKg || entry.waterLiters || entry.sleepHours).length;
              const totalMeasures = 7;
              const measuresCount = Object.entries(checkInForm.measurementExtras).filter(
                ([key, value]) => key !== "glutesCm" && String(value).trim(),
              ).length;
              const resumenCount = [
                checkInForm.summary.sleepNotes,
                checkInForm.summary.hungerNotes,
                checkInForm.summary.anxietyNotes,
                checkInForm.summary.stressNotes,
                checkInForm.summary.offDietNotes,
                checkInForm.summary.trainingCompletionNotes,
                checkInForm.summary.cardioCompletionNotes,
                checkInForm.waterLiters > 0 ? "x" : "",
                checkInForm.summary.digestionNotes,
                checkInForm.summary.trainingPerformanceNotes,
                checkInForm.summary.improvementNotes,
                checkInForm.observations,
              ].filter(Boolean).length;
              const completionPercent = Math.round(
                ((photosCount / 3) * 0.2 +
                  (daysCount / totalDays) * 0.35 +
                  (measuresCount / totalMeasures) * 0.2 +
                  (resumenCount / 12) * 0.25) * 100,
              );
              return (
                <div className="checkin-summary-bar">
                  <div className="checkin-summary-pill checkin-summary-pill--week">
                    <span className="checkin-summary-pill-icon checkin-summary-pill-icon--orange" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
                      </svg>
                    </span>
                    <div className="checkin-summary-pill-body">
                      <span className="checkin-summary-pill-label">SEMANA ACTIVA</span>
                      <input
                        type="date"
                        className="checkin-summary-pill-input"
                        value={checkInForm.weekStart}
                        onChange={(event) => {
                          setCheckInError("");
                          setCheckInForm((current) => ({
                            ...current,
                            weekStart: event.target.value,
                            dailyEntries: generateWeeklyEntries(event.target.value, current.dailyEntries, selectedTrainingDays),
                          }));
                        }}
                        required
                      />
                    </div>
                  </div>

                  <div className="checkin-summary-pill checkin-summary-pill--info">
                    <span className="checkin-summary-pill-icon checkin-summary-pill-icon--teal" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z" />
                      </svg>
                    </span>
                    <div className="checkin-summary-pill-body">
                      <span className="checkin-summary-pill-label">INFORMACIÓN IMPORTANTE</span>
                      <p className="checkin-summary-pill-text">Algunos datos se sincronizan automáticamente o se usan para tu seguimiento personalizado.</p>
                    </div>
                  </div>

                  <div className="checkin-summary-metrics">
                    <div className="checkin-summary-metric">
                      <span className="checkin-summary-pill-icon checkin-summary-pill-icon--blue" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                        </svg>
                      </span>
                      <div>
                        <strong>Fotos</strong>
                        <p>{photosCount} / 3</p>
                      </div>
                    </div>
                    <div className="checkin-summary-metric">
                      <span className="checkin-summary-pill-icon checkin-summary-pill-icon--orange" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4" />
                        </svg>
                      </span>
                      <div>
                        <strong>Días registrados</strong>
                        <p>{daysCount} / {totalDays}</p>
                      </div>
                    </div>
                    <div className="checkin-summary-metric">
                      <span className="checkin-summary-pill-icon checkin-summary-pill-icon--cyan" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m21.3 15.3-6 6a1 1 0 0 1-1.4 0l-12-12a1 1 0 0 1 0-1.4l6-6a1 1 0 0 1 1.4 0l12 12a1 1 0 0 1 0 1.4z" /><path d="m7.5 10.5 1 1M10.5 7.5l1 1M13.5 13.5l1 1M16.5 10.5l1 1" />
                        </svg>
                      </span>
                      <div>
                        <strong>Medidas</strong>
                        <p>{measuresCount} / {totalMeasures}</p>
                      </div>
                    </div>
                    <div className="checkin-summary-metric">
                      <span className="checkin-summary-pill-icon checkin-summary-pill-icon--purple" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 13h6M9 17h6M9 9h2" />
                        </svg>
                      </span>
                      <div>
                        <strong>Resumen</strong>
                        <p>{resumenCount} / 12</p>
                      </div>
                    </div>
                    <div className="checkin-summary-progress" role="img" aria-label={`${completionPercent}% completado`}>
                      <div
                        className="checkin-summary-progress-ring"
                        style={{ background: `conic-gradient(#ff7a3d ${completionPercent * 3.6}deg, #eef1f5 0deg)` }}
                      >
                        <span>{completionPercent}%</span>
                      </div>
                      <p>Completado</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="form-grid client-form-grid checkin-form-grid">

              <div className="client-question-card checkin-question-card checkin-section-card checkin-photos-section">
                <div className="checkin-section-heading">
                  <span className="checkin-section-heading-icon checkin-section-heading-icon--orange" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
                    </svg>
                  </span>
                  <div>
                    <span className="checkin-section-heading-title">1. Fotos de progreso</span>
                    <p className="checkin-section-heading-copy">Sube fotos de frente, lado y espalda para comparar tus cambios.</p>
                  </div>
                </div>
                {photoErrors.length > 0 ? (
                  <div className="error-banner">
                    {photoErrors.map((item, index) => (
                      <p key={`${item}-${index}`} style={{ margin: 0 }}>
                        {item}
                      </p>
                    ))}
                  </div>
                ) : null}
                <div className="photo-upload-grid">
                  {PROGRESS_PHOTO_SLOTS.map((type) => (
                    <label key={type} className="client-question-card checkin-question-card photo-upload-card">
                      <span className="photo-upload-card-title">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" />
                        </svg>
                        {progressPhotoLabel(type)}
                      </span>
                      <input
                        id={`photo-upload-${type}`}
                        type="file"
                        accept="image/*"
                        className="photo-upload-input"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          event.currentTarget.value = "";
                          setCheckInError("");
                          void setProgressPhoto(type, file);
                        }}
                        disabled={savingCheckIn || uploadingPhotos}
                      />
                      {progressPhotoPreviews[type] ? (
                        <div
                          className="photo-preview-frame"
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            const previewPhotos = PROGRESS_PHOTO_SLOTS
                              .filter((photoType) => progressPhotoPreviews[photoType])
                              .map((photoType) => ({
                                title: progressPhotoLabel(photoType),
                                subtitle: "Vista previa antes de guardar",
                                src: progressPhotoPreviews[photoType],
                              }));
                            openPhotoViewer(previewPhotos, previewPhotos.findIndex((item) => item.src === progressPhotoPreviews[type]));
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              const previewPhotos = PROGRESS_PHOTO_SLOTS
                                .filter((photoType) => progressPhotoPreviews[photoType])
                                .map((photoType) => ({
                                  title: progressPhotoLabel(photoType),
                                  subtitle: "Vista previa antes de guardar",
                                  src: progressPhotoPreviews[photoType],
                                }));
                              openPhotoViewer(previewPhotos, previewPhotos.findIndex((item) => item.src === progressPhotoPreviews[type]));
                            }
                          }}
                        >
                          <img
                            src={progressPhotoPreviews[type]}
                            alt={`Vista previa ${type}`}
                          />
                        </div>
                      ) : (
                        <div className="photo-empty-frame">
                          {renderProgressPhotoSilhouette(type)}
                          <div className="photo-empty-hint">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                              <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242M12 12v9m-4-4 4 4 4-4"/>
                            </svg>
                            <span>Arrastra o haz clic para subir<br/>JPG, PNG o WEBP</span>
                          </div>
                        </div>
                      )}
                      <div className="photo-upload-actions">
                        <label
                          htmlFor={`photo-upload-${type}`}
                          className="photo-upload-button photo-upload-button-success"
                        >
                          Subir
                        </label>
                        <button
                          type="button"
                          className="photo-upload-button photo-upload-button-danger"
                          onClick={() => void setProgressPhoto(type, null)}
                          disabled={!progressPhotoPreviews[type] || savingCheckIn || uploadingPhotos}
                        >
                          Eliminar
                        </button>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="checkin-top-grid checkin-top-grid--row">
                <div className="client-question-card checkin-question-card checkin-section-card checkin-daily-section">
                  <div className="checkin-section-heading">
                    <div className="checkin-section-heading-row">
                      <span className="checkin-section-heading-icon checkin-section-heading-icon--blue" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 3v18h18" /><path d="M7 14l3-3 4 4 5-6" />
                        </svg>
                      </span>
                      <div>
                        <span className="checkin-section-heading-title">2. Registro diario de la semana</span>
                        <p className="checkin-section-heading-copy">Registra tus hábitos y datos de hoy en ayunas.</p>
                      </div>
                    </div>
                  </div>

                  <div className="checkin-daily-table-wrap">
                    <table className="checkin-daily-edit-table">
                      <thead>
                        <tr>
                          <th>Día</th>
                          <th><span className="checkin-table-heading-stack">Agua<span>(L)</span></span></th>
                          <th><span className="checkin-table-heading-stack">Horas de<span>Sueño</span></span></th>
                          <th><span className="checkin-table-heading-stack">Pasos<span>Diarios</span></span></th>
                          <th><span className="checkin-table-heading-stack">Peso<span>(kg)</span></span></th>
                          <th><span className="checkin-table-heading-stack">Proteína<span>(g)</span></span></th>
                          <th><span className="checkin-table-heading-stack">Carbo<span>hidratos</span><span>(g)</span></span></th>
                          <th><span className="checkin-table-heading-stack">Grasas<span>(g)</span></span></th>
                          <th>Calorías</th>
                          <th><span className="checkin-table-heading-stack">Tipo de<span>Dieta</span></span></th>
                          <th><span className="checkin-table-heading-stack">Músculo<span>Entrenado</span></span></th>
                          <th><span className="checkin-table-heading-stack">Cardio<span>(min)</span></span></th>
                          <th><span className="checkin-table-heading-stack">Glucosa<span>(mg/dL)</span></span></th>
                          <th>Presión</th>
                          <th>Notas diarias</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checkInForm.dailyEntries.map((entry, entryIndex) => (
                          <tr key={entry.date}>
                            <td className="checkin-daily-day-cell">
                              <strong>{entry.label}</strong>
                              <span>{formatShortDayDate(entry.date)}</span>
                            </td>
                            <td><input type="number" step="0.1" min={0} value={entry.waterLiters ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "waterLiters", event.target.value)} /></td>
                            <td><input type="number" step="0.1" min={0} value={entry.sleepHours ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "sleepHours", event.target.value)} /></td>
                            <td><input type="number" step="1" min={0} value={entry.steps ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "steps", event.target.value)} /></td>
                            <td><input type="number" step="0.1" min={0} value={entry.weightKg ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "weightKg", event.target.value)} /></td>
                            <td><input type="number" step="1" min={0} value={entry.proteinGrams ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "proteinGrams", event.target.value)} /></td>
                            <td><input type="number" step="1" min={0} value={entry.carbsGrams ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "carbsGrams", event.target.value)} /></td>
                            <td><input type="number" step="1" min={0} value={entry.fatsGrams ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "fatsGrams", event.target.value)} /></td>
                            <td>
                              <input
                                type="number"
                                step="1"
                                min={0}
                                value={calculateDailyCalories(entry) || entry.calories || ""}
                                readOnly
                                title="Proteína x 4 + carbohidratos x 4 + grasas x 9"
                              />
                            </td>
                            <td>
                              <select value={formatDietTypeLabel(entry.dietType)} onChange={(event) => updateDailyEntry(entryIndex, "dietType", event.target.value)}>
                                <option value="">-</option>
                                <option value="High">High</option>
                                <option value="Mid">Mid</option>
                                <option value="Low">Low</option>
                                <option value="Dias On">Dias On</option>
                                <option value="Dia Off">Dia Off</option>
                              </select>
                            </td>
                            <td>
                              <select value={normalizeMuscleTrainedValue(entry.trainedMuscle)} onChange={(event) => updateDailyEntry(entryIndex, "trainedMuscle", event.target.value)}>
                                <option value="">-</option>
                                <option value="Pull">Pull</option>
                                <option value="Push">Push</option>
                                <option value="Cadena posterior">Cadena posterior</option>
                                <option value="CHEST">Pecho</option>
                                <option value="BACK">Espalda</option>
                                <option value="SHOULDERS">Hombros</option>
                                <option value="BICEPS">Bíceps</option>
                                <option value="TRICEPS">Tríceps</option>
                                <option value="LEGS">Piernas</option>
                                <option value="GLUTES">Glúteos</option>
                                <option value="Femorales">Femorales</option>
                                <option value="Quadriceps">Quadriceps</option>
                                <option value="Gluteo y femoral">Gluteo y femoral</option>
                                <option value="Gluteo y cuadriceps">Gluteo y cuadriceps</option>
                                <option value="ABS">Abdomen</option>
                                <option value="FULL_BODY">Cuerpo completo</option>
                                <option value="REST">Descanso</option>
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                value={entry.cardio ?? ""}
                                style={{ width: `${Math.min(18, Math.max(6, String(entry.cardio ?? "").length + 1))}ch` }}
                                onChange={(event) => updateDailyEntry(entryIndex, "cardio", event.target.value)}
                              />
                            </td>
                            <td><input type="number" step="1" min={0} value={entry.bloodGlucose ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "bloodGlucose", event.target.value)} /></td>
                            <td><input type="text" value={entry.bloodPressure ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "bloodPressure", event.target.value)} /></td>
                            <td><textarea rows={2} value={entry.dayReflections ?? ""} onChange={(event) => updateDailyEntry(entryIndex, "dayReflections", event.target.value)} /></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td><strong>Promedio</strong></td>
                          <td>{dailyTableStats.water}</td>
                          <td>{dailyTableStats.sleep}</td>
                          <td>{dailyTableStats.steps}</td>
                          <td>{dailyTableStats.weight}</td>
                          <td>{dailyTableStats.protein}</td>
                          <td>{dailyTableStats.carbs}</td>
                          <td>{dailyTableStats.fats}</td>
                          <td>{dailyTableStats.calories}</td>
                          <td colSpan={6}><strong>Variación de peso: {dailyTableStats.variation}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div className="client-question-card checkin-question-card checkin-section-card checkin-measures-section">
                  <div className="checkin-section-heading">
                    <span className="checkin-section-heading-icon checkin-section-heading-icon--orange" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 8h16" />
                        <path d="M7 5 4 8l3 3" />
                        <path d="m17 5 3 3-3 3" />
                        <path d="M6 16h12" />
                        <path d="M9 13 6 16l3 3" />
                        <path d="m15 13 3 3-3 3" />
                      </svg>
                    </span>
                    <div>
                      <span className="checkin-section-heading-title">3. Medidas</span>
                      <p className="checkin-section-heading-copy">Registra tus medidas corporales para comparar cambios reales.</p>
                    </div>
                  </div>
                  <div className="checkin-measurements-grid">
                    <label className="checkin-measurement-field">
                      <span className="checkin-measurement-icon checkin-measurement-icon--amber" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>
                      </span>
                      <span>Pliegue Abdominal</span>
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        placeholder="mm"
                        value={checkInForm.measurementExtras.abdominalFoldMm}
                        onChange={(event) => updateMeasurementExtra("abdominalFoldMm", event.target.value)}
                      />
                    </label>

                    <label className="checkin-measurement-field">
                      <span className="checkin-measurement-icon checkin-measurement-icon--coral" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>
                      </span>
                      <span>Medida de cintura (debajo de las costillas) (relajado)</span>
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        placeholder="cm"
                        value={checkInForm.measurementExtras.waistRelaxedCm}
                        onChange={(event) => updateMeasurementExtra("waistRelaxedCm", event.target.value)}
                      />
                    </label>

                    <label className="checkin-measurement-field">
                      <span className="checkin-measurement-icon checkin-measurement-icon--orange" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>
                      </span>
                      <span>Medida de cintura (debajo de las costillas con el abdomen apretado)</span>
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        placeholder="cm"
                        value={checkInForm.measurementExtras.waistContractedCm}
                        onChange={(event) => updateMeasurementExtra("waistContractedCm", event.target.value)}
                      />
                    </label>

                    <label className="checkin-measurement-field">
                      <span className="checkin-measurement-icon checkin-measurement-icon--cyan" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>
                      </span>
                      <span>Medida de cadera (rollo caderal)</span>
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        placeholder="cm"
                        value={checkInForm.measurementExtras.hipsRollCm}
                        onChange={(event) => updateMeasurementExtra("hipsRollCm", event.target.value)}
                      />
                    </label>

                    <label className="checkin-measurement-field">
                      <span className="checkin-measurement-icon checkin-measurement-icon--purple" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>
                      </span>
                      <span>Medida de brazo flexionado</span>
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        placeholder="cm"
                        value={checkInForm.measurementExtras.flexedArmCm}
                        onChange={(event) => updateMeasurementExtra("flexedArmCm", event.target.value)}
                      />
                    </label>

                    <label className="checkin-measurement-field">
                      <span className="checkin-measurement-icon checkin-measurement-icon--violet" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>
                      </span>
                      <span>Medida de muslo (altura cuando termina la nalga)</span>
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        placeholder="cm"
                        value={checkInForm.measurementExtras.upperThighCm}
                        onChange={(event) => updateMeasurementExtra("upperThighCm", event.target.value)}
                      />
                    </label>

                    <label className="checkin-measurement-field">
                      <span className="checkin-measurement-icon checkin-measurement-icon--blue" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>
                      </span>
                      <span>Medida del cuádriceps (un poco más arriba de la rodilla)</span>
                      <input
                        type="number"
                        step="0.1"
                        min={0}
                        placeholder="cm"
                        value={checkInForm.measurementExtras.quadCm}
                        onChange={(event) => updateMeasurementExtra("quadCm", event.target.value)}
                      />
                    </label>

                    {isFemale ? (
                      <label className="checkin-measurement-field">
                        <span className="checkin-measurement-icon checkin-measurement-icon--pink" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="1"/><path d="M6 9v3M10 9v4M14 9v3M18 9v4"/></svg>
                        </span>
                        <span>Medida de glúteo (perímetro más grande de las nalgas, con las piernas juntas)</span>
                        <input
                          type="number"
                          step="0.1"
                          min={0}
                          placeholder="cm"
                          value={checkInForm.measurementExtras.glutesCm}
                          onChange={(event) => updateMeasurementExtra("glutesCm", event.target.value)}
                        />
                      </label>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="client-question-card checkin-question-card checkin-section-card checkin-summary-section">
                <div className="checkin-section-heading">
                  <span className="checkin-section-heading-icon checkin-section-heading-icon--blue" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3v18h18" />
                      <path d="M8 17V9" />
                      <path d="M13 17V5" />
                      <path d="M18 17v-6" />
                    </svg>
                  </span>
                  <div>
                    <span className="checkin-section-heading-title">4. Resumen semanal</span>
                    <p className="checkin-section-heading-copy">Cuéntale a tu coach cómo estuvo tu semana en general.</p>
                  </div>
                </div>
                <div className="checkin-summary-grid">
                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--blue">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                    </span>
                    <span>Cómo está tu sueño</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.sleepNotes}
                      onChange={(event) => updateSummaryField("sleepNotes", event.target.value)}
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--orange">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h2v11"/><path d="M7 2v7"/><path d="M21 15V2l-3 3c-1 1-1.5 2.5-1.5 4S17 12 18 13l3 2z"/></svg>
                    </span>
                    <span>Hambre y momentos al día</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.hungerNotes}
                      onChange={(event) => updateSummaryField("hungerNotes", event.target.value)}
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--pink">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2z"/><path d="M14.5 2a2.5 2.5 0 0 0-2.5 2.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2z"/></svg>
                    </span>
                    <span>Ansiedad</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.anxietyNotes}
                      onChange={(event) => updateSummaryField("anxietyNotes", event.target.value)}
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--purple">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h9l-1 8 9-12h-9z"/></svg>
                    </span>
                    <span>Estrés</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.stressNotes}
                      onChange={(event) => updateSummaryField("stressNotes", event.target.value)}
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--amber">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11h16a2 2 0 0 0 0-4H4a2 2 0 0 0 0 4z"/><path d="M3 14h18"/><path d="M3 17a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3"/></svg>
                    </span>
                    <span>Comidas fuera de la dieta</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.offDietNotes}
                      onChange={(event) => updateSummaryField("offDietNotes", event.target.value)}
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--green">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6v12M3 9v6M21 9v6M18 6v12M6 12h12"/></svg>
                    </span>
                    <span>Entrenamientos completados</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.trainingCompletionNotes}
                      onChange={(event) => updateSummaryField("trainingCompletionNotes", event.target.value)}
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--red">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    </span>
                    <span>Cardio completado</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.cardioCompletionNotes}
                      onChange={(event) => updateSummaryField("cardioCompletionNotes", event.target.value)}
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--sky">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69 5.66 8.82a8.04 8.04 0 0 0 0 11.36 8.04 8.04 0 0 0 11.36 0 8.04 8.04 0 0 0 0-11.36z"/></svg>
                    </span>
                    <span>Agua al día (litros)</span>
                    <input
                      type="number"
                      step="0.1"
                      min={0}
                      placeholder="Escribe aquí..."
                      value={checkInForm.waterLiters}
                      onChange={(event) => {
                        setCheckInError("");
                        setCheckInForm({ ...checkInForm, waterLiters: Number(event.target.value) });
                      }}
                      required
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--teal">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3c0 2 2 3 3 3 2 0 3 1 3 3 0 1.5-.5 2.5-1 3.5C18 14 18 17 16 19c-2 1.5-5 2-7 1-2-1-3.5-2.5-4-5-.5-2 0-4 1.5-5.5C8 8 8.5 6.5 9 5c.5-1.5 2-3 5-2z"/><circle cx="10" cy="12" r="1"/><circle cx="14" cy="14" r="1"/></svg>
                    </span>
                    <span>Digestión e hinchazon</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.digestionNotes}
                      onChange={(event) => updateSummaryField("digestionNotes", event.target.value)}
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--orange">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17 9 11l4 4 8-10"/><path d="M14 5h7v7"/></svg>
                    </span>
                    <span>Cómo va tu entrenamiento</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.trainingPerformanceNotes}
                      onChange={(event) => updateSummaryField("trainingPerformanceNotes", event.target.value)}
                    />
                  </label>

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--green">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.2 6.4 20.2 7.5 14 3 9.6l6.2-.9z"/></svg>
                    </span>
                    <span>En qué crees que podemos mejorar</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.summary.improvementNotes}
                      onChange={(event) => updateSummaryField("improvementNotes", event.target.value)}
                    />
                  </label>

                  {isFemale ? (
                    <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--pink">
                      <span className="checkin-summary-field-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                      </span>
                      <span>¿Qué día vino y se fue el periodo?</span>
                      <textarea
                        rows={4}
                        value={checkInForm.menstrualPeriodDates}
                        placeholder="Ej: Vino el 3 y se fue el 8"
                        onChange={(event) => {
                          setCheckInError("");
                          setCheckInForm({ ...checkInForm, menstrualPeriodDates: event.target.value });
                        }}
                      />
                    </label>
                  ) : null}

                  <label className="client-question-card checkin-question-card checkin-summary-field checkin-summary-field--blue">
                    <span className="checkin-summary-field-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                    </span>
                    <span>Observaciones adicionales</span>
                    <textarea
                      rows={4}
                      placeholder="Escribe aquí..."
                      value={checkInForm.observations}
                      onChange={(event) => {
                        setCheckInError("");
                        setCheckInForm({ ...checkInForm, observations: limitWords(event.target.value) });
                      }}
                    />
                  </label>
                </div>
              </div>

            </div>
            {checkInError ? <div className="error-banner">{checkInError}</div> : null}
            <div className="client-form-actions checkin-actions">
              <button type="button" className="checkin-action-button checkin-action-button--back" onClick={() => changeTab("dashboard")}>
                <span className="checkin-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></svg>
                </span>
                Volver
              </button>
              <button type="button" className="checkin-action-button checkin-action-button--draft" onClick={handleCheckInDraftSave} disabled={uploadingPhotos}>
                <span className="checkin-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8" /><path d="M7 3v5h8" /></svg>
                </span>
                Guardar borrador
              </button>
              <button className="checkin-action-button checkin-action-button--submit" type="submit" disabled={savingCheckIn || uploadingPhotos}>
                <span className="checkin-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M9 12l2 2 4-5" /><circle cx="12" cy="12" r="9" /></svg>
                </span>
                {savingCheckIn ? "Enviando..." : uploadingPhotos ? "Subiendo fotos..." : "Guardar seguimiento semanal"}
              </button>
            </div>
          </form>
          )}
        </div>
      )}
      {photoViewer ? (
        <div className="photo-viewer-backdrop" role="dialog" aria-modal="true" aria-label="Visualizador de imagen">
          <div className="photo-viewer">
            <div className="photo-viewer-header">
              <div>
                <p>{photoViewer.photos[photoViewer.index]?.subtitle ?? "Foto de progreso"}</p>
                <h2>{photoViewer.photos[photoViewer.index]?.title ?? "Foto"}</h2>
              </div>
              <div className="photo-viewer-tools" aria-label="Controles de zoom">
                <button type="button" onClick={() => zoomPhotoViewer(-0.25)} disabled={photoViewer.zoom <= 1} aria-label="Alejar imagen">
                  -
                </button>
                <span>{Math.round(photoViewer.zoom * 100)}%</span>
                <button type="button" onClick={() => zoomPhotoViewer(0.25)} disabled={photoViewer.zoom >= 6} aria-label="Acercar imagen">
                  +
                </button>
                <button type="button" onClick={resetPhotoViewerZoom}>
                  Restablecer
                </button>
              </div>
              <button type="button" className="photo-viewer-close" onClick={() => setPhotoViewer(null)} aria-label="Cerrar visualizador">
                ×
              </button>
            </div>
            <div
              className={`photo-viewer-stage${photoViewer.zoom > 1 ? " photo-viewer-stage--zoomed" : ""}`}
              onWheel={(event) => {
                event.preventDefault();
                zoomPhotoViewer(event.deltaY < 0 ? 0.15 : -0.15);
              }}
              onPointerDown={startPhotoPan}
              onPointerMove={movePhotoPan}
              onPointerUp={endPhotoPan}
              onPointerCancel={endPhotoPan}
            >
              {photoViewer.photos.length > 1 ? (
                <button type="button" className="photo-viewer-nav photo-viewer-nav--prev" onClick={() => movePhotoViewer(-1)} aria-label="Foto anterior">
                  ‹
                </button>
              ) : null}
              <img
                src={photoViewer.photos[photoViewer.index]?.src}
                alt={photoViewer.photos[photoViewer.index]?.title ?? "Foto ampliada"}
                draggable={false}
                style={{ transform: `translate(${photoViewer.panX}px, ${photoViewer.panY}px) scale(${photoViewer.zoom})` }}
              />
              {photoViewer.photos.length > 1 ? (
                <button type="button" className="photo-viewer-nav photo-viewer-nav--next" onClick={() => movePhotoViewer(1)} aria-label="Foto siguiente">
                  ›
                </button>
              ) : null}
            </div>
            <div className="photo-viewer-footer">
              <span>{photoViewer.index + 1} / {photoViewer.photos.length}</span>
              <div>
                {photoViewer.photos.map((photo, index) => (
                  <button
                    key={`${photo.title}-${photo.src}`}
                    type="button"
                    className={index === photoViewer.index ? "is-active" : ""}
                    onClick={() => setPhotoViewer((current) => (current ? { ...current, index, zoom: 1, panX: 0, panY: 0 } : current))}
                  >
                    {photo.title}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
